import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Settings as SettingsIcon, Save, Mail, Send, TestTube2, Loader2, Server, Palette, Globe2, Info, Users, Gift, Plus, Trash2, Gamepad2, Landmark, Headphones, ExternalLink, Upload, Image as ImageIcon, Bell, Copy, Check, Dices, Spade, Gem, Joystick, LayoutTemplate, Wallet, Banknote, ShieldCheck, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, DatabaseBackup, Download, Play, AlertTriangle, Clock, Lock, Search, RotateCcw, Code2 } from 'lucide-react';
import client, { API, resolveApiAssetUrl } from '../lib/api';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import { useToast } from '../hooks/use-toast';
import { useAdminPermissions } from './lib/permissions';
import { notifySettingsUpdated } from '../context/SettingsContext';
import ConfirmDialog from './components/ConfirmDialog';
import BackupPanelV2, { RESTORE_CONFIRM_PHRASE } from './v2/BackupPanel';
import CryptoBoxSettings from './components/CryptoBoxSettings';
import CashMoneySettings from './components/CashMoneySettings';
import PayluxSettings from './components/PayluxSettings';
import ExpaySettings from './components/ExpaySettings';
import PrimevpaySettings from './components/PrimevpaySettings';
import WithdrawRulesSettings from './components/WithdrawRulesSettings';
import CashbackRulesSettings from './components/CashbackRulesSettings';
import SempicoSettings from './components/SempicoSettings';
import { BackupRemoteSettings, BackupRestoreSources } from './components/BackupRemotePanel';
import {
  BACKUP_STATUS_BADGE, BACKUP_STATUS_LABEL, BACKUP_TRIGGER_LABEL,
  formatBackupDate, formatBackupSize, remoteStatusBadge,
} from './lib/backupRemote';
import { AdminError, AdminSkeleton } from './components/PageState';
import {
  clearDirectionLimits, directionHasCustom, hasMethodLimit,
  limitFieldValue, setMethodLimit,
} from './lib/paymentLimits';
import { GAME_LANGUAGES, GAME_LANGUAGE_HINT } from '../constants/gameLanguages';
import {
  DEFAULT_THEME_ID, LISTED_SITE_THEMES, SITE_THEMES, findCustomTheme, getSiteTheme,
  isThemeLocked, themeHasFixedColors,
} from '../lib/siteThemes';
import { applyAdminTheme } from '../lib/theme';
import {
  HIZLICAPARALAR_DEFAULTS, HIZLICAPARALAR_METHODS,
  CALLBACK_PATH as HIZLICAPARALAR_CALLBACK_PATH,
  changedFields as hizlicaparalarChangedFields,
  dirtyFields as hizlicaparalarDirtyFields,
  hasMethod as hizlicaparalarHasMethod,
  toggleMethod as hizlicaparalarToggleMethod,
} from './lib/hizlicaparalar';
import CustomCodePanel from './components/CustomCodePanel';
import {
  changedFields as customCodeChangedFields,
  codeFromResponse,
  dirtyKeys as customCodeDirtyFields,
  EMPTY_CUSTOM_CODE,
  metaFromResponse,
} from './lib/customCode';
import {
  HIZLIHAVALE_DEFAULTS,
  CALLBACK_PATH as HIZLIHAVALE_CALLBACK_PATH,
  changedFields as hizlihavaleChangedFields,
  dirtyFields as hizlihavaleDirtyFields,
} from './lib/hizlihavale';
import {
  ZELVOQ_DEFAULTS, ZELVOQ_METHODS,
  CALLBACK_PATH as ZELVOQ_CALLBACK_PATH,
  REDIRECT_PATH as ZELVOQ_REDIRECT_PATH,
  changedFields as zelvoqChangedFields,
  dirtyFields as zelvoqDirtyFields,
  hasMethod as zelvoqHasMethod,
  saveBeforeConnectionTest as saveZelvoqBeforeTest,
  toggleMethod as zelvoqToggleMethod,
} from './lib/zelvoq';
import { VEGA_THEME_ID } from '../lib/pageSlider';
import LiveChatDefaultIcon from '../components/LiveChatDefaultIcon';
import {
  BOTTOM_NAV_ICON_OPTIONS, DEFAULT_BOTTOM_NAV, MAX_BOTTOM_NAV_ITEMS,
  bottomNavIcon, normalizeBottomNav, splitBottomNav,
} from '../lib/bottomNav';

const MASKED_SECRET = '••••••••••••••••';
const SECRET_FIELDS = [
  'smtp_password',
  'heracles_otp_api_key',
  'telegram_bot_token',
  'anchor_telegram_bot_token',
  'drakon_agent_token',
  'drakon_agent_secret',
  'drakon_webhook_secret',
  'goldpalace_api_token',
  'goldpalace_callback_token',
  'oroplay_client_secret',
  'scorpio_api_token',
  'sportsbook_api_key',
  'wizkasa_api_key',
  'wizkasa_secret_key',
  'hemensystem_password',
  'hemensystem_api_secret',
  'hera_api_secret',
  'payurus_secret',
  'ultrapay_api_secret',
  'linkhavale_api_secret',
  // VEVO PAY'de iki değer de sırdır: giden istekte imza yoktur, yani kanal
  // anahtarını ele geçiren biri doğrudan çekim açabilir.
  'vevopay_client_token',
  'vevopay_client_secret',
];

// Finans sekmesi tek uzun form yerine sağlayıcı bazlı bölümlere ayrılır;
// `flag` alanı bölüm butonundaki aktiflik noktasını besler. Tek bölüm kalırsa
// seçici gizlenir — tıklanacak tek düğme yönetimi kolaylaştırmaz.
const FINANCE_SECTIONS = [
  { id: 'wizkasa', name: 'Wizkasa', icon: Wallet, flag: 'wizkasa_enabled' },
  {
    id: 'hemensystem',
    name: 'Hemen Havale',
    icon: Landmark,
    flag: 'hemensystem_enabled',
  },
  { id: 'hera', name: 'Hera', icon: Banknote, flag: 'hera_enabled' },
  { id: 'payurus', name: 'Payurus', icon: Wallet, flag: 'payurus_enabled' },
  {
    id: 'ultrapay',
    name: 'UltraPay',
    icon: Banknote,
    flag: 'ultrapay_enabled',
  },
  {
    id: 'linkhavale',
    name: 'Link Havale',
    icon: Landmark,
    flag: 'linkhavale_enabled',
  },
  {
    id: 'vevopay',
    name: 'VEVO PAY',
    icon: Landmark,
    flag: 'vevopay_enabled',
  },
  {
    id: 'hizlicaparalar',
    name: 'HızlıcaParalar',
    icon: Landmark,
    // Ayarları `settings` kaydında **değil** `provider_configs` satırındadır
    // (bkz. services/hizlicaparalar.py); açık/kapalı rozeti bu yüzden `s`
    // yerine kendi state'inden okunur.
    flag: '',
    config: 'hizlicaparalar',
  },
  {
    id: 'hizlihavale',
    name: 'Hızlı Havale',
    icon: Landmark,
    // Bunun da ayarları `provider_configs` satırındadır.
    flag: '',
    config: 'hizlihavale',
  },
  {
    id: 'zelvoq',
    name: 'Zelvoq',
    icon: Landmark,
    // Bunun da ayarları `provider_configs` satırındadır.
    flag: '',
    config: 'zelvoq',
  },
  {
    id: 'cryptobox',
    name: 'CryptoBox',
    icon: Wallet,
    // `provider_configs` satırındadır; bölüm kendi Kaydet düğmesini taşır.
    flag: '',
    config: 'cryptobox',
  },
  {
    id: 'cashmoney',
    name: 'CashMoney Pay',
    icon: Landmark,
    // `provider_configs` satırındadır; bölüm kendi Kaydet düğmesini taşır.
    flag: '',
    config: 'cashmoney',
  },
  {
    id: 'paylux',
    name: 'Paylux',
    icon: Landmark,
    // `provider_configs` satırındadır; bölüm kendi Kaydet düğmesini taşır.
    flag: '',
    config: 'paylux',
  },
  {
    id: 'expay',
    name: 'ExPay',
    icon: Landmark,
    // `provider_configs` satırındadır; bölüm kendi Kaydet düğmesini taşır.
    flag: '',
    config: 'expay',
  },
  {
    id: 'primevpay',
    name: 'PrimeVpay',
    icon: Landmark,
    // `provider_configs` satırındadır; bölüm kendi Kaydet düğmesini taşır.
    flag: '',
    config: 'primevpay',
  },
];


// UltraPay yöntem kataloğu. Liste backend'deki `services/ultrapay.METHODS`
// ile **aynı kalmalıdır**: kodlar sağlayıcının kendi kataloğudur, panelde
// olmayan bir kod açılamaz ve backend liste dışı değeri 400 ile eler.
const ULTRAPAY_METHODS = [
  { code: 2, name: 'Havale / FAST', payout: 'iban', withdraw: true },
  {
    code: 42,
    name: 'Havale / FAST (H2H)',
    payout: 'iban',
    withdraw: true,
    note: 'Alıcı IBAN kendi ekranımızda görünür',
  },
  { code: 1, name: 'Papara', payout: 'iban', withdraw: true },
  {
    code: 10,
    name: 'Kredi Kartı',
    payout: 'iban',
    withdraw: false,
    note: '500 – 50.000 ₺ · yalnızca yatırım',
  },
  { code: 15, name: 'Paycell', payout: 'iban', withdraw: true },
  { code: 20, name: 'Tosla', payout: 'iban', withdraw: true },
  { code: 26, name: 'Para Kolay', payout: 'iban', withdraw: true },
  { code: 8, name: 'Fups', payout: 'iban', withdraw: true },
  { code: 29, name: 'Money Tolia', payout: 'iban', withdraw: true },
  { code: 30, name: 'Ahlpay', payout: 'iban', withdraw: true },
  { code: 33, name: 'Hayhay', payout: 'iban', withdraw: true },
  { code: 34, name: 'Hadi Pay', payout: 'iban', withdraw: true },
  {
    code: 4,
    name: 'Kripto Para',
    payout: 'crypto',
    withdraw: true,
    note: 'Çekimde para birimi/ağ seçimi zorunlu',
  },
  { code: 44, name: 'JoyPayz Cüzdan', payout: 'wallet', withdraw: true },
  { code: 45, name: 'Yesod Cüzdan', payout: 'wallet', withdraw: true },
];

// Sağlayıcının kabul ettiği coin/ağ ikilileri. Listede olmayan bir çift
// reddedilir ve yanlış ağa gönderilen çekim **geri alınamaz**.
const ULTRAPAY_CRYPTO_PAIRS = [
  'BTC:BTC', 'ETH:ERC20', 'LTC:LTC', 'TRX:TRC20',
  'USDC:SOL', 'USDC:ERC20', 'USDT:TRC20', 'USDT:ERC20',
];

// Link Havale yöntem kataloğu. Liste backend'deki `services/linkhavale.METHODS`
// ile **aynı kalmalıdır**: slug'lar ödeme yöntemi satırının kalıcı kimliğine
// girer ve backend liste dışı değeri 400 ile eler.
const LINKHAVALE_METHODS = [
  { slug: 'bank', name: 'Havale / EFT', code: 'BANK_TRANSFER', withdraw: true },
  {
    slug: 'card',
    name: 'Kredi Kartı',
    code: 'CREDIT_CARD',
    withdraw: false,
    note: 'Yalnızca yatırım · 3D Secure',
  },
  { slug: 'papara', name: 'Papara', code: 'PAPARA', withdraw: true },
  {
    slug: 'crypto',
    name: 'Kripto Para',
    code: 'CRYPTO',
    withdraw: true,
    note: 'Çekimde ağ seçimi zorunlu',
  },
];

// Sağlayıcı çekimde yalnızca **ağı** sorar (coin alanı yoktur); liste bu
// yüzden USDT ile sınırlıdır. Yanlış ağa gönderilen çekim geri alınamaz.
const LINKHAVALE_CRYPTO_PAIRS = ['USDT:TRC20', 'USDT:ERC20', 'USDT:BEP20'];

// Hera'nın açılabilen yatırım akışları; sağlayıcı sözleşmesiyle sınırlıdır ve
// backend liste dışı değeri eler (services/hera.METHOD_TYPES).
const HERA_DEPOSIT_METHODS = [
  { id: 'BANK', name: 'Havale / EFT', note: 'Oyuncuya özel IBAN atanır' },
  { id: 'CARD2CARD', name: 'Kart - Kart', note: 'Sağlayıcının ödeme sayfası' },
  {
    id: 'CREDITCARD',
    name: 'Kredi Kartı',
    note: 'Sağlayıcının ödeme sayfası · en az 500 ₺',
  },
];

// Wizkasa yöntem kümeleri sağlayıcı sözleşmesiyle sınırlıdır: kart yalnızca
// yatırımda, kripto her iki yönde; backend liste dışı değeri reddeder.
const WIZKASA_DEPOSIT_LEDGERS = [
  { id: 'BANK', name: 'Havale / EFT' },
  { id: 'PAPARA', name: 'Papara' },
  { id: 'CARD', name: 'Kredi Kartı' },
  { id: 'CRYPTO', name: 'Kripto Para' },
  { id: 'WALLET', name: 'Mobil Cüzdan' },
  { id: 'CROSSBANK', name: 'Çapraz Banka' },
];
const WIZKASA_WITHDRAW_LEDGERS = [
  { id: 'BANK', name: 'Havale / EFT' },
  { id: 'CRYPTO', name: 'Kripto Para' },
];
// Sağlayıcı sözleşmesinde kripto için kabul edilen tek küme.
const WIZKASA_CRYPTO_COINS = ['USDT', 'TRX'];
const WIZKASA_CRYPTO_NETWORKS = ['TRC20'];
// Hemen Havale ödeme ekranı başarı/başarısızlıkta ayrı adrese döner.

// Sağlayıcının bildirim yolları: adres elle yazılmaz, sitenin kendi kökünden
// türetilir (bkz. CallbackField). Sekme kaydedilirken ayar kaydı da bu değere
// çekilir; site başka bir alan adına taşındığında ayarların eski domainde
// kalması yatırımın sessizce kredilenmemesi demektir.
const CALLBACK_FIELDS = [
  { key: 'wizkasa_callback_url', path: '/api/wizkasa/callback' },
  { key: 'hemensystem_callback_url', path: '/api/hemensystem/callback' },
  { key: 'hera_callback_url', path: '/api/hera/callback' },
  { key: 'payurus_callback_url', path: '/api/payurus/callback' },
  { key: 'ultrapay_callback_url', path: '/api/ultrapay/callback' },
  { key: 'linkhavale_callback_url', path: '/api/linkhavale/callback' },
  { key: 'vevopay_callback_url', path: '/api/vevopay/callback' },
];

// Sağlayıcının `website_id` alanı için rastgele numara üretir.
//
// Benzersizliğin sağlayıcının **tüm müşterileri** arasında tutması gerekiyor
// ama sorgulanabilecek bir liste yok; tek savunma çarpışmayı pratikte
// imkânsız kılacak kadar geniş bir aralıktan seçmektir. 10 haneli aralık
// (1.000.000.000–9.999.999.999) sağlayıcının örnek biçimiyle (1011251) uyumlu
// kalırken 9 milyar olasılık sunar. Değer `crypto` ile üretilir: `Math.random`
// aynı anda açılan iki panelde aynı sayıyı verebilir.
const WEBSITE_ID_MIN = 1_000_000_000;
const WEBSITE_ID_RANGE = 9_000_000_000;
const generateWebsiteId = () => {
  const buffer = new Uint32Array(2);
  window.crypto.getRandomValues(buffer);
  // İki 32 bitlik parçadan 53 bite sığan bir tamsayı kurulur; tek parça
  // (2^32) aralığı kapsamaya yetmez ve modulo sapması ölçülebilir olurdu.
  const value = buffer[0] * 2 ** 21 + (buffer[1] >>> 11);
  return WEBSITE_ID_MIN + (value % WEBSITE_ID_RANGE);
};

// Callback adresi sitenin kendi ucudur: elle yazılacak bir şey yoktur ve
// yanlış yazılan bir adres sessizce çalışmaz — sağlayıcının bildirimi bize hiç
// ulaşmaz, oyuncu parayı yatırır ve bakiyesi artmaz. Bu yüzden alan salt
// okunurdur ve değer her zaman sitenin o an açıldığı kökten türetilir;
// yönetici yalnızca kopyalayıp sağlayıcı paneline yapıştırır.
//
// Ayar kaydına yazılması ayrı bir adımdır: aynı sağlayıcının başka bir alanı
// kaydedilirken adres de güncel değere çekilir (bkz. buildPayload). Site başka
// bir alan adına taşındığında panelin üst bandı ayrıca uyarır.
const CallbackField = ({ label, path, note, copied, onCopy, base }) => {
  // Taban bilinmiyorsa (kurulum yeni, henüz ziyaretçi yok) yalnızca yol
  // gösterilir; uydurma bir adres kopyalatmak sağlayıcı paneline yanlış
  // değer yazdırırdı.
  const url = base ? `${base}${path}` : path;
  return (
    <F l={label} full>
      <div className="flex gap-2">
        <input
          value={url}
          readOnly
          onFocus={(event) => event.target.select()}
          className="admin-input"
          title="Bu adres site alan adından türetilir ve elle değiştirilemez"
        />
        <button
          type="button"
          onClick={() => onCopy(path)}
          className="admin-btn-secondary shrink-0 px-3 text-xs"
        >
          {copied === path ? <Check size={14} /> : <Copy size={14} />}
          Kopyala
        </button>
      </div>
      {note && (
        <div className="mt-1.5 text-[11px] leading-4 text-slate-400">{note}</div>
      )}
    </F>
  );
};

// Yöntem kartı: açma kutusu + (açıkken) yönteme özel alt/üst tutar.
//
// Limit yön başına tekti; Wizkasa'nın altı yatırım yöntemi aynı aralığı
// paylaşıyordu. Oysa aralık yöntemden yönteme gerçekten farklıdır (havalede
// sağlayıcının IBAN havuzu, kartta bankanın kabulü, kriptoda ağ ücreti) ve tek
// aralık zorunlu tutulduğunda yönetici en dar yöntemin sınırını hepsine
// uygulamak zorunda kalıyordu. Boş bırakılan alan yön limitine düşer.
const MethodLimitPicker = ({
  options,
  selected,
  onToggle,
  provider,
  direction,
  limits,
  onLimitsChange,
  sharedMin,
  sharedMax,
}) => {
  const shared = { min: sharedMin, max: sharedMax };
  return (
    <div className="settings-method-grid">
      {options.map((option) => {
        const code = String(option.id);
        const on = selected.includes(option.id);
        const custom = hasMethodLimit(limits, provider, direction, code);
        const field = (name, label) => {
          const own = limitFieldValue(limits, provider, direction, code, name);
          const fallback = shared[name];
          return (
            <label className="settings-method-limit">
              <span>{label}</span>
              <input
                type="number"
                min="0"
                step="1"
                className="admin-input"
                // Kutu boş durmaz: kendi tutarı yoksa ortak tutarı gösterir.
                // Yer tutucu metin ("yön limiti") yöneticinin bilmediği bir
                // terimdi ve alanın neyi devraldığını hiç söylemiyordu.
                value={own || (fallback === undefined ? '' : String(fallback))}
                onChange={(e) => {
                  const next = e.target.value;
                  // Ortak tutarın aynısı yazıldıysa kayıt açılmaz: aksi hâlde
                  // hiçbir şeyi değiştirmeyen bir düzenleme ortak alanı
                  // kilitler ve yönetici sebebini bulamazdı.
                  const same = fallback !== undefined
                    && next !== ''
                    && Number(next) === Number(fallback);
                  onLimitsChange(setMethodLimit(
                    limits, provider, direction, code, name,
                    same ? '' : next));
                }}
              />
            </label>
          );
        };
        return (
          <div key={code} className={`settings-method${on ? ' is-on' : ''}`}>
            <label className="settings-method-head">
              <input
                type="checkbox"
                checked={on}
                onChange={() => onToggle(option.id)}
              />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-white">{option.name}</span>
                {option.note && (
                  <span className="mt-0.5 block text-[11px] leading-4 text-slate-500">
                    {option.note}
                  </span>
                )}
              </span>
              {custom && <span className="settings-method-badge">özel tutar</span>}
            </label>
            {on && (
              <div className="settings-method-limits">
                {field('min', 'Min tutar (₺)')}
                {field('max', 'Max tutar (₺)')}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Yönün ortak alt/üst tutarı.
 *
 * Aşağıdaki yöntemlerden biri kendi tutarını aldığı anda bu alan
 * **kilitlenir**: aynı sayı iki yerden yönetilseydi yönetici üstte yazdığı
 * değerin neden hiçbir şeyi değiştirmediğini anlayamazdı. Kilit geri
 * alınabilir — *Ortak tutara dön* bütün yöntem tutarlarını siler.
 */
const SharedLimitFields = ({
  minLabel,
  maxLabel,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  provider,
  direction,
  limits,
  onLimitsChange,
}) => {
  const locked = directionHasCustom(limits, provider, direction);
  const field = (label, value, onChange) => (
    <label className="settings-inline-field">
      {label}
      <input
        type="number"
        min="0.01"
        step="0.01"
        value={value}
        disabled={locked}
        onChange={(e) => onChange(Number(e.target.value))}
        className="admin-input mt-1"
      />
    </label>
  );
  return (
    <>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        {field(minLabel, minValue, onMinChange)}
        {field(maxLabel, maxValue, onMaxChange)}
      </div>
      {locked && (
        <div className="settings-shared-locked">
          <span>
            Yöntemlerin kendi tutarları girildi; ortak tutar artık kullanılmıyor.
          </span>
          <button
            type="button"
            className="admin-btn-secondary"
            onClick={() => onLimitsChange(
              clearDirectionLimits(limits, provider, direction))}
          >
            Ortak tutara dön
          </button>
        </div>
      )}
    </>
  );
};

// Virgülle ayrılmış ledger listesi ile dizi arasındaki çeviri. Kayıt biçimi
// (metin) korunur: sağlayıcı istemcileri ve göçler onu okur.
const ledgerList = (value) => String(value || '')
  .split(',')
  .map((item) => item.trim().toUpperCase())
  .filter(Boolean);

const toggleLedger = (value, id) => {
  const current = ledgerList(value);
  const next = current.includes(id)
    ? current.filter((item) => item !== id)
    : [...current, id];
  return next.join(',');
};

const hydrateSecretFields = (data) => {
  const next = { ...data };
  SECRET_FIELDS.forEach((field) => {
    next[field] = data[`${field}_configured`] ? MASKED_SECRET : '';
  });
  return next;
};

// Sekmelerin `permission` alanı backend'deki alan bazlı denetimin karşılığıdır
// (bkz. content_router._ensure_settings_scope): ödeme ve limit ayarları finans
// yetkisine, kalan her şey içerik yetkisine bağlıdır.
export const tabs = [
  {
    id: 'general',
    name: 'Site Ayarları',
    icon: Globe2,
    permission: 'content.read',
    desc: 'Site adı, iletişim bilgileri, üst bilgi metni ve oyuncu portalına eklenen özel kod alanları.',
  },
  {
    id: 'customcode',
    name: 'Özel Kod',
    icon: Code2,
    permission: 'content.read',
    desc: 'Oyuncu portalına eklenen header, body ve footer kodları. Yönetim panelinde çalışmazlar.',
  },
  {
    id: 'design',
    name: 'Tasarım',
    icon: LayoutTemplate,
    permission: 'content.read',
    desc: 'Hazır şema seçimi, tema renkleri, katalog slider’ları ve promosyon kartı görünümü.',
  },
  {
    id: 'branding',
    name: 'Marka & Logo',
    icon: Palette,
    permission: 'content.read',
    desc: 'Logo görseli, metin logosu ve sekme simgesi. Site adı bu iki metinden türer.',
  },
  {
    id: 'finance',
    name: 'Finans',
    icon: Landmark,
    permission: 'finance.read',
    desc: 'Genel çekim sınırları ve ödeme sağlayıcılarının bağlantı bilgileri. Sunucudaki .env dosyası okunmaz.',
  },
  {
    id: 'affiliate',
    name: 'Affiliate',
    icon: Users,
    permission: 'content.read',
    desc: 'Ortaklık programının açılması ve genel kuralları. Komisyon oranı ortak başına Affiliate Yönetimi’nde girilir.',
  },
  {
    id: 'cashback',
    name: 'Kayıp Bonusu',
    icon: Gift,
    permission: 'content.read',
    desc: 'Kayıp iadesinin hesaplama modeli, oranı, dönemi ve çevrim şartı.',
  },
  {
    id: 'livechat',
    name: 'Canlı Destek',
    icon: Headphones,
    permission: 'content.read',
    desc: 'Canlı destek sağlayıcısı (LiveChat / Text.com), kimliği ve buton görünümü.',
  },
  {
    id: 'telegram',
    name: 'Telegram Bildirim',
    icon: Bell,
    permission: 'content.read',
    desc: 'Bot, bildirim grubu, webhook kaydı ve gönderim geçmişi.',
  },
  {
    id: 'social',
    name: 'Sosyal & İletişim',
    icon: Globe2,
    permission: 'content.read',
    desc: 'Sitede gösterilen sosyal medya adresleri.',
  },
  {
    id: 'otp',
    name: 'SMS Doğrulama',
    icon: ShieldCheck,
    permission: 'content.read',
    desc: 'HeraclesOtp ve Sempico SMS sağlayıcıları; kayıt/giriş doğrulama kodları.',
  },
  {
    id: 'smtp',
    name: 'SMTP Ayarları',
    icon: Server,
    permission: 'content.read',
    desc: 'Giden posta sunucusu, kimlik bilgileri ve test gönderimi.',
  },
  {
    id: 'mail',
    name: 'Toplu Mail',
    icon: Mail,
    permission: 'content.read',
    desc: 'Üye segmentine toplu e-posta gönderimi.',
  },
  // Yedek bütün veritabanını ve yükleme klasörünü taşır; sekme bu yüzden
  // içerik değil yönetim yetkisine bağlıdır (admin.manage).
  {
    id: 'backup',
    name: 'Yedekleme',
    icon: DatabaseBackup,
    permission: 'admin.manage',
    desc: 'Günlük yedeğin saati, Cloudflare R2 uzak yedeği, anlık yedek indirme ve geri yükleme.',
  },
];

// Özel kod kutuları. Üçü de aynı biçimde çizilir; sırası sayfadaki enjeksiyon
// sırasıdır (head → body başı → body sonu).
// Şema önizlemesi. Altı kartta da aynı maket çiziliyordu: yalnızca renkler
// değiştiği için "Merit ile Zümrüt arasındaki fark ne" sorusu karttan
// yanıtlanamıyordu. Maket artık şemanın kendi kabuğunu çizer — menünün
// nerede durduğu şemalar arasındaki asıl farktır. Geometri
// siteThemes.js'teki `shell` alanından gelir.
const ThemeShellPreview = ({ shell, bg, card, accent }) => {
  const soft = 'rgba(255,255,255,0.16)';
  const bar = (children, style) => (
    <div className="flex items-center gap-1.5 px-2" style={{ background: card, ...style }}>
      {children}
    </div>
  );
  const brand = <span className="h-1.5 w-7 rounded-full" style={{ background: accent }} />;
  const dash = (w) => <span className={`h-1 ${w} rounded-full`} style={{ background: soft }} />;
  const cta = <span className="ml-auto h-2.5 w-6 rounded" style={{ background: accent }} />;
  const hero = <div className="w-full rounded" style={{ height: 20, background: card }} />;
  const tiles = (count) => (
    <div className="grid gap-1" style={{ gridTemplateColumns: `repeat(${count}, minmax(0, 1fr))` }}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="rounded" style={{ height: 13, background: card }} />
      ))}
    </div>
  );
  const rows = (count, w) => (
    <div className="flex flex-col gap-1">
      {Array.from({ length: count }, (_, i) => (
        <span key={i} className={`h-1 ${w} rounded-full`} style={{ background: soft }} />
      ))}
    </div>
  );

  let body = null;
  if (shell === 'rail-top') {
    // Merit: sol ikon rayı + üst kategori barı.
    body = (
      <div className="flex h-full">
        <div className="flex flex-col items-center gap-1.5 py-2" style={{ width: '15%', background: card }}>
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: accent }} />
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: soft }} />
          <span className="h-1.5 w-1.5 rounded-full" style={{ background: soft }} />
        </div>
        <div className="flex flex-1 flex-col">
          {bar(<>{dash('w-5')}{dash('w-4')}{dash('w-4')}{cta}</>, { height: 16 })}
          <div className="flex flex-1 flex-col gap-1.5 p-2">{hero}{tiles(3)}</div>
        </div>
      </div>
    );
  } else if (shell === 'wide-left') {
    // Zümrüt: başlıklı geniş sol menü.
    body = (
      <div className="flex h-full">
        <div className="flex flex-col gap-1.5 p-2" style={{ width: '34%', background: card }}>
          <span className="h-2 w-full rounded" style={{ background: accent }} />
          {rows(4, 'w-full')}
        </div>
        <div className="flex flex-1 flex-col gap-1.5 p-2">{hero}{tiles(3)}</div>
      </div>
    );
  } else if (shell === 'two-tier') {
    // Paris: iki katlı üst menü.
    body = (
      <div className="flex h-full flex-col">
        {bar(<>{brand}{dash('w-4')}{dash('w-4')}{cta}</>, { height: 15 })}
        {bar(<>{dash('w-3')}{dash('w-3')}{dash('w-3')}{dash('w-3')}</>, {
          height: 11, background: 'rgba(255,255,255,0.06)',
        })}
        <div className="flex flex-1 flex-col gap-1.5 p-2">{hero}{tiles(4)}</div>
      </div>
    );
  } else if (shell === 'three-col') {
    // Herkül: sol ray + içerik + sohbet rayı.
    body = (
      <div className="flex h-full">
        <div className="flex flex-col gap-1.5 p-1.5" style={{ width: '20%', background: card }}>
          <span className="h-1.5 w-full rounded-full" style={{ background: accent }} />
          {rows(3, 'w-full')}
        </div>
        <div className="flex flex-1 flex-col">
          {bar(<>{dash('w-4')}{dash('w-4')}{cta}</>, { height: 14 })}
          <div className="flex flex-1 flex-col gap-1.5 p-1.5">{hero}{tiles(2)}</div>
        </div>
        <div className="flex flex-col gap-1 p-1.5" style={{ width: '26%', background: card }}>
          {rows(5, 'w-full')}
        </div>
      </div>
    );
  } else if (shell === 'top-offcanvas') {
    // Vega: üst kategori barı, sol menü hamburgerin arkasında.
    body = (
      <div className="flex h-full flex-col">
        {bar(
          <>
            <span className="flex flex-col gap-0.5">
              <span className="h-0.5 w-3 rounded-full" style={{ background: soft }} />
              <span className="h-0.5 w-3 rounded-full" style={{ background: soft }} />
              <span className="h-0.5 w-3 rounded-full" style={{ background: soft }} />
            </span>
            {brand}
            <span className="ml-auto h-3 w-7 rounded-full" style={{ background: accent }} />
          </>,
          { height: 17 },
        )}
        <div className="flex flex-1 flex-col gap-1.5 p-2">
          <div className="flex gap-1">
            {[0, 1, 2, 3].map((i) => (
              <span key={i} className="h-2.5 flex-1 rounded-full" style={{ background: card }} />
            ))}
          </div>
          {hero}
        </div>
      </div>
    );
  } else {
    // Klasik: header + içerik + footer.
    body = (
      <div className="flex h-full flex-col">
        {bar(<>{brand}{dash('w-4')}{dash('w-4')}{cta}</>, { height: 16 })}
        <div className="flex flex-1 flex-col gap-1.5 p-2">{hero}{tiles(4)}</div>
        <div style={{ height: 8, background: card, opacity: 0.7 }} />
      </div>
    );
  }

  return (
    <div
      className="overflow-hidden rounded-lg"
      style={{ height: 96, background: bg, border: '1px solid var(--v2-line, rgba(255,255,255,0.07))' }}
    >
      {body}
    </div>
  );
};


// Sekmeler beş başlık altında toplanır: on üç pill düz bir şeritte iki-üç
// satıra sarıyor ve aralarında hiçbir hiyerarşi kalmıyordu. Gruplar yalnızca
// menünün düzenidir — yetki, alan kümesi ve kaydetme kapsamı sekme başınadır.
// Yeni bir sekme eklerken buraya da yazın, yoksa menüde hiç görünmez.
export const TAB_GROUPS = [
  { name: 'Site', ids: ['general', 'customcode', 'social'] },
  { name: 'Görünüm', ids: ['design', 'branding'] },
  { name: 'Para', ids: ['finance', 'affiliate', 'cashback'] },
  { name: 'İletişim', ids: ['livechat', 'telegram', 'otp', 'smtp', 'mail'] },
  { name: 'Sistem', ids: ['backup'] },
];

// Oyun servisleri tek yerden tanımlanır: kart, form alanları, kapsam
// anahtarları ve callback adresi bu listeden üretilir. Yeni bir sağlayıcı
// eklemek için buraya bir kayıt eklemek yeterlidir.
const gameServices = [
  {
    id: 'goldpalace',
    name: 'Gold Palace',
    icon: Spade,
    accent: '#22c55e',
    enabledKey: 'goldpalace_enabled',
    tagline: 'Seamless cüzdan · Slot + Canlı Casino',
    note: 'Callback Token boş bırakılırsa Kaydet sırasında güvenli biçimde otomatik oluşturulur; tokenlar tekrar gösterilmez.',
    callbackPath: '/api/goldpalace_api',
    scopes: [
      { key: 'slot_goldpalace_enabled', label: 'Slot oyunları' },
      { key: 'live_casino_goldpalace_enabled', label: 'Canlı casino' },
    ],
    fields: [
      {
        key: 'goldpalace_api_url',
        label: 'API URL',
        placeholder: 'https://agent.goldslotpalase.com',
        required: true,
        full: true,
      },
      { key: 'goldpalace_api_token', label: 'API Token', secret: true, required: true },
      {
        key: 'goldpalace_callback_token',
        label: 'Callback Token',
        secret: true,
        required: true,
        autoGenerate: true,
      },
      {
        key: 'goldpalace_return_url',
        label: 'Oyundan Dönüş URL’si',
        placeholder: '/slotlar',
        full: true,
      },
    ],
  },
  {
    id: 'oroplay',
    name: 'OroPlay',
    icon: Gem,
    accent: '#f472b6',
    enabledKey: 'oroplay_enabled',
    tagline: 'Seamless cüzdan · Slot + Canlı Casino + Mini oyun',
    note: 'OroPlay seamless cüzdanla çalışır: clientId/clientSecret hem giden isteklerin bearer token’ını üretir hem de sağlayıcının bize gönderdiği webhook’un Basic auth’unu doğrular. Sağlayıcı IP beyaz listesi uygular; sunucunun çıkış IP’si OroPlay panelinde ekli değilse bağlantı 403 döner.',
    // Sağlayıcı callback adresinin sonuna kendi ek yolunu koyabildiği için
    // uç hem /api/oroplay/transaction hem /api/oroplay/api/transaction
    // adresinden karşılanır; panele taban adres yazılır.
    callbackPath: '/api/oroplay',
    scopes: [
      { key: 'slot_oroplay_enabled', label: 'Slot oyunları' },
      { key: 'live_casino_oroplay_enabled', label: 'Canlı casino' },
    ],
    fields: [
      {
        key: 'oroplay_api_url',
        label: 'API URL',
        placeholder: 'https://oroplay.com/api/v2',
        required: true,
        full: true,
      },
      { key: 'oroplay_client_id', label: 'Client ID', required: true },
      {
        key: 'oroplay_client_secret',
        label: 'Client Secret',
        secret: true,
        required: true,
      },
      {
        key: 'oroplay_return_url',
        label: 'Oyundan Dönüş URL’si (lobbyUrl)',
        placeholder: '/slotlar',
        full: true,
      },
    ],
  },
  {
    id: 'drakon',
    name: 'Drakon',
    icon: Dices,
    accent: '#38bdf8',
    enabledKey: 'drakon_enabled',
    tagline: 'Seamless cüzdan · demo (mode=fun) destekli',
    note: 'Drakon bağlantısı da yönetim ayarlarında tutulur; token ve secret tekrar gösterilmez. '
      + 'Drakon, oyunu açmadan önce aşağıdaki callback adresine bakiye sorar ve kimliğini isteğin '
      + 'gövdesinde agent bilgileriyle bildirir; ayrı bir doğrulama başlığı göndermez. '
      + 'İzinli IP listesi isteğe bağlıdır — yalnızca kimlik bilgisi taşımayan çağrılar için yedek kapıdır.',
    // Drakon adresin sonuna /drakon_api ekini kendisi ekler.
    callbackPath: '/api',
    scopes: [
      { key: 'slot_drakon_enabled', label: 'Slot oyunları' },
      { key: 'live_casino_drakon_enabled', label: 'Canlı casino' },
    ],
    fields: [
      {
        key: 'drakon_api_url',
        label: 'API URL',
        placeholder: 'https://gator.drakonapi.tech/api/v1',
        required: true,
        full: true,
      },
      { key: 'drakon_agent_code', label: 'Agent Code', placeholder: 'Agent code' },
      { key: 'drakon_agent_token', label: 'Agent Token', secret: true, required: true },
      { key: 'drakon_agent_secret', label: 'Agent Secret', secret: true, required: true, full: true },
      {
        key: 'drakon_webhook_secret',
        label: 'Webhook Secret (Drakon göndermez — yalnızca ara katman kullanıyorsanız)',
        secret: true,
        full: true,
      },
      {
        key: 'drakon_allowed_ips',
        label: 'İzinli webhook IP adresleri (isteğe bağlı, virgülle ayırın)',
        placeholder: '203.0.113.10, 203.0.113.11',
        full: true,
      },
    ],
  },
  {
    id: 'scorpio',
    name: 'ScorpioPlay',
    icon: Joystick,
    accent: '#a78bfa',
    enabledKey: 'scorpio_enabled',
    tagline: 'Transfer / seamless cüzdan · otomatik mod',
    note: 'Bilgiler panelden yönetilir; boş bırakılan alanlar için sunucudaki .env değeri kullanılır.',
    callbackPath: '/api/scorpio_api',
    scopes: [
      { key: 'live_casino_scorpio_enabled', label: 'Canlı casino' },
    ],
    fields: [
      {
        key: 'scorpio_api_url',
        label: 'API URL',
        placeholder: 'https://api.scorpioplay.com',
        required: true,
        full: true,
      },
      { key: 'scorpio_api_token', label: 'API Token', secret: true, required: true },
      { key: 'scorpio_operator_id', label: 'Operatör ID', placeholder: 'Operatör kodu' },
      {
        key: 'scorpio_return_url',
        label: 'Oyundan Dönüş URL’si',
        placeholder: '/slotlar',
        full: true,
      },
    ],
  },
];

// Panelden açılıp kapatılabilen bildirim olayları.
const telegramEvents = [
  { key: 'telegram_notify_register', label: 'Yeni üye kaydı' },
  { key: 'telegram_notify_login', label: 'Üye girişi' },
  { key: 'telegram_notify_deposit', label: 'Para yatırma talebi' },
  { key: 'telegram_notify_deposit_result', label: 'Yatırım onay / red' },
  { key: 'telegram_notify_withdraw', label: 'Para çekme talebi' },
  { key: 'telegram_notify_withdraw_result', label: 'Çekim onay / red' },
  { key: 'telegram_notify_kyc', label: 'KYC başvurusu ve sonucu' },
  { key: 'telegram_notify_bonus', label: 'Bonus / kayıp bonusu talebi' },
  { key: 'telegram_notify_balance', label: 'Admin bakiye işlemi' },
];

// Oyun servisleri sekmesindeki sağlayıcı alanları tek kaynaktan türetilir;
// gameServices listesine yeni bir sağlayıcı eklendiğinde kapsama kendiliğinden
// girer.
const gameServiceFieldKeys = gameServices.flatMap((service) => [
  service.enabledKey,
  ...service.fields.map((field) => field.key),
  ...(service.scopes || []).map((scope) => scope.key),
]);

// Her sekmenin yönettiği ayar anahtarları. Kaydet yalnızca açık sekmedeki
// değişmiş alanları gönderir, bu yüzden panele yeni bir alan eklerken
// anahtarını buraya da yazın — listede olmayan alan hiçbir sekmeye ait
// sayılmaz ve yalnızca aşağıdaki "sahipsiz alan" ağıyla kurtarılır.
// Text.com organizasyon kimliği UUID'dir (backend/services/livechat_provider.py).
const TEXT_ORGANIZATION_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const TAB_FIELDS = {
  general: [
    'site_name', 'contact_email', 'contact_phone', 'top_bar_message',
    'footer_text',
    'maintenance_mode', 'registration_open', 'kyc_auto_approve',
    'callback_enabled',
    'portal_block_context_menu', 'portal_block_text_copy',
    'portal_block_devtools',
  ],
  // Hazır şema ve tema renkleri birlikte kaydedilir: şema seçildiğinde renk
  // alanları da o şemanın vurgularıyla dolar.
  // Özel kod alanları Site Ayarları'nın içindeyken sayfayı ikiye bölüyordu:
  // üç kod kutusu, aralarındaki uyarı ve altındaki bayraklar tek listede
  // sıralanıyordu. Kendi bölümüne alındı.
  // Özel kod artık `settings` kaydında değildir: parçalar kendi tablosundadır
  // ve kendi ucundan yazılır (bkz. lib/customCode.js). Liste bilinçli olarak
  // boştur — burada bir alan bırakılsaydı ayar kaydına da yazılır ve iki
  // kaynak ayrışırdı.
  customcode: [],
  design: [
    'site_theme', 'primary_color', 'secondary_color', 'mobile_bottom_nav',
    'bonus_card_stats_enabled',
    // Yalnızca Vega şemasında çizilir ama her zaman kaydedilir: başka bir
    // şemaya geçip geri dönen kurulum seçimini kaybetmemelidir.
    'vega_slot_slider_enabled', 'vega_live_casino_slider_enabled',
    'vega_slot_slider_use_main', 'vega_live_casino_slider_use_main',
  ],
  // site_name burada yok: marka sekmesinde logo metinlerinden türetilir ve
  // saveBranding kapsamına ayrıca eklenir.
  branding: [
    'logo_image_url', 'favicon_url',
    'logo_text_primary', 'logo_text_secondary',
  ],
  finance: [
    'withdraw_min_amount', 'withdraw_max_amount',
    // Yöntem başına alt/üst tutar (services/payment_limits.py).
    'payment_method_limits',
    'wizkasa_enabled',
    'wizkasa_deposit_enabled', 'wizkasa_withdraw_enabled',
    'wizkasa_deposit_min_amount', 'wizkasa_deposit_max_amount',
    'wizkasa_withdraw_min_amount', 'wizkasa_withdraw_max_amount',
    'wizkasa_api_url', 'wizkasa_api_key', 'wizkasa_secret_key',
    'wizkasa_callback_url', 'wizkasa_return_url',
    'wizkasa_deposit_ledger_types', 'wizkasa_withdraw_ledger_types',
    'wizkasa_deposit_channel',
    'wizkasa_crypto_coin', 'wizkasa_crypto_network',
    'hemensystem_enabled',
    'hemensystem_deposit_enabled', 'hemensystem_withdraw_enabled',
    'hemensystem_deposit_min_amount', 'hemensystem_deposit_max_amount',
    'hemensystem_withdraw_min_amount', 'hemensystem_withdraw_max_amount',
    'hemensystem_creditcard_enabled',
    'hemensystem_creditcard_min_amount', 'hemensystem_creditcard_max_amount',
    'hemensystem_api_url', 'hemensystem_api_type',
    'hemensystem_email', 'hemensystem_password', 'hemensystem_api_secret',
    'hemensystem_website_id',
    'hemensystem_callback_url',
    'hemensystem_success_url', 'hemensystem_failed_url',
    'payurus_enabled',
    'payurus_deposit_enabled', 'payurus_withdraw_enabled',
    'payurus_deposit_min_amount', 'payurus_deposit_max_amount',
    'payurus_withdraw_min_amount', 'payurus_withdraw_max_amount',
    'payurus_api_url', 'payurus_secret', 'payurus_redirect_mode',
    'payurus_return_url',
    'payurus_callback_url', 'payurus_callback_registered',
    'ultrapay_enabled',
    'ultrapay_deposit_enabled', 'ultrapay_withdraw_enabled',
    'ultrapay_deposit_min_amount', 'ultrapay_deposit_max_amount',
    'ultrapay_withdraw_min_amount', 'ultrapay_withdraw_max_amount',
    'ultrapay_api_url', 'ultrapay_api_key', 'ultrapay_api_secret',
    'ultrapay_deposit_methods', 'ultrapay_withdraw_methods',
    'ultrapay_crypto_pairs', 'ultrapay_transaction_prefix',
    'ultrapay_test_mode', 'ultrapay_return_url', 'ultrapay_callback_url',
    'linkhavale_enabled',
    'linkhavale_deposit_enabled', 'linkhavale_withdraw_enabled',
    'linkhavale_deposit_min_amount', 'linkhavale_deposit_max_amount',
    'linkhavale_withdraw_min_amount', 'linkhavale_withdraw_max_amount',
    'linkhavale_api_url', 'linkhavale_api_key', 'linkhavale_api_secret',
    'linkhavale_deposit_methods', 'linkhavale_withdraw_methods',
    'linkhavale_crypto_pairs',
    'linkhavale_return_url', 'linkhavale_callback_url',
    'vevopay_enabled',
    'vevopay_deposit_enabled', 'vevopay_withdraw_enabled',
    'vevopay_deposit_min_amount', 'vevopay_deposit_max_amount',
    'vevopay_withdraw_min_amount', 'vevopay_withdraw_max_amount',
    'vevopay_api_url', 'vevopay_client_token', 'vevopay_client_secret',
    'vevopay_payment_domain', 'vevopay_hosted_deposit',
    'vevopay_callback_url', 'vevopay_callback_registered',
    'hera_enabled',
    'hera_deposit_enabled', 'hera_withdraw_enabled',
    'hera_deposit_min_amount', 'hera_deposit_max_amount',
    'hera_withdraw_min_amount', 'hera_withdraw_max_amount',
    'hera_api_url', 'hera_api_key', 'hera_api_secret',
    'hera_deposit_methods', 'hera_return_url',
    'hera_callback_url', 'hera_callback_registered',
  ],
  affiliate: [
    'affiliate_enabled', 'affiliate_min_withdraw', 'affiliate_cookie_days',
    'affiliate_terms',
  ],
  cashback: [
    'cashback_enabled', 'cashback_period', 'cashback_wager_multiplier',
    'cashback_min_payout', 'cashback_max_bonus', 'cashback_tiers',
  ],
  gameapi: [
    'game_api_enabled',
    ...gameServiceFieldKeys,
    // Gateway alanları bilinçli olarak burada değil: tek yönetim yeri
    // Oyun Kontrol → Sistem sayfasıdır (GameSystemSettingsPanel).
    'game_api_currency', 'game_api_language', 'game_api_rtp',
    'demo_enabled', 'demo_requires_login', 'demo_start_balance', 'demo_rtp',
  ],
  livechat: [
    // Sağlayıcı ve Text.com kimliği burada değildir: `provider_configs`'te
    // durur ve sekmenin Kaydet düğmesi onu `/admin/livechat` ucuna yazar.
    'livechat_enabled', 'livechat_require_login', 'livechat_license_id',
    'livechat_group_id', 'livechat_icon_url',
  ],
  telegram: [
    'telegram_bot_enabled', 'telegram_bot_token', 'telegram_chat_id',
    'telegram_thread_id', 'anchor_telegram_bot_token',
    'telegram_community_chat_id', 'telegram_url',
    'telegram_webhook_secret', 'promo_require_telegram',
    ...telegramEvents.map((event) => event.key),
  ],
  social: ['instagram_url', 'telegram_url', 'whatsapp_url'],
  otp: ['heracles_otp_api_key', 'registration_otp_enabled'],
  smtp: [
    'smtp_host', 'smtp_port', 'smtp_user', 'smtp_password', 'smtp_from_name',
    'smtp_use_tls',
  ],
  // Toplu mail ayar kaydetmez, kendi gönderim ucunu kullanır.
  mail: [],
  backup: [
    'backup_enabled', 'backup_hour', 'backup_include_uploads',
  ],
};

const KNOWN_TAB_FIELDS = new Set(Object.values(TAB_FIELDS).flat());

// Backend callback token'ı ancak bu alanlardan biri istekte varsa üretir
// (content_router.a_update_settings içindeki kesişim kontrolü).
const GOLDPALACE_TRIGGER_FIELDS = [
  'goldpalace_enabled', 'goldpalace_api_url', 'goldpalace_api_token',
  'goldpalace_callback_token', 'goldpalace_return_url',
];

// Ayar değerleri metin, sayı, bool ve dizi (cashback_tiers) olabildiği için
// karşılaştırma JSON üzerinden yapılır.
const sameValue = (a, b) => (
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null)
);

const changedKeys = (current, base, keys) => (
  (!current || !base) ? [] : keys.filter((key) => !sameValue(current[key], base[key]))
);

// Logo metinlerinden türetilen site adı; marka sekmesi kaydedilince site_name
// bununla eşitlenir.
const brandingSiteName = (settings) => [
  settings.logo_text_primary,
  settings.logo_text_secondary,
].map((part) => String(part || '').trim()).join('');

// Telegram gönderim geçmişi sayfa sayfa gösterilir; liste ayarların altında
// durduğu için uzun bir dökümden çok kısa bir sayfa daha okunaklı.
const TELEGRAM_LOG_PAGE_SIZE = 5;

// İzlenen iş bu süre içinde kaydını açmadıysa yoklama bırakılır.
const BACKUP_WATCH_TIMEOUT_MS = 45 * 60 * 1000;

// Yeni panel tasarımında bu sayfa tek bir "Genel Ayarlar" ekranı değildir:
// her menü maddesi sekmelerin bir alt kümesini açar (`onlyTabs`, bkz.
// lib/navigation.js). Alanlar, kaydetme kapsamı ve yetki sekme başına
// olduğu için hiçbiri değişmez; yalnızca hangi sekmenin görüneceği daralır.
// Prop verilmezse klasik görünümün bütün sekmeli sayfası çizilir.
// `financePart` Finans sekmesini ikiye böler: `rules` yalnızca çekim
// sınırlarını ve kurallarını, `providers` yalnızca sağlayıcı bağlantılarını
// çizer (yeni tasarımda Finans → Çekim Kuralları / Ödeme Sağlayıcıları).
const SettingsPage = ({
  onlyTabs, title: pageTitle, desc: pageDesc, icon: PageIcon, financePart,
} = {}) => {
  const { can } = useAdminPermissions();
  const onlyKey = onlyTabs ? onlyTabs.join(',') : '';
  const [s, setS] = useState(null);
  // Adres tabanları sunucudan gelir; tarayıcının origin'i kullanılamaz.
  // Panel ayrı bir alan adına taşındığında oyuncunun dönüş adresi de
  // sağlayıcı callback'i de panelin adresine yazılırdı
  // (bkz. backend/services/site_address.py address_bases).
  const addressBases = s?.address_bases || {};
  const siteBase = addressBases.site || '';
  const paymentBase = addressBases.payment || siteBase;
  const gameBase = addressBases.game || siteBase;
  // Sunucudaki son hâli; "hangi alan değişti" hesabı buna göre yapılır.
  const [initial, setInitial] = useState(null);
  const [tab, setTab] = useState(onlyTabs?.[0] || 'general');
  const visibleTabs = useMemo(
    () => (onlyKey
      ? onlyKey.split(',').map((id) => tabs.find((item) => item.id === id))
      : tabs
    ).filter((item) => item && can(item.permission)),
    [can, onlyKey],
  );
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [mail, setMail] = useState({ subject: '', body_html: '', target: 'all', custom_emails: '' });
  const [logs, setLogs] = useState([]);
  const [error, setError] = useState('');
  const [confirmSecret, setConfirmSecret] = useState(false);
  const [pendingPayload, setPendingPayload] = useState(null);
  // Parolayla korunan tasarım şeması. Parola bir kez doğrulanınca ayar
  // kaydına işlenir (`site_theme_unlocked`), o şema bir daha sorulmaz.
  const [themeUnlockPrompt, setThemeUnlockPrompt] = useState(null);
  const [themeUnlockPassword, setThemeUnlockPassword] = useState('');
  // Panelde listelenmeyen, kuruluma özel şemanın anahtarı (bkz. siteThemes.js).
  const [customThemeKey, setCustomThemeKey] = useState('');
  const [themeUnlockBusy, setThemeUnlockBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  // Yükleme yüzdesi alan başına tutulur: 30 MB'lık hareketli bir logo
  // yavaş bağlantıda dakikalar sürer ve yalnızca dönen bir ikon, işin
  // takıldığı sanılıp sayfanın yenilenmesine yol açıyordu (nginx 499).
  const [uploadProgress, setUploadProgress] = useState({});
  const [faviconBusy, setFaviconBusy] = useState(false);
  const [liveChatIconBusy, setLiveChatIconBusy] = useState(false);
  const [liveChatProvider, setLiveChatProvider] = useState(
    { provider: 'livechat', organization_id: '' });
  const [liveChatProviderInitial, setLiveChatProviderInitial] = useState(null);
  useEffect(() => {
    let alive = true;
    client.get('/admin/livechat')
      .then(({ data }) => {
        if (!alive) return;
        const loaded = {
          provider: data.provider || 'livechat',
          organization_id: data.organization_id || '',
        };
        setLiveChatProvider(loaded);
        setLiveChatProviderInitial(loaded);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  const [telegramLogs, setTelegramLogs] = useState([]);
  const [telegramLogPage, setTelegramLogPage] = useState(0);
  const [telegramLogTotal, setTelegramLogTotal] = useState(0);
  const [telegramWebhookBusy, setTelegramWebhookBusy] = useState(false);
  // Telegram grup kimliğini arayüzünde göstermiyor; botun gördüğü gruplar
  // listelenip tek tıkla seçtirilir.
  const [telegramChats, setTelegramChats] = useState(null);
  const [telegramChatsBusy, setTelegramChatsBusy] = useState(false);
  // Tür başına hedef grup `settings` kaydında değil `provider_configs`'in
  // `telegram_routes` satırındadır; null = henüz okunmadı.
  const [telegramRoutes, setTelegramRoutes] = useState({});
  const [telegramRoutesInitial, setTelegramRoutesInitial] = useState(null);
  // Secret kaydedildikten sonra sunucudan geri okunamaz (bot token gibi
  // write-only); üretilen değer bu yüzden kaydedilmeden önce gösterilir.
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  const [telegramWebhookInfo, setTelegramWebhookInfo] = useState(null);
  const [heraclesOtpStatus, setHeraclesOtpStatus] = useState(null);
  const [heraclesOtpBusy, setHeraclesOtpBusy] = useState(false);
  const [wizkasaStatus, setWizkasaStatus] = useState(null);
  // Bağlantı bilgileri nadiren değişir; sekme açılınca kapalı gelir.
  const [wizkasaApiOpen, setWizkasaApiOpen] = useState(false);
  const [hemenStatus, setHemenStatus] = useState(null);
  const [hemenApiOpen, setHemenApiOpen] = useState(false);
  const [heraStatus, setHeraStatus] = useState(null);
  const [heraApiOpen, setHeraApiOpen] = useState(false);
  const [payurusStatus, setPayurusStatus] = useState(null);
  const [payurusApiOpen, setPayurusApiOpen] = useState(false);
  const [ultrapayStatus, setUltrapayStatus] = useState(null);
  const [ultrapayApiOpen, setUltrapayApiOpen] = useState(false);
  const [linkhavaleStatus, setLinkhavaleStatus] = useState(null);
  const [linkhavaleApiOpen, setLinkhavaleApiOpen] = useState(false);
  const [vevopayStatus, setVevopayStatus] = useState(null);
  // HızlıcaParalar ayarları `settings` kaydında değil `provider_configs`
  // satırındadır; bu yüzden kendi uçlarından okunup yazılır. Ana Kaydet
  // düğmesi ikisini birden gönderir — yöneticinin aynı sekmede iki düğme
  // araması, "kaydettim ama kaydolmadı" şikâyetinin en kısa yoludur.
  const [hizlica, setHizlica] = useState(HIZLICAPARALAR_DEFAULTS);
  const [hizlicaInitial, setHizlicaInitial] = useState(HIZLICAPARALAR_DEFAULTS);
  const [hizlicaStatus, setHizlicaStatus] = useState(null);
  const [hizlicaApiOpen, setHizlicaApiOpen] = useState(false);
  // Hızlı Havale de `provider_configs` satırındadır; ayrı state, ayrı uç.
  const [hizliHavale, setHizliHavale] = useState(HIZLIHAVALE_DEFAULTS);
  const [hizliHavaleInitial, setHizliHavaleInitial] = useState(
    HIZLIHAVALE_DEFAULTS);
  const [hizliHavaleStatus, setHizliHavaleStatus] = useState(null);
  const [hizliHavaleApiOpen, setHizliHavaleApiOpen] = useState(false);
  // Zelvoq da `provider_configs` satırındadır; ayrı state, ayrı uç.
  const [zelvoq, setZelvoq] = useState(ZELVOQ_DEFAULTS);
  const [zelvoqInitial, setZelvoqInitial] = useState(ZELVOQ_DEFAULTS);
  const [zelvoqStatus, setZelvoqStatus] = useState(null);
  const [zelvoqApiOpen, setZelvoqApiOpen] = useState(false);
  // CryptoBox bölümü kendi state'ini taşır; buraya yalnızca rozet için
  // açık/kapalı bilgisi düşer.
  const [cryptoboxEnabled, setCryptoboxEnabled] = useState(false);
  const [cashmoneyEnabled, setCashmoneyEnabled] = useState(false);
  const [payluxEnabled, setPayluxEnabled] = useState(false);
  const [expayEnabled, setExpayEnabled] = useState(false);
  const [primevpayEnabled, setPrimevpayEnabled] = useState(false);
  // Özel kod ayrı bir uçtan gelir (kendi tablosu, sürüm geçmişi). Sekme
  // açılmadan istenmez: her panel açılışında bir istek daha, tek worker'lı
  // backend'de gereksiz bir yüktür.
  const [customCode, setCustomCode] = useState(EMPTY_CUSTOM_CODE);
  const [customCodeInitial, setCustomCodeInitial] = useState(EMPTY_CUSTOM_CODE);
  const [customCodeMeta, setCustomCodeMeta] = useState({});
  const [vevopayApiOpen, setVevopayApiOpen] = useState(false);
  // website_id kaydedildikten sonra salt okunur olur; yönetici kilidi bilerek
  // açmadıkça değiştirilemez (yanlışlıkla düzeltilen bir hane sağlayıcıdaki
  // işlem geçmişini bu siteden koparır).
  const [websiteIdUnlocked, setWebsiteIdUnlocked] = useState(false);
  const [backupStatus, setBackupStatus] = useState(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [downloadBusy, setDownloadBusy] = useState('');
  const [snapshotFormat, setSnapshotFormat] = useState('zip');
  // Anlık yedek arka planda alınır; hazır olunca kendiliğinden indirilsin
  // diye başlatma anındaki son kayıt kimliği tutulur (yeni satır o değilse
  // henüz iş başlamamıştır).
  const [jobWatch, setJobWatch] = useState(null);
  const [restoreFile, setRestoreFile] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const [financeSection, setFinanceSection] = useState(FINANCE_SECTIONS[0].id);
  const [gameService, setGameService] = useState(gameServices[0].id);
  const [copiedCallback, setCopiedCallback] = useState('');
  // Sol menünün süzgeci. Bölüm adında arar — alan adlarının kayıtlı bir
  // etiket listesi yok, arayana bulamayacağı bir arama vaat etmeyelim.
  const [navQuery, setNavQuery] = useState('');
  const { toast } = useToast();

  const load = async () => {
    setError('');
    try {
      const { data } = await client.get('/admin/settings');
      const hydrated = hydrateSecretFields(data);
      setS(hydrated);
      setInitial(hydrated);
      const { data: l } = await client.get('/admin/mail/logs');
      setLogs(l.items || []);
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Ayarlar yüklenemedi');
    }
  };
  useEffect(() => { load(); }, []);

  // HızlıcaParalar ayarları ayrı bir uçtan gelir (`provider_configs`). Finans
  // sekmesi açılmadan istenmez: her panel açılışında bir istek daha, tek
  // worker'lı backend'de gereksiz bir yüktür.
  const loadHizlica = async () => {
    try {
      const { data } = await client.get('/admin/hizlicaparalar/config');
      const merged = { ...HIZLICAPARALAR_DEFAULTS, ...data };
      // Sır panele geri dönmez; alan maskeyle çizilir ve maske olduğu gibi
      // geri gönderildiğinde yazılmaz.
      merged.api_secret = data.api_secret_configured ? MASKED_SECRET : '';
      setHizlica(merged);
      setHizlicaInitial(merged);
    } catch (requestError) {
      // Yetkisi olmayan yönetici bu bölümü hiç görmez; sessiz geç.
    }
  };
  const loadHizliHavale = async () => {
    try {
      const { data } = await client.get('/admin/hizlihavale/config');
      const merged = { ...HIZLIHAVALE_DEFAULTS, ...data };
      merged.api_secret = data.api_secret_configured ? MASKED_SECRET : '';
      setHizliHavale(merged);
      setHizliHavaleInitial(merged);
    } catch (requestError) {
      // Yetkisi olmayan yönetici bu bölümü hiç görmez; sessiz geç.
    }
  };
  const loadZelvoq = async () => {
    try {
      const { data } = await client.get('/admin/zelvoq/config');
      const merged = { ...ZELVOQ_DEFAULTS, ...data };
      // Sırlar panele geri dönmez; maskesi olduğu gibi duran değer
      // kaydedilirken gönderilmez ve kayıtlı anahtar korunur.
      merged.secret_key = data.secret_key_configured ? MASKED_SECRET : '';
      merged.secret_with = data.secret_with_configured ? MASKED_SECRET : '';
      setZelvoq(merged);
      setZelvoqInitial(merged);
    } catch (requestError) {
      // Yetkisi olmayan yönetici bu bölümü hiç görmez; sessiz geç.
    }
  };
  const loadCustomCode = async () => {
    try {
      const { data } = await client.get('/admin/custom-code');
      setCustomCode(codeFromResponse(data));
      setCustomCodeInitial(codeFromResponse(data));
      setCustomCodeMeta(metaFromResponse(data));
    } catch (requestError) {
      // Yetkisi olmayan yönetici bu bölümü hiç görmez; sessiz geç.
    }
  };
  useEffect(() => {
    if (tab === 'finance' && hizlicaInitial === HIZLICAPARALAR_DEFAULTS) {
      loadHizlica();
    }
    if (tab === 'finance' && hizliHavaleInitial === HIZLIHAVALE_DEFAULTS) {
      loadHizliHavale();
    }
    if (tab === 'finance' && zelvoqInitial === ZELVOQ_DEFAULTS) {
      loadZelvoq();
    }
    if (tab === 'telegram' && telegramRoutesInitial === null) {
      loadTelegramRoutes();
    }
    if (tab === 'finance') {
      client.get('/admin/cryptobox/config')
        .then(({ data }) => setCryptoboxEnabled(Boolean(data?.enabled)))
        .catch(() => {});
      client.get('/admin/cashmoney/config')
        .then(({ data }) => setCashmoneyEnabled(Boolean(data?.enabled)))
        .catch(() => {});
      client.get('/admin/paylux/config')
        .then(({ data }) => setPayluxEnabled(Boolean(data?.enabled)))
        .catch(() => {});
      client.get('/admin/expay/config')
        .then(({ data }) => setExpayEnabled(Boolean(data?.enabled)))
        .catch(() => {});
      client.get('/admin/primevpay/config')
        .then(({ data }) => setPrimevpayEnabled(Boolean(data?.enabled)))
        .catch(() => {});
    }
    if (tab === 'customcode' && customCodeInitial === EMPTY_CUSTOM_CODE) {
      loadCustomCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const customCodeChanges = () => customCodeChangedFields(
    customCode, customCodeInitial);

  const customCodeDirtyKeys = () => customCodeDirtyFields(
    customCode, customCodeInitial);

  const saveCustomCode = async () => {
    const payload = customCodeChanges();
    if (!Object.keys(payload).length) return;
    const { data } = await client.put('/admin/custom-code', payload);
    setCustomCode(codeFromResponse(data));
    setCustomCodeInitial(codeFromResponse(data));
    setCustomCodeMeta(metaFromResponse(data));
    // Portal özel kodu `/settings` yanıtından okur; kaydettikten sonra
    // tazelenmezse yönetici kendi sitesinde eski stili görmeye devam eder.
    notifySettingsUpdated();
  };

  // Sürüm geri yüklendiğinde alanlar da o metne döner: ekranda eski metin
  // dururken yayında yenisi olsaydı bir sonraki Kaydet geri yüklemeyi ezerdi.
  const applyRestoredCode = (record) => {
    if (!record?.slot) return;
    setCustomCode((current) => ({ ...current, [record.slot]: record.content || '' }));
    setCustomCodeInitial((current) => ({ ...current, [record.slot]: record.content || '' }));
    setCustomCodeMeta((current) => ({ ...current, [record.slot]: record }));
    notifySettingsUpdated();
  };

  // Yalnızca değişen alanlar gönderilir: tam gövde yazmak kayıtlı anahtarı
  // silerdi (uç birleştirme yapar ama maskelenmiş sırrı da geri yazardı).
  const hizlicaChanges = () => hizlicaparalarChangedFields(
    hizlica, hizlicaInitial, { paymentBase, maskedSecret: MASKED_SECRET });

  // Kaydet düğmesinin görünürlüğü için: callback adresi **sayılmaz**.
  // O alan salt okunurdur ve kaydederken sessizce güncel köke çekilir;
  // değişiklik sayılsaydı yönetici hiçbir şeye dokunmadan Kaydet düğmesini
  // sürekli açık görürdü.
  const hizlicaDirtyKeys = () => hizlicaparalarDirtyFields(
    hizlica, hizlicaInitial, { paymentBase, maskedSecret: MASKED_SECRET });

  const saveHizlica = async () => {
    const payload = hizlicaChanges();
    if (!Object.keys(payload).length) return;
    const { data } = await client.patch(
      '/admin/hizlicaparalar/config', payload);
    const merged = { ...HIZLICAPARALAR_DEFAULTS, ...data };
    merged.api_secret = data.api_secret_configured ? MASKED_SECRET : '';
    setHizlica(merged);
    setHizlicaInitial(merged);
  };

  const hizliHavaleChanges = () => hizlihavaleChangedFields(
    hizliHavale, hizliHavaleInitial,
    { paymentBase, maskedSecret: MASKED_SECRET });

  const hizliHavaleDirtyKeys = () => hizlihavaleDirtyFields(
    hizliHavale, hizliHavaleInitial,
    { paymentBase, maskedSecret: MASKED_SECRET });

  const saveHizliHavale = async () => {
    const payload = hizliHavaleChanges();
    if (!Object.keys(payload).length) return;
    const { data } = await client.patch('/admin/hizlihavale/config', payload);
    const merged = { ...HIZLIHAVALE_DEFAULTS, ...data };
    merged.api_secret = data.api_secret_configured ? MASKED_SECRET : '';
    setHizliHavale(merged);
    setHizliHavaleInitial(merged);
  };

  const loadTelegramRoutes = async () => {
    try {
      const { data } = await client.get('/admin/telegram/routes');
      setTelegramRoutes(data.routes || {});
      setTelegramRoutesInitial(data.routes || {});
    } catch (requestError) {
      // Yetkisi olmayan yönetici bu bölümü hiç görmez; sessiz geç.
    }
  };

  // Boş satır kayıtta hiç durmaz; karşılaştırma da onu yok sayar, yoksa bir
  // alana girip silmek "kaydedilmemiş değişiklik" gösterirdi.
  const cleanTelegramRoutes = (routes) => Object.fromEntries(
    Object.entries(routes || {})
      .map(([key, value]) => [key, {
        chat_id: String(value?.chat_id || '').trim(),
        thread_id: String(value?.thread_id || '').trim(),
      }])
      .filter(([, value]) => value.chat_id || value.thread_id),
  );

  const telegramRoutesDirtyKeys = () => {
    if (telegramRoutesInitial === null) return [];
    const now = cleanTelegramRoutes(telegramRoutes);
    const before = cleanTelegramRoutes(telegramRoutesInitial);
    return telegramEvents
      .map((event) => event.key)
      .filter((key) => (
        (now[key]?.chat_id || '') !== (before[key]?.chat_id || '')
        || (now[key]?.thread_id || '') !== (before[key]?.thread_id || '')
      ));
  };

  const saveTelegramRoutes = async () => {
    const { data } = await client.put('/admin/telegram/routes', {
      routes: cleanTelegramRoutes(telegramRoutes),
    });
    setTelegramRoutes(data.routes || {});
    setTelegramRoutesInitial(data.routes || {});
  };

  // Canlı destek sağlayıcısı (LiveChat / Text.com) `settings` kaydında değil
  // `provider_configs`'tedir (bkz. backend/services/livechat_provider.py);
  // sekmenin Kaydet düğmesi önce onu kendi ucuna yazar.
  const liveChatProviderDirty = () => liveChatProviderInitial !== null && (
    liveChatProvider.provider !== liveChatProviderInitial.provider
    || liveChatProvider.organization_id.trim().toLowerCase()
      !== liveChatProviderInitial.organization_id
  );

  const saveLiveChatProvider = async () => {
    const { data } = await client.patch('/admin/livechat', {
      provider: liveChatProvider.provider,
      organization_id: liveChatProvider.organization_id.trim(),
    });
    const next = {
      provider: data.provider,
      organization_id: data.organization_id || '',
    };
    setLiveChatProvider(next);
    setLiveChatProviderInitial(next);
  };

  const setTelegramRoute = (key, field, value) => setTelegramRoutes((prev) => ({
    ...prev,
    [key]: { ...(prev[key] || {}), [field]: value },
  }));

  const zelvoqChanges = () => zelvoqChangedFields(
    zelvoq, zelvoqInitial, { paymentBase, maskedSecret: MASKED_SECRET });

  const zelvoqDirtyKeys = () => zelvoqDirtyFields(
    zelvoq, zelvoqInitial, { paymentBase, maskedSecret: MASKED_SECRET });

  const saveZelvoq = async () => {
    const payload = zelvoqChanges();
    if (!Object.keys(payload).length) return;
    const { data } = await client.patch('/admin/zelvoq/config', payload);
    const merged = { ...ZELVOQ_DEFAULTS, ...data };
    merged.secret_key = data.secret_key_configured ? MASKED_SECRET : '';
    merged.secret_with = data.secret_with_configured ? MASKED_SECRET : '';
    setZelvoq(merged);
    setZelvoqInitial(merged);
  };

  // Yetkisi olmayan bir sekmede kalınmasın: yalnızca finans yetkisi olan
  // yönetici sayfayı doğrudan Finans sekmesiyle açar.
  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.some((item) => item.id === tab)) {
      setTab(visibleTabs[0].id);
    }
  }, [tab, visibleTabs]);

  const loadTelegramLogs = async (page = telegramLogPage) => {
    try {
      const { data } = await client.get('/admin/telegram/logs', {
        params: {
          limit: TELEGRAM_LOG_PAGE_SIZE,
          offset: page * TELEGRAM_LOG_PAGE_SIZE,
        },
      });
      const items = data.items || [];
      // Eski kayıtlar temizlenmişse son sayfa boşalabilir; boş ekranda
      // kalmak yerine başa dönülür.
      if (!items.length && page > 0) {
        setTelegramLogPage(0);
        return;
      }
      setTelegramLogs(items);
      setTelegramLogTotal(Number(data.total) || items.length);
    } catch { /* bildirim geçmişi kritik değil */ }
  };
  useEffect(() => {
    if (tab === 'telegram') loadTelegramLogs(telegramLogPage);
  }, [tab, telegramLogPage]);
  const telegramLogPages = Math.max(
    1, Math.ceil(telegramLogTotal / TELEGRAM_LOG_PAGE_SIZE));

  const loadBackupStatus = async () => {
    try {
      const { data } = await client.get('/admin/backup/status');
      setBackupStatus(data);
      return data;
    } catch {
      // Durum kartı kritik değil; ayar formu yine düzenlenebilir.
      return null;
    }
  };
  // Yedek arka planda alınır (döküm dakikalar sürebilir); sekme açıkken ve iş
  // sürerken durum yoklanır, bittiğinde yoklama kendiliğinden durur.
  const backupRunning = Boolean(backupStatus?.running);
  const jobWatchKey = jobWatch?.kind || '';
  useEffect(() => {
    if (tab !== 'backup') return undefined;
    loadBackupStatus();
    // İş yeni başlatıldıysa henüz `running` görünmeyebilir; izlenen bir iş
    // varken de yoklamaya devam edilir.
    if (!backupRunning && !jobWatchKey) return undefined;
    const timer = setInterval(loadBackupStatus, 5000);
    return () => clearInterval(timer);
  }, [tab, backupRunning, jobWatchKey]);

  const runBackup = async () => {
    setBackupBusy(true);
    try {
      await client.post('/admin/backup/run');
      toast({
        title: 'Yedekleme başlatıldı',
        description: backupStatus?.remote?.ready
          ? "Döküm arka planda alınıyor; bitince R2'ye de gönderilecek."
          : 'Döküm arka planda alınıyor; dosya sunucuda oluşacak.',
      });
      await loadBackupStatus();
    } catch (e) {
      toast({
        title: 'Yedekleme başlatılamadı',
        description: e.response?.data?.detail || 'İşlem başarısız.',
      });
    } finally {
      setBackupBusy(false);
    }
  };

  const downloadBackup = async (filename = '') => {
    const requestedName = filename
      || backupStatus?.current_file?.filename || '';
    if (!requestedName) return;
    setDownloadBusy(requestedName);
    try {
      const { data } = await client.post('/admin/backup/download-ticket', null, {
        params: { name: requestedName },
      });
      const link = document.createElement('a');
      link.href = `${API}/admin/backup/download-file/${encodeURIComponent(data.ticket)}`;
      link.download = data.filename || requestedName;
      link.rel = 'noreferrer';
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast({
        title: 'Yedek indirilemedi',
        description: e.response?.data?.detail
          || 'Sunucuda indirilebilir bir yedek dosyası bulunamadı.',
      });
    } finally {
      setDownloadBusy((current) => (
        current === requestedName ? '' : current));
    }
  };

  // Anlık yedek postalanmaz: bittiğinde dosya kendiliğinden indirilir.
  const runSnapshot = async () => {
    setBackupBusy(true);
    try {
      await client.post('/admin/backup/snapshot', null, {
        params: { format: snapshotFormat },
      });
      setJobWatch({
        kind: 'snapshot',
        afterId: backupStatus?.logs?.[0]?.id || '',
        startedAt: Date.now(),
      });
      toast({
        title: 'Anlık yedek alınıyor',
        description: 'Döküm hazır olduğunda dosya kendiliğinden inecek.',
      });
      await loadBackupStatus();
    } catch (e) {
      toast({
        title: 'Anlık yedek başlatılamadı',
        description: e.response?.data?.detail || 'İşlem başarısız.',
      });
    } finally {
      setBackupBusy(false);
    }
  };

  const startRestore = async () => {
    if (!restoreFile) return;
    setBackupBusy(true);
    try {
      const form = new FormData();
      form.append('file', restoreFile);
      await client.post('/admin/backup/restore', form);
      setJobWatch({
        kind: 'restore',
        afterId: backupStatus?.logs?.[0]?.id || '',
        startedAt: Date.now(),
      });
      setConfirmRestore(false);
      setRestoreFile(null);
      toast({
        title: 'Geri yükleme başladı',
        description: 'Önce güvenlik kopyası alınır, sonra dosya uygulanır.',
      });
      await loadBackupStatus();
    } catch (e) {
      toast({
        title: 'Geri yükleme başlatılamadı',
        description: e.response?.data?.detail || 'İşlem başarısız.',
      });
    } finally {
      setBackupBusy(false);
    }
  };

  // İzlenen iş bittiğinde sonucu bildir: anlık yedek indirilir, geri yükleme
  // sonrası ayarlar yeniden okunur (veritabanı değişmiştir).
  useEffect(() => {
    if (!jobWatch || !backupStatus || backupStatus.running) return;
    // İş hiç kayıt açmadan düşmüşse (servis yeniden başladı) sonsuza kadar
    // yoklanmasın.
    if (Date.now() - jobWatch.startedAt > BACKUP_WATCH_TIMEOUT_MS) {
      setJobWatch(null);
      toast({
        title: 'İşlem sonucu alınamadı',
        description: 'Durumu Yedekleme Geçmişi listesinden kontrol edin.',
      });
      return;
    }
    const log = (backupStatus.logs || [])[0];
    // Arka plan işi henüz kaydını açmamış olabilir; eski satıra bakılmaz.
    if (!log || log.id === jobWatch.afterId || log.trigger !== jobWatch.kind) return;
    setJobWatch(null);
    if (log.status === 'failed') {
      toast({
        title: jobWatch.kind === 'restore'
          ? 'Geri yükleme başarısız' : 'Anlık yedek alınamadı',
        description: log.error || 'İşlem tamamlanamadı.',
      });
      return;
    }
    if (jobWatch.kind === 'snapshot' && log.filename) {
      downloadBackup(log.filename);
      return;
    }
    toast({
      title: 'Geri yükleme tamamlandı',
      description: `Veritabanı ve ${log.uploads_files || 0} dosya aktarıldı. `
        + 'Oturumunuz düşerse yedekteki yönetici şifresiyle yeniden giriş yapın.',
    });
    load();
  }, [jobWatch, backupStatus]);

  // Renk seçicide gezinirken panel de anında yeni renge geçsin.
  const colorsLoaded = !!s;
  const previewPrimary = s?.primary_color || '';
  const previewSecondary = s?.secondary_color || '';
  useEffect(() => {
    if (colorsLoaded) applyAdminTheme(previewPrimary, previewSecondary);
  }, [colorsLoaded, previewPrimary, previewSecondary]);

  // Kaydetmeden sayfadan çıkılırsa önizleme rengi panele yapışmasın: son
  // kaydedilmiş değerlere dönülür.
  const savedColors = useRef(null);
  savedColors.current = initial;
  useEffect(() => () => {
    applyAdminTheme(savedColors.current?.primary_color, savedColors.current?.secondary_color);
  }, []);

  // Yalnızca kapsamdaki (açık sekmeye ait) ve gerçekten değişmiş alanlardan
  // istek gövdesi kurar. Dokunulmayan alan hiç gönderilmez; böylece başka bir
  // yöneticinin aynı anda yaptığı değişiklik geri alınmaz.
  const buildPayload = (nextSettings, scopeKeys) => {
    if (!initial) return {};
    // Hiçbir sekmeye yazılmamış bir alan değişmişse kaybolmasın diye açık
    // sekmenin kaydına eklenir. Buraya düşen alan TAB_FIELDS'te eksik demektir.
    const orphanKeys = Object.keys(nextSettings).filter((key) => (
      !KNOWN_TAB_FIELDS.has(key) && !sameValue(nextSettings[key], initial[key])
    ));
    const payload = {};
    [...scopeKeys, ...orphanKeys].forEach((key) => {
      if (sameValue(nextSettings[key], initial[key])) return;
      payload[key] = nextSettings[key];
    });
    SECRET_FIELDS.forEach((field) => {
      if (!payload[field] || payload[field] === MASKED_SECRET) {
        delete payload[field];
      }
    });
    // Callback adresleri salt okunurdur ve panelde her zaman sitenin güncel
    // kökünden çizilir; kayıttaki değer geride kalmışsa (alan adı değişmiş)
    // bu sekme kaydedilirken sessizce düzeltilir.
    CALLBACK_FIELDS.forEach(({ key, path }) => {
      if (!scopeKeys.includes(key)) return;
      if (!paymentBase) return;
      const url = `${paymentBase}${path}`;
      if (!sameValue(initial[key], url)) payload[key] = url;
    });
    // Tanımlı bir website_id'yi değiştirmek backend'de açık onay ister; onayı
    // yönetici kilidi açarken zaten vermiştir (bkz. unlockWebsiteId).
    if (
      payload.hemensystem_website_id !== undefined
      && Number(initial.hemensystem_website_id || 0) > 0
    ) {
      payload.hemensystem_website_id_change_confirmed = true;
    }
    return payload;
  };

  const persistSettings = async (payload) => {
    setBusy(true);
    try {
      const callbackTokenWillBeGenerated = (
        !s.goldpalace_callback_token_configured
        && GOLDPALACE_TRIGGER_FIELDS.some((field) => field in payload)
        && !payload.goldpalace_callback_token
      );
      const { data } = await client.patch('/admin/settings', payload);
      const hydrated = hydrateSecretFields(data);
      setInitial(hydrated);
      // Başka sekmelerde kaydedilmemiş değişiklikler duruyor olabilir; sunucu
      // yanıtı yalnızca bu istekte gönderilen ve dokunulmamış alanları tazeler.
      setS((current) => {
        const next = { ...hydrated };
        Object.keys(current).forEach((key) => {
          if (payload[key] === undefined && !sameValue(current[key], initial[key])) {
            next[key] = current[key];
          }
        });
        return next;
      });
      notifySettingsUpdated();
      toast({
        title: 'Ayarlar kaydedildi',
        description: (
          callbackTokenWillBeGenerated
          && data.goldpalace_callback_token_configured
        )
          ? 'Gold Palace Callback Token otomatik oluşturuldu.'
          : undefined,
      });
    } catch (e) { toast({ title: 'Hata', description: e.response?.data?.detail || 'Kaydedilemedi' }); }
    finally { setBusy(false); }
  };

  // HızlıcaParalar bağlantı testi para hareketi doğurmaz: sağlayıcının bakiye
  // ucu yoktur, banka listesi okunur. Kenar katmanının (Cloudflare) reddi
  // ayrı bir kodla döner — "anahtar yanlış" diye raporlanırsa yönetici
  // olmayan bir sorunu aramaya başlar.
  const testHizlica = async () => {
    setBusy(true);
    setHizlicaStatus(null);
    try {
      const { data } = await client.post('/admin/hizlicaparalar/test');
      setHizlicaStatus({ ok: true, ...data });
      toast({
        title: 'HızlıcaParalar bağlantısı doğrulandı',
        description: data.message,
      });
    } catch (e) {
      setHizlicaStatus({
        ok: false,
        error: e.response?.data?.detail || 'Bağlantı kurulamadı.',
      });
      toast({
        title: 'HızlıcaParalar testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kurulamadı.',
      });
    } finally { setBusy(false); }
  };

  // Sağlayıcının bakiye ucu yoktur; anahtar yatırım listesi tek satır
  // okunarak, **işlem üretmeden** sınanır. Kenar katmanının reddi ayrı bir
  // kodla döner — "anahtar yanlış" diye raporlanırsa yönetici olmayan bir
  // sorunu aramaya başlar.
  const testHizliHavale = async () => {
    setBusy(true);
    setHizliHavaleStatus(null);
    try {
      const { data } = await client.post('/admin/hizlihavale/test');
      setHizliHavaleStatus({ ok: true, ...data });
      toast({
        title: 'Hızlı Havale bağlantısı doğrulandı',
        description: data.message,
      });
    } catch (e) {
      setHizliHavaleStatus({
        ok: false,
        error: e.response?.data?.detail || 'Bağlantı kurulamadı.',
      });
      toast({
        title: 'Hızlı Havale testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kurulamadı.',
      });
    } finally { setBusy(false); }
  };

  // Çekimde `bank_name` sağlayıcının **kendi kodudur**; liste kayda yazılır
  // ve çekim anında ağa çıkılmaz.
  const syncHizlicaBanks = async () => {
    setBusy(true);
    try {
      const { data } = await client.post('/admin/hizlicaparalar/banks/sync');
      await loadHizlica();
      toast({
        title: 'Banka listesi güncellendi',
        description: `${data.count} banka kaydı alındı.`,
      });
    } catch (e) {
      toast({
        title: 'Banka listesi alınamadı',
        description: e.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally { setBusy(false); }
  };

  // Test ucu yalnızca sunucuda kayıtlı yapılandırmayı okur. Bu yüzden formdaki
  // bekleyen Zelvoq değişiklikleri önce kaydedilir; aksi hâlde ekranda sid
  // doluyken eski boş kayıt sağlayıcıya gider ve "sid gerekli" cevabı alınır.
  // Sağlayıcının bakiye ucu yoktur; banka listesi işlem üretmeden okunur. O uç
  // hash doğrulamadığı için test secret'ı değil yalnızca sid'i kanıtlar.
  const testZelvoq = async () => {
    setBusy(true);
    setZelvoqStatus(null);
    try {
      const { data } = await saveZelvoqBeforeTest(
        saveZelvoq,
        () => client.post('/admin/zelvoq/test'),
      );
      setZelvoqStatus({ ok: true, ...data });
      toast({
        title: 'Zelvoq bağlantısı doğrulandı',
        description: data.message,
      });
    } catch (e) {
      setZelvoqStatus({
        ok: false,
        error: e.response?.data?.detail || 'Bağlantı kurulamadı.',
      });
      toast({
        title: 'Zelvoq testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kurulamadı.',
      });
    } finally { setBusy(false); }
  };

  // Çekimde `bank_id` sağlayıcının **sayısal kimliğidir**; liste kayda yazılır
  // ve çekim anında ağa çıkılmaz.
  const syncZelvoqBanks = async () => {
    setBusy(true);
    try {
      const { data } = await client.post('/admin/zelvoq/banks/sync');
      await loadZelvoq();
      toast({
        title: 'Banka listesi güncellendi',
        description: `${(data.banks || []).length} banka kaydı alındı.`,
      });
    } catch (e) {
      toast({
        title: 'Banka listesi alınamadı',
        description: e.response?.data?.detail || 'Lütfen tekrar deneyin.',
      });
    } finally { setBusy(false); }
  };

  // `category` verilirse test o türün grubuna gider: ayrı gruba alınan türde
  // botun o grupta yazabildiği ancak böyle görülür.
  const sendTelegramTest = async (category = '') => {
    setBusy(true);
    try {
      await client.post('/admin/telegram/test', null, {
        params: category ? { category } : {},
      });
      toast({ title: 'Test bildirimi gönderildi', description: 'Telegram grubunu kontrol edin.' });
    } catch (e) {
      toast({ title: 'Gönderilemedi', description: e.response?.data?.detail || 'Telegram bildirimi başarısız.' });
    } finally {
      setBusy(false);
      // Test kaydı en yeni satırdır; geçmiş ilk sayfaya döner.
      setTelegramLogPage(0);
      loadTelegramLogs(0);
    }
  };

  const testHeraclesOtp = async () => {
    setHeraclesOtpBusy(true);
    setHeraclesOtpStatus(null);
    try {
      await client.post('/admin/heracles-otp/test');
      setHeraclesOtpStatus({ ok: true });
      toast({
        title: 'HeraclesOtp bağlantısı başarılı',
        description: 'Entegrasyon anahtarı sağlayıcı tarafından doğrulandı',
      });
    } catch (e) {
      const message = e.response?.data?.detail || 'HeraclesOtp bağlantısı kurulamadı';
      setHeraclesOtpStatus({ ok: false, message });
      toast({ title: 'Bağlantı başarısız', description: message });
    } finally {
      setHeraclesOtpBusy(false);
    }
  };

  // Webhook secret'ı dışarıdan alınmaz, biz üretiriz: Telegram'a setWebhook
  // ile bildirilir ve her istekte başlıkta geri gelir.
  const generateWebhookSecret = () => {
    const value = (window.crypto?.randomUUID?.() || '').replace(/-/g, '')
      || Math.random().toString(36).slice(2).repeat(2);
    setS({ ...s, telegram_webhook_secret: value });
    // Üretilen değer görünür açılır: kaydedildikten sonra sunucu onu geri
    // vermez, yönetici bu anda görmezse bir daha göremez.
    setShowWebhookSecret(true);
    toast({
      title: 'Secret üretildi',
      description: 'Kaydedip ardından webhook’u Telegram’a bildirin.',
    });
  };

  const copyWebhookSecret = async () => {
    const value = s.telegram_webhook_secret || '';
    if (!value) {
      toast({ title: 'Önce secret üretin' });
      return;
    }
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: 'Secret panoya kopyalandı' });
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Değeri elle seçip kopyalayın' });
    }
  };

  // Telegram secret'ı geri vermez; webhook'un gerçekten kurulu olduğu adres,
  // bekleyen güncelleme ve son hata üzerinden doğrulanır.
  const loadWebhookInfo = async () => {
    try {
      const { data } = await client.get('/admin/telegram/webhook');
      setTelegramWebhookInfo(data);
    } catch (e) {
      toast({
        title: 'Webhook durumu alınamadı',
        description: e.response?.data?.detail || 'Bilgi okunamadı',
      });
    }
  };

  const loadTelegramChats = async () => {
    setTelegramChatsBusy(true);
    try {
      const { data } = await client.get('/admin/telegram/chats');
      setTelegramChats(data);
    } catch (e) {
      toast({
        title: 'Gruplar alınamadı',
        description: e.response?.data?.detail || 'Grup listesi okunamadı',
      });
    } finally {
      setTelegramChatsBusy(false);
    }
  };

  const scanTelegramChats = async () => {
    setTelegramChatsBusy(true);
    try {
      const { data } = await client.post('/admin/telegram/chats/scan');
      setTelegramChats((prev) => ({ ...(prev || {}), ...data }));
      toast({
        title: data.found
          ? `${data.found} sohbet tarandı`
          : 'Yeni sohbet bulunamadı',
        description: data.found
          ? undefined
          : 'Botu gruba ekleyip gruba bir mesaj yazın, sonra tekrar tarayın.',
      });
    } catch (e) {
      toast({
        title: 'Tarama yapılamadı',
        description: e.response?.data?.detail || 'Sohbetler taranamadı',
      });
    } finally {
      setTelegramChatsBusy(false);
    }
  };

  const checkTelegramChat = async (chatId) => {
    try {
      const { data } = await client.post(
        `/admin/telegram/chats/${encodeURIComponent(chatId)}/check`);
      toast({
        title: `Botun rolü: ${data.bot_status}`,
        description: data.bot_status === 'administrator'
          ? 'Üyelik sorgusu için uygun.'
          : 'Üyelik sorgusunun güvenilir çalışması için botu grupta yönetici yapın.',
      });
      loadTelegramChats();
    } catch (e) {
      toast({
        title: 'Durum okunamadı',
        description: e.response?.data?.detail || 'Botun grup durumu alınamadı',
      });
    }
  };

  // Webhook adresi sunucu tarafında isteğin kökünden türetilir; yöneticinin
  // adres yazmasına gerek yok, yanlış adres en sık hata kaynağıydı.
  const registerTelegramWebhook = async () => {
    setTelegramWebhookBusy(true);
    try {
      const { data } = await client.post('/admin/telegram/webhook');
      toast({
        title: 'Webhook kaydedildi',
        description: data.url,
      });
      loadWebhookInfo();
    } catch (e) {
      toast({
        title: 'Webhook kaydedilemedi',
        description: e.response?.data?.detail
          || 'Telegram webhook adresi tanımlanamadı.',
      });
    } finally {
      setTelegramWebhookBusy(false);
    }
  };

  const testWizkasa = async () => {
    setBusy(true);
    setWizkasaStatus(null);
    try {
      const { data } = await client.post('/admin/wizkasa/test');
      setWizkasaStatus(data);
      // İmza ve IP yetkisi ayrı kontrol edilir: imza doğru olsa bile sunucu
      // IP'si beyaz listede değilse yatırım/çekim açılmaz.
      // `not_found`, test için sorgulanan sahte siparişin bulunamamasıdır —
      // hata değil, imzanın kabul edildiğinin kanıtıdır.
      const detail = data.connected
        ? (data.ip_allowed === false
          ? 'Ancak sunucu IP adresi Wizkasa beyaz listesinde değil; yatırım açılmaz.'
          : data.ip_note
            ? `Sağlayıcı notu: ${data.ip_note} (hesap/bayi ataması Wizkasa tarafında yapılmalı).`
            : 'İmza ve IP yetkisi tamam; entegrasyon kullanıma hazır.')
        : (data.error || data.code || 'İstek sağlayıcıya ulaşmadı.');
      toast({
        title: data.connected
          ? 'Wizkasa bağlantısı doğrulandı'
          : 'Wizkasa bağlantısı başarısız',
        description: detail,
      });
    } catch (e) {
      toast({
        title: 'Wizkasa testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kontrol edilemedi',
      });
    } finally {
      setBusy(false);
    }
  };

  const websiteIdLocked = (
    Number(initial?.hemensystem_website_id || 0) > 0 && !websiteIdUnlocked);

  const unlockWebsiteId = () => {
    // Uyarı bilinçli olarak tek tıkla geçilemiyor: numaranın değişmesi
    // sağlayıcı tarafındaki mutabakatı sessizce bozar, geri dönüşü elle
    // düzeltmektir.
    if (!window.confirm(
      'website_id sabitlenmiş bir numaradır. Değiştirirseniz sağlayıcı '
      + 'tarafındaki geçmiş işlemler bu siteyle eşleşmez ve mutabakat bozulur. '
      + 'Yalnızca gerçekten yeni bir kurulum tanımlıyorsanız devam edin.\n\n'
      + 'Numarayı değiştirmek istiyor musunuz?',
    )) return;
    setWebsiteIdUnlocked(true);
  };

  // Hemen Havale'in durum sorgulama ucu yoktur; test, yatırım ucuna tutarı 0
  // olan bir istek atar. Doğrulama hatası (422) kimlik katmanının geçildiği
  // anlamına gelir ve gerçek bir yatırım oluşmaz.
  const testHemenSystem = async () => {
    setBusy(true);
    setHemenStatus(null);
    try {
      const { data } = await client.post('/admin/hemensystem/test');
      setHemenStatus(data);
      const detail = data.connected
        ? (data.ip_allowed === false
          ? 'Ancak sunucu IP adresi izin listesinde değil; işlem açılmaz.'
          : 'Kimlik bilgileri kabul edildi; entegrasyon kullanıma hazır.')
        : (data.error || data.message || 'İstek sağlayıcıya ulaşmadı.');
      toast({
        title: data.connected
          ? 'Hemen Havale bağlantısı doğrulandı'
          : 'Hemen Havale bağlantısı başarısız',
        description: detail,
      });
    } catch (e) {
      toast({
        title: 'Hemen Havale testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kontrol edilemedi',
      });
    } finally {
      setBusy(false);
    }
  };

  // Hera'nın banka listesi ucu kimliği doğrular ama hiçbir kayıt açmaz;
  // Hemen Havale'deki "tutarı 0 gönder" hilesine gerek yoktur.
  const testHera = async () => {
    setBusy(true);
    setHeraStatus(null);
    try {
      const { data } = await client.post('/admin/hera/test');
      setHeraStatus(data);
      toast({
        title: data.connected
          ? 'Hera bağlantısı doğrulandı'
          : 'Hera bağlantısı başarısız',
        description: data.connected
          ? `Kimlik bilgileri kabul edildi (${data.banks ?? 0} aktif banka).`
          : (data.error || data.message || 'İstek sağlayıcıya ulaşmadı.'),
      });
    } catch (e) {
      toast({
        title: 'Hera testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kontrol edilemedi',
      });
    } finally {
      setBusy(false);
    }
  };

  // Payurus'un durum sorgulama ucu (`check-transaction`) para hareketi
  // doğurmaz: var olmayan bir işlem numarası sorulur. Secret geçersizse
  // sağlayıcı doğrulama hatası döndürür, geçerliyse "böyle bir işlem yok" der.
  const testPayurus = async () => {
    setBusy(true);
    setPayurusStatus(null);
    try {
      const { data } = await client.post('/admin/payurus/test');
      setPayurusStatus(data);
      toast({
        title: data.connected
          ? 'Payurus bağlantısı doğrulandı'
          : 'Payurus bağlantısı başarısız',
        description: data.connected
          ? 'Secret kabul edildi; entegrasyon kullanıma hazır.'
          : (data.error || data.message || 'İstek sağlayıcıya ulaşmadı.'),
      });
    } catch (e) {
      toast({
        title: 'Payurus testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kontrol edilemedi',
      });
    } finally {
      setBusy(false);
    }
  };

  // UltraPay bağlantı testi para hareketi doğurmaz: yalnızca firma bakiyesi
  // sorulur (`/company/balance`). Başarısızlık çoğu zaman "anahtar yanlış"
  // değil **"sunucunun çıkış IP'si sağlayıcının beyaz listesinde yok"**
  // demektir; mesaj bu yüzden olduğu gibi gösterilir.
  const testUltraPay = async () => {
    setBusy(true);
    setUltrapayStatus(null);
    try {
      const { data } = await client.post('/admin/ultrapay/test');
      setUltrapayStatus(data);
      toast({
        title: data.connected
          ? 'UltraPay bağlantısı doğrulandı'
          : 'UltraPay bağlantısı başarısız',
        description: data.connected
          ? 'API anahtarı kabul edildi; sunucunun IP adresi de listede.'
          : (data.error || data.message || 'İstek sağlayıcıya ulaşmadı.'),
      });
    } catch (e) {
      toast({
        title: 'UltraPay testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kontrol edilemedi',
      });
    } finally {
      setBusy(false);
    }
  };

  // Link Havale bağlantı testi para hareketi doğurmaz: yalnızca bayi
  // bakiyesi sorulur (`GET /balance`). Sağlayıcı IP beyaz listesi
  // uygulayabildiği için başarısızlık her zaman "anahtar yanlış" demek
  // değildir; mesaj olduğu gibi gösterilir.
  const testLinkHavale = async () => {
    setBusy(true);
    setLinkhavaleStatus(null);
    try {
      const { data } = await client.post('/admin/linkhavale/test');
      setLinkhavaleStatus(data);
      toast({
        title: data.connected
          ? 'Link Havale bağlantısı doğrulandı'
          : 'Link Havale bağlantısı başarısız',
        description: data.connected
          ? 'API anahtarı ve imza kabul edildi.'
          : (data.error || data.message || 'İstek sağlayıcıya ulaşmadı.'),
      });
    } catch (e) {
      toast({
        title: 'Link Havale testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kontrol edilemedi',
      });
    } finally {
      setBusy(false);
    }
  };

  // VEVO PAY bağlantı testi para hareketi doğurmaz ve sağlayıcının bakiye ucu
  // yoktur: var olmayan bir referansın durumu sorulur. "Kayıt bulunamadı"
  // cevabı anahtarın kabul edildiği anlamına gelir (Payurus testindeki
  // gerekçenin aynısı).
  const testVevoPay = async () => {
    setBusy(true);
    setVevopayStatus(null);
    try {
      const { data } = await client.post('/admin/vevopay/test');
      setVevopayStatus(data);
      toast({
        title: data.connected
          ? 'VEVO PAY bağlantısı doğrulandı'
          : 'VEVO PAY bağlantısı başarısız',
        description: data.connected
          ? 'Kanal anahtarı kabul edildi (sorgulanan test işlemi sağlayıcıda bulunamadı — beklenen).'
          : (data.error || data.message || 'İstek sağlayıcıya ulaşmadı.'),
      });
    } catch (e) {
      toast({
        title: 'VEVO PAY testi başarısız',
        description: e.response?.data?.detail || 'Bağlantı kontrol edilemedi',
      });
    } finally {
      setBusy(false);
    }
  };

  // Panelde işaretli Link Havale yöntemleri; ayar virgülle ayrılmış slug
  // listesidir ve sıra katalogla sabitlenir (backend de aynı sırayı yazar).
  const linkhavaleSlugs = (key) => String(s?.[key] || '')
    .split(',')
    .map((item) => String(item).trim().toLowerCase())
    .filter(Boolean);
  const linkhavaleDepositSlugs = linkhavaleSlugs('linkhavale_deposit_methods');
  const linkhavaleWithdrawSlugs = linkhavaleSlugs(
    'linkhavale_withdraw_methods');
  const toggleLinkHavaleMethod = (key, slug) => {
    const current = linkhavaleSlugs(key);
    const next = current.includes(slug)
      ? current.filter((item) => item !== slug)
      : [...current, slug];
    const ordered = LINKHAVALE_METHODS
      .map((item) => item.slug)
      .filter((item) => next.includes(item));
    setS({ ...s, [key]: ordered.join(',') });
  };
  const linkhavalePairs = String(s?.linkhavale_crypto_pairs || '')
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
  const toggleLinkHavalePair = (pair) => {
    const next = linkhavalePairs.includes(pair)
      ? linkhavalePairs.filter((item) => item !== pair)
      : [...linkhavalePairs, pair];
    const ordered = LINKHAVALE_CRYPTO_PAIRS.filter(
      (item) => next.includes(item));
    setS({ ...s, linkhavale_crypto_pairs: ordered.join(',') });
  };

  // Panelde işaretli UltraPay yöntemleri; ayar virgülle ayrılmış kod listesi.
  const ultrapayCodes = (key) => String(s?.[key] || '')
    .split(',')
    .map((item) => Number(String(item).trim()))
    .filter((item) => Number.isFinite(item) && item > 0);
  // MethodLimitPicker seçeneği `id` ile tanır; katalogların kendi anahtarı
  // (UltraPay'de `code`, Link Havale'de `slug`) o alana çevrilir. Kod backend
  // tarafındaki anahtarla aynı olmalıdır (services/payment_limits.py).
  const ultrapayOptions = ULTRAPAY_METHODS.map((item) => ({
    ...item,
    id: item.code,
    note: `#${item.code}${item.note ? ` · ${item.note}` : ''}`,
  }));
  const ultrapayDepositCodes = ultrapayCodes('ultrapay_deposit_methods');
  const ultrapayWithdrawCodes = ultrapayCodes('ultrapay_withdraw_methods');
  const toggleUltraPayMethod = (key, code) => {
    const current = ultrapayCodes(key);
    const next = current.includes(code)
      ? current.filter((item) => item !== code)
      : [...current, code];
    // Sıra katalogla sabitlenir: backend de aynı sırayı yazar, iki taraf
    // ayrışmasın diye panelde işaretleme sırası kayda geçmez.
    const ordered = ULTRAPAY_METHODS
      .map((item) => item.code)
      .filter((item) => next.includes(item));
    setS({ ...s, [key]: ordered.join(',') });
  };
  const ultrapayPairs = String(s?.ultrapay_crypto_pairs || '')
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);
  const toggleUltraPayPair = (pair) => {
    const next = ultrapayPairs.includes(pair)
      ? ultrapayPairs.filter((item) => item !== pair)
      : [...ultrapayPairs, pair];
    const ordered = ULTRAPAY_CRYPTO_PAIRS.filter((item) => next.includes(item));
    setS({ ...s, ultrapay_crypto_pairs: ordered.join(',') });
  };

  const linkhavaleOptions = LINKHAVALE_METHODS.map((item) => ({
    ...item,
    id: item.slug,
    note: item.note || item.code,
  }));

  // Panelde işaretli yatırım akışları; ayar virgülle ayrılmış metindir.
  const heraDepositMethods = String(s?.hera_deposit_methods || 'BANK')
    .split(',')
    .map((item) => item.trim().toUpperCase())
    .filter(Boolean);

  const toggleHeraMethod = (id) => {
    const next = heraDepositMethods.includes(id)
      ? heraDepositMethods.filter((item) => item !== id)
      : [...heraDepositMethods, id];
    // Sıra sabittir: backend de aynı sırayı yazar, iki taraf ayrışmasın.
    const ordered = HERA_DEPOSIT_METHODS
      .map((item) => item.id)
      .filter((item) => next.includes(item));
    setS({ ...s, hera_deposit_methods: ordered.join(',') });
  };

  // Doğrulama ve onay kararları hazır gövde üzerinden verilir: LiveChat lisansı
  // yalnızca LiveChat alanları gönderiliyorsa, parola onayı yalnızca parola
  // gerçekten gövdeye girdiyse devreye girer.
  const submit = async (payload) => {
    if (!Object.keys(payload).length) {
      toast({
        title: 'Değişiklik yok',
        description: 'Bu sekmede kaydedilecek bir değişiklik bulunmuyor.',
      });
      return;
    }
    const invalidColor = ['primary_color', 'secondary_color'].find((key) => (
      key in payload && !/^#[0-9a-f]{6}$/i.test(String(payload[key] || ''))
    ));
    if (invalidColor) {
      toast({
        title: 'Tema rengi geçersiz',
        description: 'Rengi #25AB6C biçiminde, 6 haneli HEX kodu olarak girin.',
      });
      return;
    }
    if (
      ('livechat_enabled' in payload || 'livechat_license_id' in payload)
      && s.livechat_enabled
      && liveChatProvider.provider !== 'text'
      && !/^\d{8,20}$/.test(s.livechat_license_id || '')
    ) {
      toast({
        title: 'LiveChat lisans numarası geçersiz',
        description: 'LiveChat panelindeki sayısal lisans numarasını girin.',
      });
      return;
    }
    if (payload.smtp_password) {
      setPendingPayload(payload);
      setConfirmSecret(true);
      return;
    }
    await persistSettings(payload);
  };

  const save = async () => {
    // Özel kod ayarları `settings` kaydında değildir: sekme yalnızca kendi
    // ucuna yazar ve ayar kaydına boş bir istek atılmaz — `submit` boş gövdede
    // "Değişiklik yok" der ve yönetici kaydın yapılmadığını sanırdı.
    if (tab === 'customcode') {
      try {
        await saveCustomCode();
        toast({ title: 'Özel kod kaydedildi' });
      } catch (e) {
        toast({
          title: 'Özel kod kaydedilemedi',
          description: e.response?.data?.detail || 'Lütfen tekrar deneyin.',
        });
      }
      return;
    }
    const payload = buildPayload(s, TAB_FIELDS[tab] || []);
    if (tab === 'livechat') {
      const providerPending = liveChatProviderDirty();
      const text = liveChatProvider.provider === 'text';
      if (s.livechat_enabled && text && !TEXT_ORGANIZATION_RE.test(
        liveChatProvider.organization_id.trim().toLowerCase(),
      )) {
        toast({
          title: 'Text.com organizasyon kimliği geçersiz',
          description: 'Text uygulamasındaki kurulum kodunda yazan Organization ID değerini girin.',
        });
        return;
      }
      // Sağlayıcı ayar kaydından **önce** yazılır: ayar kaydı widget açılırken
      // seçili sağlayıcının kimliğini doğrular ve Text.com'a geçerken
      // lisans numarası boştur.
      if (providerPending) {
        try {
          await saveLiveChatProvider();
        } catch (e) {
          toast({
            title: 'Canlı destek sağlayıcısı kaydedilemedi',
            description: e.response?.data?.detail?.[0]?.msg
              || e.response?.data?.detail || 'Lütfen tekrar deneyin.',
          });
          return;
        }
        if (!Object.keys(payload).length) {
          toast({ title: 'Canlı destek sağlayıcısı kaydedildi' });
          notifySettingsUpdated();
          return;
        }
      }
      await submit(payload);
      return;
    }
    // HızlıcaParalar ayarları `settings` kaydında olmadığı için ayrı bir uca
    // gider; tek düğme deneyimi bilinçlidir.
    const hizlicaPending = tab === 'finance'
      && Object.keys(hizlicaChanges()).length > 0;
    const hizliHavalePending = tab === 'finance'
      && Object.keys(hizliHavaleChanges()).length > 0;
    const zelvoqPending = tab === 'finance'
      && Object.keys(zelvoqChanges()).length > 0;
    const telegramRoutesPending = tab === 'telegram'
      && telegramRoutesDirtyKeys().length > 0;
    // Yalnızca HızlıcaParalar değiştiyse ayar kaydına boş bir istek atılmaz:
    // `submit` boş gövdede "Değişiklik yok" der ve yönetici kaydın
    // yapılmadığını sanırdı.
    if (
      Object.keys(payload).length
      || !(hizlicaPending || hizliHavalePending || zelvoqPending
        || telegramRoutesPending)
    ) {
      await submit(payload);
    }
    if (telegramRoutesPending) {
      try {
        await saveTelegramRoutes();
        if (!Object.keys(payload).length) {
          toast({ title: 'Bildirim grupları kaydedildi' });
        }
      } catch (e) {
        toast({
          title: 'Bildirim grupları kaydedilemedi',
          description: e.response?.data?.detail || 'Lütfen tekrar deneyin.',
        });
      }
    }
    if (hizlicaPending) {
      try {
        await saveHizlica();
        if (!Object.keys(payload).length) {
          toast({ title: 'HızlıcaParalar ayarları kaydedildi' });
        }
      } catch (e) {
        toast({
          title: 'HızlıcaParalar kaydedilemedi',
          description: e.response?.data?.detail || 'Lütfen tekrar deneyin.',
        });
      }
    }
    if (hizliHavalePending) {
      try {
        await saveHizliHavale();
        if (!Object.keys(payload).length) {
          toast({ title: 'Hızlı Havale ayarları kaydedildi' });
        }
      } catch (e) {
        toast({
          title: 'Hızlı Havale kaydedilemedi',
          description: e.response?.data?.detail || 'Lütfen tekrar deneyin.',
        });
      }
    }
    if (zelvoqPending) {
      try {
        await saveZelvoq();
        if (!Object.keys(payload).length) {
          toast({ title: 'Zelvoq ayarları kaydedildi' });
        }
      } catch (e) {
        toast({
          title: 'Zelvoq kaydedilemedi',
          description: e.response?.data?.detail || 'Lütfen tekrar deneyin.',
        });
      }
    }
  };

  const saveBranding = async () => {
    const siteName = brandingSiteName(s);
    const nextSettings = siteName ? { ...s, site_name: siteName } : s;
    await submit(buildPayload(nextSettings, [...TAB_FIELDS.branding, 'site_name']));
  };

  const saveSecret = async () => {
    if (pendingPayload) await persistSettings(pendingPayload);
    setPendingPayload(null);
    setConfirmSecret(false);
  };

  const uploadSettingImage = async (
    event,
    { settingKey, label, setLoading },
  ) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({ title: `${label} yüklenemedi`, description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.` });
      return;
    }

    setLoading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const { data: uploaded } = await client.post('/admin/uploads/images', form, {
        onUploadProgress: (progress) => {
          if (!progress.total) return;
          const percent = Math.min(100, Math.round((progress.loaded / progress.total) * 100));
          setUploadProgress((current) => ({ ...current, [settingKey]: percent }));
        },
      });
      const { data: updated } = await client.patch('/admin/settings', { [settingKey]: uploaded.url });
      setS((current) => ({ ...current, [settingKey]: updated[settingKey] }));
      setInitial((current) => ({ ...current, [settingKey]: updated[settingKey] }));
      notifySettingsUpdated();
      toast({ title: `${label} yüklendi` });
    } catch (e) {
      toast({
        title: `${label} yüklenemedi`,
        description: e.response?.data?.detail || 'Dosya yükleme başarısız.',
      });
    } finally {
      setLoading(false);
      setUploadProgress((current) => {
        const next = { ...current };
        delete next[settingKey];
        return next;
      });
    }
  };

  // Yükleme sürerken düğme yazısı yüzdeyi söyler; %100'den sonra sunucu
  // dosyayı doğrular, o an "İşleniyor" yazılır.
  const uploadLabel = (settingKey, idleLabel) => {
    const percent = uploadProgress[settingKey];
    if (percent == null) return idleLabel;
    return percent >= 100 ? 'İşleniyor…' : `Yükleniyor %${percent}`;
  };

  const removeSettingImage = async ({
    settingKey, label, prompt, description, setLoading,
  }) => {
    if (!window.confirm(prompt)) return;
    setLoading(true);
    try {
      await client.patch('/admin/settings', { [settingKey]: '' });
      setS((current) => ({ ...current, [settingKey]: '' }));
      setInitial((current) => ({ ...current, [settingKey]: '' }));
      notifySettingsUpdated();
      toast({ title: `${label} kaldırıldı`, description });
    } catch (e) {
      toast({
        title: `${label} kaldırılamadı`,
        description: e.response?.data?.detail || 'İşlem başarısız.',
      });
    } finally {
      setLoading(false);
    }
  };

  const uploadLogo = (event) => uploadSettingImage(event, {
    settingKey: 'logo_image_url', label: 'Logo', setLoading: setLogoBusy,
  });

  const removeLogo = () => removeSettingImage({
    settingKey: 'logo_image_url',
    label: 'Logo',
    prompt: 'Yüklenen logo kaldırılsın mı? Metin logosu yeniden gösterilecek.',
    description: 'Metin logosu yeniden kullanılacak.',
    setLoading: setLogoBusy,
  });

  const uploadFavicon = (event) => uploadSettingImage(event, {
    settingKey: 'favicon_url', label: 'Favicon', setLoading: setFaviconBusy,
  });

  const removeFavicon = () => removeSettingImage({
    settingKey: 'favicon_url',
    label: 'Favicon',
    prompt: 'Yüklenen favicon kaldırılsın mı? Varsayılan simge kullanılacak.',
    description: 'Varsayılan favicon yeniden kullanılacak.',
    setLoading: setFaviconBusy,
  });

  const uploadLiveChatIcon = (event) => uploadSettingImage(event, {
    settingKey: 'livechat_icon_url',
    label: 'Canlı destek ikonu',
    setLoading: setLiveChatIconBusy,
  });

  const removeLiveChatIcon = () => removeSettingImage({
    settingKey: 'livechat_icon_url',
    label: 'Canlı destek ikonu',
    prompt: 'Özel canlı destek ikonu kaldırılsın mı?',
    description: 'Varsayılan canlı destek ikonu yeniden kullanılacak.',
    setLoading: setLiveChatIconBusy,
  });

  const testSmtp = async () => {
    if (!testTo) { toast({ title: 'Test adresi girin' }); return; }
    setBusy(true);
    try { await client.post('/admin/mail/test-smtp', { to: testTo }); toast({ title: 'Test maili gönderildi' }); }
    catch (e) { toast({ title: 'SMTP Hata', description: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const sendBulk = async () => {
    if (!mail.subject || !mail.body_html) { toast({ title: 'Konu ve içerik gerekli' }); return; }
    setBusy(true);
    try {
      const payload = { subject: mail.subject, body_html: mail.body_html, target: mail.target, custom_emails: mail.target === 'custom' ? mail.custom_emails.split(/[,\n]/).map((x) => x.trim()).filter(Boolean) : [] };
      const { data } = await client.post('/admin/mail/send', payload);
      toast({ title: 'Toplu gönderim başladı', description: `${data.total} adrese gönderiliyor` });
      setTimeout(load, 3000);
    } catch (e) { toast({ title: 'Hata', description: e.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  if (error && !s) return <AdminError message={error} onRetry={load} />;
  if (!s) return <AdminSkeleton rows={7} label="Ayarlar yükleniyor" />;

  // Bir sekmede kaydedilmemiş değişiklik var mı? Sekme başlığındaki uyarı
  // noktası ve Kaydet butonunun etkinliği buradan gelir.
  const tabChangedKeys = (tabId) => {
    // Boşaltılan veya maskeli bırakılan sır alanı isteğe girmez (buildPayload
    // onu eler); alana yalnızca odaklanmış olmak değişiklik sayılmamalı.
    const keys = changedKeys(s, initial, TAB_FIELDS[tabId] || []).filter((key) => (
      !SECRET_FIELDS.includes(key) || (s[key] && s[key] !== MASKED_SECRET)
    ));
    // HızlıcaParalar ayarları `settings` kaydında değil `provider_configs`
    // satırındadır (bkz. services/hizlicaparalar.py) ve ayrı bir state'te
    // tutulur; sayılmazsa yönetici alanları doldurur ama Kaydet düğmesi hiç
    // çıkmaz ve altta "Kaydedilmemiş değişiklik yok" yazar.
    if (tabId === 'finance') {
      return [
        ...keys,
        ...hizlicaDirtyKeys().map((key) => `hizlicaparalar_${key}`),
        ...hizliHavaleDirtyKeys().map((key) => `hizlihavale_${key}`),
        ...zelvoqDirtyKeys().map((key) => `zelvoq_${key}`),
      ];
    }
    // Bildirim grupları da ayrı kayıttadır; sayılmazsa grup kimliği yazılır
    // ama Kaydet düğmesi hiç çıkmaz.
    if (tabId === 'telegram') {
      return [...keys, ...telegramRoutesDirtyKeys().map((key) => `route_${key}`)];
    }
    // Canlı destek sağlayıcısı da ayrı kayıttadır (provider_configs →
    // livechat); sayılmazsa Text.com seçilir ama Kaydet düğmesi hiç çıkmaz.
    if (tabId === 'livechat') {
      return liveChatProviderDirty() ? [...keys, 'livechat_provider'] : keys;
    }
    // Özel kod da `settings` kaydında değildir ve ayrı bir state'te tutulur;
    // sayılmazsa yönetici kodu yazar ama Kaydet düğmesi hiç çıkmaz.
    if (tabId === 'customcode') {
      return [...keys, ...customCodeDirtyKeys().map((key) => `custom_code_${key}`)];
    }
    if (tabId !== 'branding') return keys;
    // Marka sekmesi site adını logo metinlerinden eşitler; alanların kendisi
    // değişmese bile eşitlenecek bir fark olabilir.
    const siteName = brandingSiteName(s);
    return (siteName && !sameValue(siteName, initial?.site_name))
      ? [...keys, 'site_name']
      : keys;
  };
  // Bölüm rozeti: ayarları `settings` kaydında olmayan sağlayıcılar kendi
  // state'inden okunur. Koşulu bölüm bölüm JSX'e yazmak, üçüncü sağlayıcıda
  // unutulup rozeti hep sönük bırakırdı.
  const providerSectionActive = (section) => {
    if (section.config === 'hizlicaparalar') return Boolean(hizlica.enabled);
    if (section.config === 'hizlihavale') return Boolean(hizliHavale.enabled);
    if (section.config === 'zelvoq') return Boolean(zelvoq.enabled);
    if (section.config === 'cryptobox') return cryptoboxEnabled;
    if (section.config === 'cashmoney') return cashmoneyEnabled;
    if (section.config === 'paylux') return payluxEnabled;
    if (section.config === 'expay') return expayEnabled;
    if (section.config === 'primevpay') return primevpayEnabled;
    return Boolean(s[section.flag]);
  };
  const canSave = tabChangedKeys(tab).length > 0;
  const activeTab = visibleTabs.find((item) => item.id === tab) || visibleTabs[0];
  // Kaydet yalnızca açık sekmenin alanlarını gönderir; başka sekmede duran
  // değişiklik sessizce beklerdi. Başlıktaki rozet toplamı, menüdeki nokta da
  // hangi bölümde olduğunu söyler.
  const pendingTabs = visibleTabs
    .map((item) => ({ name: item.name, count: tabChangedKeys(item.id).length }))
    .filter((item) => item.count > 0);
  const pendingTotal = pendingTabs.reduce((sum, item) => sum + item.count, 0);
  // Menü süzgeci: eşleşme yoksa liste boş kalır, açık sekme yine çizilir.
  const navMatch = (item) => {
    const q = navQuery.trim().toLocaleLowerCase('tr');
    return !q || item.name.toLocaleLowerCase('tr').includes(q);
  };
  // Alt kümeli sayfada grup başlığı yoktur; tek sekmede şerit hiç çizilmez.
  const navGroups = onlyKey ? (
    visibleTabs.length > 1 ? [{ name: '', items: visibleTabs.filter(navMatch) }] : []
  ) : TAB_GROUPS
    .map((group) => ({
      name: group.name,
      items: group.ids
        .map((id) => visibleTabs.find((item) => item.id === id))
        .filter((item) => item && navMatch(item)),
    }))
    .filter((group) => group.items.length > 0);
  // Vazgeç yalnızca açık sekmenin alanlarını ilk hâline döndürür: başka
  // sekmedeki değişikliği de silseydi düğme, adının söylemediği bir iş yapardı.
  // Yöntem başına limitler tek ayar alanında durur; her seçici aynı
  // yazıcıyı kullanır.
  const setMethodLimits = (next) => setS({ ...s, payment_method_limits: next });
  const revertTab = () => {
    const keys = TAB_FIELDS[tab] || [];
    const next = { ...s };
    keys.forEach((key) => { next[key] = initial[key]; });
    // Marka sekmesi site adını logo metinlerinden türetir; o alan
    // TAB_FIELDS'te yoktur ama sekmeyle birlikte değişir.
    if (tab === 'branding') next.site_name = initial.site_name;
    // HızlıcaParalar ayrı bir kayıttan geldiği için ayrı geri alınır; yoksa
    // "Vazgeç" şeridi temizler ama alanlar dolu kalırdı.
    if (tab === 'finance') {
      setHizlica(hizlicaInitial);
      setHizliHavale(hizliHavaleInitial);
      setZelvoq(zelvoqInitial);
    }
    if (tab === 'telegram' && telegramRoutesInitial !== null) {
      setTelegramRoutes(telegramRoutesInitial);
    }
    setS(next);
  };
  const hasUnsavedHeraclesKey = Boolean(
    s.heracles_otp_api_key && s.heracles_otp_api_key !== MASKED_SECRET,
  );
  const canTestHeracles = Boolean(
    s.heracles_otp_api_key_configured && !hasUnsavedHeraclesKey,
  );
  const primaryColor = /^#[0-9a-f]{6}$/i.test(s.primary_color || '')
    ? s.primary_color
    : '#25ab6c';
  const secondaryColor = /^#[0-9a-f]{6}$/i.test(s.secondary_color || '')
    ? s.secondary_color
    : primaryColor;
  const activeThemeId = SITE_THEMES.some((theme) => theme.id === s.site_theme)
    ? s.site_theme
    : DEFAULT_THEME_ID;
  // Renkleri kimliği olan şemada (Vega: siyah zemin + altın vurgu) renk
  // alanları hiç çizilmez — site zaten şemanın kendi renklerini kullanır
  // (bkz. lib/theme.js `fixedColors`), alan açık bırakılsaydı yönetici renk
  // seçer ve sitede hiçbir şey değişmezdi.
  const themeColorsFixed = themeHasFixedColors(activeThemeId);

  // Şema seçimi renk alanlarını da o şemanın vurgularıyla doldurur; aksi hâlde
  // lacivert Merit zemininin üstünde eski kırmızı vurgu kalırdı. Yönetici
  // isterse aşağıdaki renk alanlarından kendi rengini yine seçebilir.
  const applyTheme = (theme) => setS((current) => ({
    ...current,
    site_theme: theme.id,
    primary_color: theme.accent,
    secondary_color: theme.surface,
  }));

  // Parola bir kez girilince ayar kaydına işlenir; şema bir daha sorulmaz
  // (parola değişene kadar). Şemadan çıkmak hiç sormaz.
  const themeNeedsUnlock = (theme) => (
    isThemeLocked(theme.id)
    && !(s?.site_theme_unlocked || []).includes(theme.id)
  );

  const selectTheme = (theme) => {
    if (themeNeedsUnlock(theme)) {
      setThemeUnlockPassword('');
      setThemeUnlockPrompt(theme);
      return;
    }
    applyTheme(theme);
  };

  // Özel şema kart listesinde çizilmez; anahtarı elle yazılarak seçilir.
  // Tanınmayan değer sessizce yok sayılmaz — yönetici yazdığının
  // uygulanmadığını görmelidir.
  const applyCustomTheme = () => {
    const theme = findCustomTheme(customThemeKey);
    if (!theme) {
      toast({
        title: 'Özel tema bulunamadı',
        description: 'Yazdığınız anahtar tanımlı bir özel temaya karşılık gelmiyor.',
      });
      return;
    }
    selectTheme(theme);
    setCustomThemeKey('');
    toast({
      title: `${theme.name} teması seçildi`,
      description: 'Yayına girmesi için Tasarım ayarlarını kaydedin.',
    });
  };
  const activeCustomTheme = getSiteTheme(activeThemeId).hidden
    ? getSiteTheme(activeThemeId)
    : null;

  const confirmThemeUnlock = async () => {
    const theme = themeUnlockPrompt;
    if (!theme) return;
    const password = themeUnlockPassword.trim();
    if (!password) {
      toast({ title: 'Parola girin', description: 'Bu şema parola ile korunuyor.' });
      return;
    }
    setThemeUnlockBusy(true);
    try {
      const { data } = await client.post('/admin/settings/theme-unlock', {
        site_theme: theme.id,
        password,
      });
      // Kilit sunucuda kalıcı açıldı; iki kopyayı da tazeleyelim ki alan
      // "değişmiş" sayılıp bir sonraki kayda eklenmesin.
      const unlocked = data.unlocked || [];
      setS((current) => ({ ...current, site_theme_unlocked: unlocked }));
      setInitial((current) => ({ ...current, site_theme_unlocked: unlocked }));
      applyTheme(theme);
      setThemeUnlockPrompt(null);
      setThemeUnlockPassword('');
      toast({
        title: `${theme.name} şeması açıldı`,
        description: 'Parola bir daha sorulmaz. Yayına girmesi için Tasarım ayarlarını kaydedin.',
      });
    } catch (e) {
      toast({
        title: 'Parola kabul edilmedi',
        description: e.response?.data?.detail || 'Şema parolası doğrulanamadı.',
      });
    } finally {
      setThemeUnlockBusy(false);
    }
  };

  // ---- Oyun servisleri yardımcıları ----
  const serviceFieldFilled = (field) => (
    Boolean(field.autoGenerate)
    || Boolean(String(s[field.key] || '').trim())
    || Boolean(s[`${field.key}_configured`])
  );
  const serviceReady = (service) => service.fields
    .filter((field) => field.required)
    .every(serviceFieldFilled);
  const serviceEnabled = (service) => (
    s[service.enabledKey] === undefined ? false : !!s[service.enabledKey]
  );
  const activeService = gameServices.find((item) => item.id === gameService)
    || gameServices[0];
  const callbackUrl = (path) => `${gameBase}${path}`;
  const copyCallback = async (path) => {
    try {
      await navigator.clipboard.writeText(callbackUrl(path));
      setCopiedCallback(path);
      setTimeout(() => setCopiedCallback(''), 2000);
    } catch {
      toast({ title: 'Kopyalanamadı', description: 'Adresi elle kopyalayın.' });
    }
  };

  return (
    <div>
      <div className="settings-head">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 settings-head-icon" style={{ background: 'rgba(56,189,248,0.15)', color: 'var(--v2-info, #38bdf8)' }}>
          {PageIcon ? <PageIcon size={18} /> : <SettingsIcon size={18} />}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-xl font-bold">{pageTitle || 'Ayarlar'}</h1>
          <p className="text-xs text-slate-400">{pageDesc || 'Site geneli konfigürasyon'}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!onlyKey && (
          <div className="settings-search">
            <Search size={16} />
            <input
              value={navQuery}
              onChange={(e) => setNavQuery(e.target.value)}
              className="admin-input pl-10"
              placeholder="Bölüm ara…"
              aria-label="Ayar bölümü ara"
            />
          </div>
          )}
          {pendingTotal > 0 && (
            <div
              className="flex items-center gap-2 rounded-full px-3.5 text-xs font-bold"
              style={{
                minHeight: 44,
                background: 'rgba(240,68,90,0.1)',
                border: '1px solid rgba(240,68,90,0.28)',
                color: 'var(--danger)',
              }}
              title={pendingTabs.map((item) => `${item.name} (${item.count})`).join(', ')}
            >
              <span className="settings-nav-dot" />
              {pendingTotal} kaydedilmemiş
            </div>
          )}
        </div>
      </div>

      <div className="settings-shell">
        {/* Sekmeler sayfanın üstünde yatay durur — Oyun Kontrol sayfasındaki
            segment kontrolünün aynısı. Gruplar korunur: on üç sekme etiketsiz
            tek bir şeritte hiyerarşisini kaybediyordu. */}
        {(!onlyKey || navGroups.length > 0) && (
        <nav className="settings-tabs" aria-label="Ayar bölümleri">
          {navGroups.map((group) => (
            <div key={group.name || 'page'} className="settings-tabgroup">
              {group.name && <h3>{group.name}</h3>}
              <div className="settings-tabgroup-list">
                {group.items.map((t) => {
                  const pending = tabChangedKeys(t.id).length;
                  return (
                    <button
                      key={t.id}
                      type="button"
                      onClick={() => setTab(t.id)}
                      aria-current={tab === t.id ? 'page' : undefined}
                      className={`settings-tab ${tab === t.id ? 'is-active' : ''}`}
                      title={pending ? `${pending} kaydedilmemiş değişiklik` : undefined}
                    >
                      <t.icon size={15} className="shrink-0" />
                      <span>{t.name}</span>
                      {pending > 0 && <span className="settings-nav-dot" />}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
          {navGroups.length === 0 && (
            <div className="settings-nav-empty">“{navQuery}” ile eşleşen bölüm yok.</div>
          )}
        </nav>
        )}

        <div className="settings-pane">
          <div className="settings-pane-body">
            {/* Bölünmüş sayfada başlık ve açıklama sayfanın kendi başlığındadır;
                sekme adı da şeritte yazar, bölme başlığı yalnızca tekrar olurdu. */}
            {!onlyKey && (
            <div className="settings-pane-head">
              <div className="text-base font-bold">{activeTab?.name}</div>
              {activeTab?.desc && (
                <p className="mt-1 text-xs leading-5 text-slate-400">{activeTab.desc}</p>
              )}
            </div>
            )}
        {tab === 'general' && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="col-span-full mb-1">
              <div className="text-sm font-semibold">Genel bilgiler</div>
              <div className="mt-1 text-xs text-slate-500">
                Site zemini, şema ve tema renkleri <b>Tasarım</b>, canlı destek
                buton ikonu <b>Canlı Destek</b> sekmesindedir.
              </div>
            </div>

            <F l="Site Adı"><input value={s.site_name} onChange={(e) => setS({ ...s, site_name: e.target.value })} className="admin-input" /></F>
            <F l="İletişim E-Posta"><input value={s.contact_email} onChange={(e) => setS({ ...s, contact_email: e.target.value })} className="admin-input" /></F>
            <F l="İletişim Telefon"><input value={s.contact_phone} onChange={(e) => setS({ ...s, contact_phone: e.target.value })} className="admin-input" /></F>
            <F l="Üst Bilgi Metni" full><input value={s.top_bar_message} onChange={(e) => setS({ ...s, top_bar_message: e.target.value })} className="admin-input" /></F>
            <F l="Footer Metni" full><textarea value={s.footer_text} onChange={(e) => setS({ ...s, footer_text: e.target.value })} className="admin-input" style={{ minHeight: 80, padding: 12 }} /></F>
            {/* Dört bayrak sayfanın dibinde çıplak satırlar hâlinde
                duruyordu; kendi kutularında ve tek başlık altında. */}
            <div className="col-span-full admin-fieldset">
              <div className="admin-fieldset-title">Site durumu</div>
              <div className="settings-toggles">
              <label className="settings-toggle"><input type="checkbox" checked={s.maintenance_mode} onChange={(e) => setS({ ...s, maintenance_mode: e.target.checked })} /> Bakım Modu (site kapalı)</label>
              <label className="settings-toggle"><input type="checkbox" checked={s.registration_open} onChange={(e) => setS({ ...s, registration_open: e.target.checked })} /> Yeni Kayıtlara Açık</label>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={s.kyc_auto_approve !== false}
                  onChange={(e) => setS({ ...s, kyc_auto_approve: e.target.checked })}
                />
                <span>
                  <span className="block">Kayıtta Otomatik KYC</span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Açıkken kayıt formu ad, soyad, T.C. kimlik no ve doğum tarihi
                    ister; bilgiler anında doğrulanır ve hesap KYC onaylı açılır.
                    Kapattığınızda kayıt kimlik sormaz, hesap doğrulanmamış açılır
                    ve üye Hesabım &rarr; KYC Doğrulama sayfasından başvurur —
                    başvurular <b>KYC Kayıtları</b> ekranındaki “Bekleyen”
                    sekmesinde onayınızı bekler. Para çekmek her iki modda da
                    doğrulama ister; para yatırma yalnızca otomatik modda
                    doğrulama arar.
                  </span>
                </span>
              </label>
              <label className="settings-toggle">
                <input
                  type="checkbox"
                  checked={s.callback_enabled !== false}
                  onChange={(e) => setS({ ...s, callback_enabled: e.target.checked })}
                />
                <span>
                  <span className="block">"Beni Ara" Butonu Açık</span>
                  <span className="mt-0.5 block text-xs text-slate-500">
                    Kapatıldığında üst bant ve mobil menüdeki buton kalkar, yeni arama
                    talebi de kabul edilmez. Gelen talepler Beni Ara Talepleri sayfasında durmaya devam eder.
                  </span>
                </span>
              </label>
              </div>
            </div>
            <div className="col-span-full mt-2 rounded-xl border border-sky-500/20 bg-sky-500/5 p-4">
              <div className="text-sm font-semibold text-sky-300">
                Oyuncu portalı tarayıcı kısıtları
              </div>
              <p className="mt-1 text-xs leading-5 text-slate-400">
                Yalnızca oyuncu sayfalarında uygulanır; yönetim paneli etkilenmez.
                Bunlar güvenlik önlemi değildir: tarayıcı menüsünden kaynak koduna ve
                geliştirici araçlarına yine erişilebilir. Form alanları ile ödeme ve
                referans bilgileri kullanılabilir kalır.
              </p>
              <div className="mt-3 grid gap-3 md:grid-cols-3">
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={s.portal_block_context_menu !== false}
                    onChange={(e) => setS({
                      ...s,
                      portal_block_context_menu: e.target.checked,
                    })}
                  />
                  <span>
                    <span className="block">Sağ tık menüsünü engelle</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Fare sağ tık ve mobil uzun basma menüsünü kapatır.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={s.portal_block_text_copy !== false}
                    onChange={(e) => setS({
                      ...s,
                      portal_block_text_copy: e.target.checked,
                    })}
                  />
                  <span>
                    <span className="block">Metin seçme ve kopyalamayı engelle</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      Sayfa içeriğinde seçim, kopyalama ve kesmeyi kapatır.
                    </span>
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={s.portal_block_devtools !== false}
                    onChange={(e) => setS({
                      ...s,
                      portal_block_devtools: e.target.checked,
                    })}
                  />
                  <span>
                    <span className="block">Geliştirici kısayollarını engelle</span>
                    <span className="mt-0.5 block text-xs text-slate-500">
                      F12, kaynak görüntüleme ve inceleme kısayollarını kapatır.
                    </span>
                  </span>
                </label>
              </div>
            </div>
          </div>
        )}

        {tab === 'customcode' && (
          <CustomCodePanel
            value={customCode}
            meta={customCodeMeta}
            onChange={(key, next) => setCustomCode(
              (current) => ({ ...current, [key]: next }),
            )}
            onRefresh={applyRestoredCode}
          />
        )}

        {tab === 'design' && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="col-span-full">
              <div className="text-sm font-semibold">Hazır tasarım şeması</div>
              <div className="mt-1 text-xs text-slate-500">
                Şema sitenin zeminini, kart yüzeylerini, köşe yuvarlaklığını ve
                yazı tipini belirler. Seçtiğinizde aşağıdaki renk alanları da
                şemanın vurgularıyla dolar; dilerseniz sonrasında kendi
                renklerinizi verebilirsiniz. Değişiklik <b>Kaydet</b>'ten sonra
                yayına girer.
              </div>
            </div>

            <div className="col-span-full grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {LISTED_SITE_THEMES.map((theme) => {
                const selected = activeThemeId === theme.id;
                const [bg, card, accent] = theme.swatches;
                const locked = isThemeLocked(theme.id);
                const needsUnlock = themeNeedsUnlock(theme);
                return (
                  <button
                    key={theme.id}
                    type="button"
                    onClick={() => selectTheme(theme)}
                    aria-pressed={selected}
                    className={`settings-theme-card${selected ? ' is-selected' : ''}`}
                    style={selected ? { borderColor: accent, boxShadow: `0 0 0 3px ${accent}26` } : undefined}
                  >
                    <ThemeShellPreview shell={theme.shell} bg={bg} card={card} accent={accent} />
                    <div className="mt-3 flex items-center gap-2">
                      <span
                        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                        style={{
                          border: `1px solid ${selected ? accent : 'rgba(255,255,255,0.25)'}`,
                          background: selected ? accent : 'transparent',
                        }}
                      >
                        {selected && <Check size={10} className="text-black" />}
                      </span>
                      <span className="text-sm font-bold">{theme.name}</span>
                      {locked && (
                        <span
                          className={`settings-theme-lock${needsUnlock ? '' : ' is-open'}`}
                          title={needsUnlock
                            ? 'Bu şemaya geçmek parola ister'
                            : 'Parolası girildi; bir daha sorulmaz'}
                        >
                          <Lock size={10} />
                          {needsUnlock ? 'Parolalı' : 'Kilit açık'}
                        </span>
                      )}
                    </div>
                    <div className="mt-1.5 text-xs leading-5 text-slate-400">{theme.description}</div>
                    {/* Şemanın üç ana rengi: kartta yalnızca maket varken
                        "hangi renk vurgu" ayırt edilemiyordu. */}
                    <div className="mt-2.5 flex items-center gap-1.5">
                      {[bg, card, accent].map((color) => (
                        <span
                          key={color}
                          className="h-3.5 w-3.5 rounded-full"
                          style={{ background: color, border: '1px solid var(--v2-line, rgba(255,255,255,0.14))' }}
                        />
                      ))}
                    </div>
                  </button>
                );
              })}
            </div>

            <div className="col-span-full rounded-xl border border-white/10 bg-white/[0.02] p-4">
              <div className="text-sm font-semibold">Özel tema</div>
              <div className="mt-1 text-xs text-slate-500">
                Kurulumunuza özel tasarlanmış tema yukarıdaki listede görünmez.
                Size verilen tema anahtarını (ya da konumunu) yazıp <b>Uygula</b>'ya
                basın, ardından <b>Kaydet</b>'le yayına alın. Hazır bir şemaya
                dönmek için yukarıdaki kartlardan birini seçmeniz yeterlidir.
              </div>
              {activeCustomTheme && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-3 py-1.5 text-xs text-emerald-300">
                  <Check size={12} />
                  Seçili özel tema: <b>{activeCustomTheme.name}</b>
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2">
                <input
                  className="admin-input admin-input-w-lg"
                  value={customThemeKey}
                  onChange={(e) => setCustomThemeKey(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyCustomTheme(); } }}
                  placeholder="ör. demo"
                  aria-label="Özel tema anahtarı"
                  autoComplete="off"
                  spellCheck={false}
                />
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={applyCustomTheme}
                  disabled={!customThemeKey.trim()}
                >
                  Uygula
                </button>
              </div>
            </div>

            {themeColorsFixed ? (
              <div className="col-span-full mt-2 border-t border-white/5 pt-4">
                <div className="text-sm font-semibold">Tema renkleri</div>
                <div className="mt-1 text-xs text-slate-500">
                  <b>{getSiteTheme(activeThemeId).name}</b> şemasının renkleri sabittir:
                  siyah zemin ve altın vurgu şemanın kimliğidir, kabuğun bütün
                  gradyanları ve düğmeleri o dengeye göre ölçüldü. Bu yüzden renk
                  seçimi bu şemada gösterilmez — seçilse de sitede hiçbir şey
                  değişmezdi. Başka bir şemaya geçtiğinizde alanlar geri gelir.
                </div>
              </div>
            ) : (
              <>
              <div className="col-span-full mt-2 border-t border-white/5 pt-4">
                <div className="text-sm font-semibold">Tema renkleri</div>
                <div className="mt-1 text-xs text-slate-500">
                  Ana renk buton ve vurgulara; üst alan rengi header ve bant yüzeylerine uygulanır.
                  Aynı renkler yönetim panelinin vurgularına da yansır.
                </div>
              </div>
              <F l="Site Ana Rengi">
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={primaryColor}
                    onChange={(e) => setS({ ...s, primary_color: e.target.value })}
                    className="h-11 w-14 cursor-pointer rounded-lg border border-white/10 bg-slate-900 p-1"
                    aria-label="Site ana rengini seç"
                  />
                  <input
                    value={s.primary_color || ''}
                    onChange={(e) => setS({ ...s, primary_color: e.target.value.slice(0, 7) })}
                    className="admin-input flex-1 font-mono uppercase"
                    maxLength={7}
                    placeholder="#25AB6C"
                  />
                </div>
              </F>
              <F l="Üst Alan Tema Rengi">
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={secondaryColor}
                    onChange={(e) => setS({ ...s, secondary_color: e.target.value })}
                    className="h-11 w-14 cursor-pointer rounded-lg border border-white/10 bg-slate-900 p-1"
                    aria-label="Üst alan tema rengini seç"
                  />
                  <input
                    value={s.secondary_color || ''}
                    onChange={(e) => setS({ ...s, secondary_color: e.target.value.slice(0, 7) })}
                    className="admin-input flex-1 font-mono uppercase"
                    maxLength={7}
                    placeholder="Boşsa ana renk kullanılır"
                  />
                </div>
              </F>
              <div
                className="col-span-full flex min-h-16 items-center justify-between rounded-lg px-4 text-sm font-bold text-white"
                style={{ background: secondaryColor }}
              >
                <span>Üst alan önizlemesi</span>
                <span
                  className="rounded-lg px-4 py-2"
                  style={{ background: primaryColor, color: '#fff' }}
                >
                  Ana renk
                </span>
              </div>
              </>
            )}

            {activeThemeId === VEGA_THEME_ID && (
              <>
                <div className="col-span-full mt-2 border-t border-white/5 pt-4">
                  <div className="text-sm font-semibold">Sayfa slider'ları (Vega)</div>
                  <div className="mt-1 text-xs text-slate-500">
                    Slot ve Canlı Casino sayfalarının üstündeki banner slider'ı.
                    Banner'lar <b>Banner &amp; Kategori Kartları</b> sayfasından,
                    konumu <b>Slot Sayfası</b> / <b>Canlı Casino Sayfası</b>
                    {' '}seçilerek eklenir. Bu seçim yalnızca Vega şemasında
                    geçerlidir.
                  </div>
                </div>
                <label className="col-span-full flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={s.vega_slot_slider_enabled !== false}
                    onChange={(e) => setS({ ...s, vega_slot_slider_enabled: e.target.checked })}
                  />
                  Slot sayfasında slider görünsün
                </label>
                <label className="col-span-full flex items-center gap-2 pl-6 text-sm text-slate-400">
                  <input
                    type="checkbox"
                    disabled={s.vega_slot_slider_enabled === false}
                    checked={s.vega_slot_slider_use_main === true}
                    onChange={(e) => setS({ ...s, vega_slot_slider_use_main: e.target.checked })}
                  />
                  Slot sayfasına banner eklenmediyse ana sayfa banner'larını kullan
                </label>
                <label className="col-span-full flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={s.vega_live_casino_slider_enabled !== false}
                    onChange={(e) => setS({
                      ...s, vega_live_casino_slider_enabled: e.target.checked,
                    })}
                  />
                  Canlı Casino sayfasında slider görünsün
                </label>
                <label className="col-span-full flex items-center gap-2 pl-6 text-sm text-slate-400">
                  <input
                    type="checkbox"
                    disabled={s.vega_live_casino_slider_enabled === false}
                    checked={s.vega_live_casino_slider_use_main === true}
                    onChange={(e) => setS({
                      ...s, vega_live_casino_slider_use_main: e.target.checked,
                    })}
                  />
                  Canlı Casino sayfasına banner eklenmediyse ana sayfa banner'larını kullan
                </label>
              </>
            )}

            <div className="col-span-full mt-2 border-t border-white/5 pt-4">
              <div className="text-sm font-semibold">Promosyon kartları</div>
              <div className="mt-1 text-xs text-slate-500">
                Bonus kartının altındaki koşul şeridi (min. yatırım, max. bonus,
                çevrim). Kapatırsanız rakamlar kartta görünmez; oyuncu koşulları
                kartın <b>Detay</b> penceresinde görmeye devam eder. Kampanya
                değerleri değişmez, yalnızca gizlenir.
              </div>
            </div>
            <label className="col-span-full flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={s.bonus_card_stats_enabled !== false}
                onChange={(e) => setS({ ...s, bonus_card_stats_enabled: e.target.checked })}
              />
              Bonus kartlarında koşul şeridi görünsün
            </label>

            <BottomNavEditor
              items={s.mobile_bottom_nav}
              onChange={(mobile_bottom_nav) => setS({ ...s, mobile_bottom_nav })}
            />

          </div>
        )}

        {tab === 'branding' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {/* Logo: yükleme, adres ve metin logosu tek kartta toplanır;
                  önizleme sitedeki sırayla (görsel varsa görsel) çizilir. */}
              <section className="admin-subcard space-y-4 p-4 lg:col-span-2">
                <BrandingCardHeader
                  icon={ImageIcon}
                  title="Site logosu"
                  hint={`PNG, JPG, GIF, WEBP veya SVG · en fazla ${UPLOAD_MAX_LABEL}. Hareketli logo için GIF yükleyin; şeffaf arka plan için PNG ya da SVG önerilir.`}
                />

                <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                  <div className="admin-asset-frame h-24 w-full shrink-0 sm:w-56">
                    {s.logo_image_url ? (
                      <img
                        src={resolveApiAssetUrl(s.logo_image_url)}
                        alt="Logo önizleme"
                        className="max-h-full max-w-full object-contain"
                      />
                    ) : (
                      <span className="inline-flex items-baseline">
                        <span className="text-2xl font-black" style={{ color: primaryColor }}>
                          {s.logo_text_primary || 'Site'}
                        </span>
                        <span className="text-2xl font-black text-white">
                          {s.logo_text_secondary}
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <label className={`admin-btn-secondary inline-flex cursor-pointer items-center gap-2 ${logoBusy ? 'pointer-events-none opacity-50' : ''}`}>
                        {logoBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                        {uploadLabel('logo_image_url', s.logo_image_url ? 'Değiştir' : 'Logo Yükle')}
                        <input
                          type="file"
                          accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,.svg"
                          className="hidden"
                          onChange={uploadLogo}
                          disabled={logoBusy}
                        />
                      </label>
                      {s.logo_image_url && (
                        <button
                          type="button"
                          onClick={removeLogo}
                          disabled={logoBusy}
                          className="admin-btn-secondary inline-flex items-center gap-2 text-red-400"
                        >
                          <Trash2 size={14} /> Sil
                        </button>
                      )}
                    </div>
                    <input
                      value={s.logo_image_url || ''}
                      onChange={(event) => setS({ ...s, logo_image_url: event.target.value })}
                      className="admin-input"
                      placeholder="veya görsel adresi: https://..."
                      disabled={logoBusy}
                    />
                  </div>
                </div>

                <div className="admin-divider" />

                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Metin logosu
                  </div>
                  <p className="mt-1 text-xs text-slate-400">
                    Görsel logo yoksa site bu iki metni kullanır; ilki ana renkle çizilir.
                  </p>
                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <F l="İlk Metin">
                      <input
                        value={s.logo_text_primary}
                        onChange={(e) => setS({ ...s, logo_text_primary: e.target.value })}
                        className="admin-input"
                        placeholder="Daria"
                      />
                    </F>
                    <F l="İkinci Metin">
                      <input
                        value={s.logo_text_secondary}
                        onChange={(e) => setS({ ...s, logo_text_secondary: e.target.value })}
                        className="admin-input"
                        placeholder="Bet"
                      />
                    </F>
                  </div>
                </div>
              </section>

              {/* Favicon ayrı bir alandır: logo yüklenmiş olsa da sekme
                  simgesi paketteki varsayılanla kalır. */}
              <section className="admin-subcard space-y-4 p-4">
                <BrandingCardHeader
                  icon={Globe2}
                  title="Favicon"
                  hint="Tarayıcı sekmesi simgesi. Kare PNG (512×512), ICO veya SVG önerilir."
                />

                <div className="admin-tab-preview">
                  <div className="admin-tab-preview-chip">
                    {s.favicon_url ? (
                      <img
                        src={resolveApiAssetUrl(s.favicon_url)}
                        alt="Favicon önizleme"
                        className="h-4 w-4 shrink-0 object-contain"
                      />
                    ) : (
                      <span className="h-4 w-4 shrink-0 rounded-sm bg-slate-600" />
                    )}
                    <span className="truncate text-xs text-slate-300">
                      {brandingSiteName(s) || s.site_name || 'Site'}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <label className={`admin-btn-secondary inline-flex cursor-pointer items-center gap-2 ${faviconBusy ? 'pointer-events-none opacity-50' : ''}`}>
                      {faviconBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {uploadLabel('favicon_url', s.favicon_url ? 'Değiştir' : 'Favicon Yükle')}
                      <input
                        type="file"
                        accept="image/png,image/gif,image/webp,image/x-icon,image/vnd.microsoft.icon,.ico,image/svg+xml,.svg"
                        className="hidden"
                        onChange={uploadFavicon}
                        disabled={faviconBusy}
                      />
                    </label>
                    {s.favicon_url && (
                      <button
                        type="button"
                        onClick={removeFavicon}
                        disabled={faviconBusy}
                        className="admin-btn-secondary inline-flex items-center gap-2 text-red-400"
                      >
                        <Trash2 size={14} /> Sil
                      </button>
                    )}
                  </div>
                  <input
                    value={s.favicon_url || ''}
                    onChange={(event) => setS({ ...s, favicon_url: event.target.value })}
                    className="admin-input"
                    placeholder="veya simge adresi: https://..."
                    disabled={faviconBusy}
                  />
                </div>

                <p className="text-[11px] text-slate-500">
                  Boş bırakılırsa sitenin varsayılan simgesi gösterilir. Tarayıcı
                  eski simgeyi önbellekte tutabilir; değişikliği görmek için
                  sekmeyi yenileyin.
                </p>
              </section>
            </div>

            <div className="admin-subcard flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs text-slate-400">
                Kaydedildiğinde site adı, logo metinleriyle otomatik eşitlenir
                {brandingSiteName(s) ? (
                  <>
                    {' '}→ <span className="font-semibold text-slate-200">{brandingSiteName(s)}</span>
                  </>
                ) : '.'}
              </p>
              <button
                onClick={saveBranding}
                disabled={busy || !canSave}
                className="admin-btn-primary inline-flex items-center justify-center gap-2 px-6 py-2.5"
              >
                {busy && <Loader2 size={14} className="animate-spin" />}
                <Save size={14} /> Kaydet
              </button>
            </div>
          </div>
        )}

        {tab === 'finance' && (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {financePart !== 'providers' && (
            <>
            <F l="Genel Minimum Çekim Tutarı (₺)">
              <input
                type="number"
                min="0"
                step="0.01"
                value={s.withdraw_min_amount ?? 100}
                onChange={(e) => setS({
                  ...s, withdraw_min_amount: Number(e.target.value),
                })}
                className="admin-input"
              />
            </F>
            <F l="Genel Maksimum Çekim Tutarı (₺)">
              <input
                type="number"
                min="0.01"
                step="0.01"
                value={s.withdraw_max_amount ?? 100000}
                onChange={(e) => setS({
                  ...s, withdraw_max_amount: Number(e.target.value),
                })}
                className="admin-input"
              />
            </F>
            <WithdrawRulesSettings canWrite={can('finance.write')} />
            </>
            )}

            {financePart !== 'rules' && (
            <>
            <div className="col-span-full mt-2">
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                Entegrasyon ayarları
              </div>
              <p className="mt-1 text-xs text-slate-400">
                Tüm bağlantı bilgileri buradan yönetilir; sunucudaki
                <code className="mx-1 text-slate-300">.env</code>
                dosyası artık okunmaz.
              </p>
              <div
                className="mt-3 flex flex-wrap gap-2"
                hidden={FINANCE_SECTIONS.length < 2}
              >
                {FINANCE_SECTIONS.map((section) => (
                  <button
                    key={section.id}
                    type="button"
                    onClick={() => setFinanceSection(section.id)}
                    className={`settings-provider-chip${financeSection === section.id ? ' is-active' : ''}`}
                  >
                    <section.icon size={16} />
                    {section.name}
                    <span
                      className={`settings-provider-dot${
                        providerSectionActive(section) ? ' is-on' : ''
                      }`}
                      title={
                        providerSectionActive(section) ? 'Aktif' : 'Pasif'
                      }
                    />
                  </button>
                ))}
              </div>
            </div>

            {financeSection === 'wizkasa' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">Wizkasa</h3>
              <p className="mt-1 text-xs text-slate-400">
                Havale, Papara, kart, kripto ve cüzdan yatırımlarını tek
                sözleşmeyle yönetir. Sonuç sağlayıcının callback bildirimiyle
                kesinleşir; panelden elle onaylanmaz.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!s.wizkasa_enabled}
                  onChange={(e) => setS({ ...s, wizkasa_enabled: e.target.checked })}
                />
                Wizkasa entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için API Key, Secret Key ve callback adresi
                zorunludur. Ayrıca sunucunun çıkış IP adresi Wizkasa panelinde
                beyaz listeye eklenmiş olmalıdır.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.wizkasa_deposit_enabled !== false}
                    onChange={(e) => setS({
                      ...s, wizkasa_deposit_enabled: e.target.checked,
                    })}
                  />
                  Wizkasa yatırım açık
                </label>
                <SharedLimitFields
                  minLabel="Minimum yatırım (₺)"
                  maxLabel="Maksimum yatırım (₺)"
                  minValue={s.wizkasa_deposit_min_amount ?? 100}
                  maxValue={s.wizkasa_deposit_max_amount ?? 100000}
                  onMinChange={(value) => setS({ ...s, wizkasa_deposit_min_amount: value })}
                  onMaxChange={(value) => setS({ ...s, wizkasa_deposit_max_amount: value })}
                  provider="wizkasa"
                  direction="deposit"
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                <div className="mt-3">
                  <div className="settings-field-label">Açık yatırım yöntemleri</div>
                  <MethodLimitPicker
                    options={WIZKASA_DEPOSIT_LEDGERS}
                    selected={ledgerList(s.wizkasa_deposit_ledger_types)}
                    onToggle={(id) => setS({
                      ...s,
                      wizkasa_deposit_ledger_types: toggleLedger(
                        s.wizkasa_deposit_ledger_types, id),
                    })}
                    provider="wizkasa"
                    direction="deposit"
                    sharedMin={s.wizkasa_deposit_min_amount ?? 100}
                    sharedMax={s.wizkasa_deposit_max_amount ?? 100000}
                    limits={s.payment_method_limits}
                    onLimitsChange={setMethodLimits}
                  />
                </div>
              </section>
              <section className="provider-money is-withdraw">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.wizkasa_withdraw_enabled !== false}
                    onChange={(e) => setS({
                      ...s, wizkasa_withdraw_enabled: e.target.checked,
                    })}
                  />
                  Wizkasa çekim açık
                </label>
                <SharedLimitFields
                  minLabel="Minimum çekim (₺)"
                  maxLabel="Maksimum çekim (₺)"
                  minValue={s.wizkasa_withdraw_min_amount ?? 100}
                  maxValue={s.wizkasa_withdraw_max_amount ?? 100000}
                  onMinChange={(value) => setS({ ...s, wizkasa_withdraw_min_amount: value })}
                  onMaxChange={(value) => setS({ ...s, wizkasa_withdraw_max_amount: value })}
                  provider="wizkasa"
                  direction="withdrawal"
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                {/* Genel limit Wizkasa çekiminde de uygulanır; iki alt sınırın
                    büyüğü geçerlidir. Ayrı ayrı bakıldığında fark edilmediği
                    için sonucu burada yazıyoruz. */}
                <p className="mt-2 text-xs text-slate-500">
                  Genel minimum çekim (
                  {Number(s.withdraw_min_amount ?? 100).toLocaleString('tr-TR')} ₺)
                  bu tutarın üzerine biner: oyuncunun görebileceği alt sınır
                  ikisinin büyüğü olan{' '}
                  <span className="font-semibold text-slate-300">
                    {Math.max(
                      Number(s.withdraw_min_amount ?? 100) || 0,
                      Number(s.wizkasa_withdraw_min_amount ?? 100) || 0,
                    ).toLocaleString('tr-TR')} ₺
                  </span>
                  {' '}olur.
                </p>
                <div className="mt-3">
                  <div className="settings-field-label">Açık çekim yöntemleri</div>
                  <MethodLimitPicker
                    options={WIZKASA_WITHDRAW_LEDGERS}
                    selected={ledgerList(s.wizkasa_withdraw_ledger_types)}
                    onToggle={(id) => setS({
                      ...s,
                      wizkasa_withdraw_ledger_types: toggleLedger(
                        s.wizkasa_withdraw_ledger_types, id),
                    })}
                    provider="wizkasa"
                    direction="withdrawal"
                    sharedMin={s.wizkasa_withdraw_min_amount ?? 100}
                    sharedMax={s.wizkasa_withdraw_max_amount ?? 100000}
                    limits={s.payment_method_limits}
                    onLimitsChange={setMethodLimits}
                  />
                  <p className="mt-2 text-xs text-slate-500">
                    Kart çekimi sağlayıcıda desteklenmez. Papara çekimi için
                    hesap numarası alanı gerektiğinden şimdilik sunulmaz.
                  </p>
                </div>
              </section>
            </div>
            <div
              className="col-span-full mt-1 overflow-hidden rounded-xl"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--v2-line, rgba(255,255,255,0.08))',
              }}
            >
              <button
                type="button"
                onClick={() => setWizkasaApiOpen((value) => !value)}
                aria-expanded={wizkasaApiOpen}
                className="flex w-full items-start gap-2 px-4 py-3 text-left"
              >
                <ChevronDown
                  size={16}
                  className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${wizkasaApiOpen ? '' : '-rotate-90'}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">
                    API bağlantı bilgileri
                  </div>
                  {wizkasaApiOpen ? (
                    <div className="mt-1 text-xs text-slate-500">
                      API adresi, anahtarlar, callback ve dönüş adresi, yatırım
                      kanalı ile kripto para/ağ seçimi.
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">
                      {s.wizkasa_api_key && s.wizkasa_secret_key ? (
                        'Anahtarlar girilmiş — düzenlemek için tıklayın.'
                      ) : (
                        <span className="text-amber-300/90">
                          API Key veya Secret Key girilmemiş — girmek için tıklayın.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
              {wizkasaApiOpen && (
              <div
                className="grid grid-cols-1 gap-3 px-4 pb-4 pt-4 md:grid-cols-2"
                style={{ borderTop: '1px solid var(--v2-line, rgba(255,255,255,0.06))' }}
              >
                <F l="Wizkasa API URL" full>
                  <input
                    value={s.wizkasa_api_url || ''}
                    onChange={(e) => setS({ ...s, wizkasa_api_url: e.target.value })}
                    className="admin-input"
                    placeholder="https://api.wizkasa.com"
                  />
                </F>
                <F l="Wizkasa API Key">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.wizkasa_api_key || ''}
                    onChange={(e) => setS({ ...s, wizkasa_api_key: e.target.value })}
                    className="admin-input"
                  />
                </F>
                <F l="Wizkasa Secret Key">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.wizkasa_secret_key || ''}
                    onChange={(e) => setS({ ...s, wizkasa_secret_key: e.target.value })}
                    className="admin-input"
                  />
                </F>
                <CallbackField
                  label="Wizkasa Callback URL"
                  path="/api/wizkasa/callback"
                  note="Yatırım isteğinde zorunlu olarak gönderilir ve Wizkasa paneline de yazılmalıdır. Ayrıca sunucunun çıkış IP adresi Wizkasa panelinde beyaz listede olmalıdır."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
                <F l="Yatırım kanalı">
                  <select
                    value={s.wizkasa_deposit_channel || 'LINK'}
                    onChange={(e) => setS({ ...s, wizkasa_deposit_channel: e.target.value })}
                    className="admin-input"
                  >
                    <option value="LINK">LINK — sağlayıcının ödeme sayfası</option>
                    <option value="API">API — hesap bilgisi bizim ekranımızda</option>
                  </select>
                </F>
                <F l="Kripto para / ağ">
                  <div className="flex gap-2">
                    <select
                      value={s.wizkasa_crypto_coin || 'USDT'}
                      onChange={(e) => setS({ ...s, wizkasa_crypto_coin: e.target.value })}
                      className="admin-input"
                    >
                      {WIZKASA_CRYPTO_COINS.map((coin) => (
                        <option key={coin} value={coin}>{coin}</option>
                      ))}
                    </select>
                    <select
                      value={s.wizkasa_crypto_network || 'TRC20'}
                      onChange={(e) => setS({ ...s, wizkasa_crypto_network: e.target.value })}
                      className="admin-input"
                    >
                      {WIZKASA_CRYPTO_NETWORKS.map((network) => (
                        <option key={network} value={network}>{network}</option>
                      ))}
                    </select>
                  </div>
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Kripto <strong>çekimde</strong> ve kripto yatırım API kanalında
                    sağlayıcıya bildirilir. Sağlayıcı yalnızca bu değerleri kabul
                    eder; LINK kanalında oyuncu seçimi ödeme sayfasında yapar.
                  </div>
                </F>
              </div>
              )}
            </div>
            {wizkasaStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: wizkasaStatus.connected
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${wizkasaStatus.connected
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={wizkasaStatus.connected ? 'text-emerald-300' : 'text-red-300'}>
                  {wizkasaStatus.connected
                    ? 'İmza doğrulandı (test siparişi bulunamadı — beklenen).'
                    : 'İmza doğrulanamadı.'}
                </span>
                {wizkasaStatus.ip_allowed === false && (
                  <span className="ml-2 text-amber-300">
                    Sunucu IP adresi beyaz listede değil — yatırım/çekim açılmaz.
                  </span>
                )}
                {wizkasaStatus.ip_allowed === true && (
                  <span className="ml-2 text-emerald-300">
                    IP yetkisi tamam ({wizkasaStatus.active_account_count ?? 0} aktif hesap).
                  </span>
                )}
                {wizkasaStatus.ip_note && (
                  <span className="ml-2 text-amber-300">
                    Sağlayıcı notu: {wizkasaStatus.ip_note} — hesap/bayi ataması
                    Wizkasa tarafında yapılmalıdır.
                  </span>
                )}
                {wizkasaStatus.error && (
                  <span className="ml-2 text-slate-400">
                    {wizkasaStatus.error}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={testWizkasa}
              disabled={busy || canSave || !s.wizkasa_enabled}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
              title={canSave ? 'Önce değişiklikleri kaydedin' : undefined}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Wizkasa İmza ve IP Yetkisini Test Et
            </button>
            </>
            )}

            {financeSection === 'hemensystem' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">Hemen Havale</h3>
              <p className="mt-1 text-xs text-slate-400">
                Havale/EFT yatırım ve çekim ile aynı bayi hesabına bağlı 3D
                Secure kredi kartı yatırımı. Havalede oyuncuya özel IBAN ve
                ödeme bağlantısı döner, kartta sağlayıcının güvenli ödeme
                sayfası açılır; sonuç her ikisinde de sağlayıcının callback
                bildirimiyle kesinleşir.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!s.hemensystem_enabled}
                  onChange={(e) => setS({
                    ...s, hemensystem_enabled: e.target.checked,
                  })}
                />
                Hemen Havale entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için bayi e-postası, şifresi,
                <strong> website_id</strong> ve callback adresi zorunludur;
                Tip 2 seçiliyse ayrıca api secret gerekir. Kredi kartı akışı
                api secret&apos;ı <strong>her tipte</strong> zorunlu kılar.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.hemensystem_deposit_enabled !== false}
                    onChange={(e) => setS({
                      ...s, hemensystem_deposit_enabled: e.target.checked,
                    })}
                  />
                  Hemen Havale yatırım açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum yatırım (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.hemensystem_deposit_min_amount ?? 100}
                      onChange={(e) => setS({
                        ...s,
                        hemensystem_deposit_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum yatırım (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.hemensystem_deposit_max_amount ?? 100000}
                      onChange={(e) => setS({
                        ...s,
                        hemensystem_deposit_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Yerel yatırım süresi 10 dakikadır; süresi dolan bekleyen
                  talep otomatik iptal edilir.
                </p>
              </section>
              <section className="provider-money is-withdraw">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.hemensystem_withdraw_enabled !== false}
                    onChange={(e) => setS({
                      ...s, hemensystem_withdraw_enabled: e.target.checked,
                    })}
                  />
                  Hemen Havale çekim açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum çekim (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.hemensystem_withdraw_min_amount ?? 100}
                      onChange={(e) => setS({
                        ...s,
                        hemensystem_withdraw_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum çekim (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.hemensystem_withdraw_max_amount ?? 100000}
                      onChange={(e) => setS({
                        ...s,
                        hemensystem_withdraw_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Genel minimum çekim (
                  {Number(s.withdraw_min_amount ?? 100).toLocaleString('tr-TR')} ₺)
                  bu tutarın üzerine biner: oyuncunun görebileceği alt sınır
                  ikisinin büyüğü olan{' '}
                  <span className="font-semibold text-slate-300">
                    {Math.max(
                      Number(s.withdraw_min_amount ?? 100) || 0,
                      Number(s.hemensystem_withdraw_min_amount ?? 100) || 0,
                    ).toLocaleString('tr-TR')} ₺
                  </span>
                  {' '}olur.
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Çekim yalnızca IBAN'a yapılır ve talep önce
                  <strong> yönetici onayından </strong> geçer; onay talebi
                  sağlayıcıya açar, ödemeyi tamamlamaz.
                </p>
              </section>
            </div>
            {/* Kart akışı aynı bayi hesabına bağlıdır; ayrı bir entegrasyon
                değildir. Kendi anahtarı ve limitleri vardır, çekimi yoktur. */}
            <section
              className="col-span-full rounded-xl p-4"
              style={{
                background: 'rgba(99,102,241,0.06)',
                border: '1px solid rgba(99,102,241,0.22)',
              }}
            >
              <label className="flex items-center gap-2 text-sm font-bold text-white">
                <input
                  type="checkbox"
                  checked={!!s.hemensystem_creditcard_enabled}
                  onChange={(e) => setS({
                    ...s, hemensystem_creditcard_enabled: e.target.checked,
                  })}
                />
                Hemen Havale kredi kartı (3D Secure) yatırımı açık
              </label>
              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="settings-inline-field">
                  Minimum kart yatırımı (₺)
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={s.hemensystem_creditcard_min_amount ?? 100}
                    onChange={(e) => setS({
                      ...s,
                      hemensystem_creditcard_min_amount: Number(e.target.value),
                    })}
                    className="admin-input mt-1"
                  />
                </label>
                <label className="settings-inline-field">
                  Maksimum kart yatırımı (₺)
                  <input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={s.hemensystem_creditcard_max_amount ?? 100000}
                    onChange={(e) => setS({
                      ...s,
                      hemensystem_creditcard_max_amount: Number(e.target.value),
                    })}
                    className="admin-input mt-1"
                  />
                </label>
              </div>
              <p className="mt-2 text-xs text-slate-500">
                Aynı bayi hesabının ikinci akışıdır: kimlik, website_id ve
                callback adresi havaleyle ortaktır. Oyuncu ekranında
                <strong> Kredi Kartı </strong> bölümünde ayrı bir yöntem olarak
                görünür; kart bilgileri sağlayıcının 3D sayfasında girilir,
                bizim sunucumuzdan geçmez. Karta çekim yapılamaz.
              </p>
              {!(s.hemensystem_api_secret || s.hemensystem_api_secret_configured) && (
                <p className="mt-2 text-xs text-amber-300/90">
                  Kart akışı <strong>api secret olmadan açılamaz</strong>: kart
                  sonuç bildiriminde havalenin gövde hash&apos;i yoktur, tek
                  kimlik kanıtı imzadır ve sağlayıcı imzayı yalnızca secret
                  tanımlı hesaplara gönderir. Secret&apos;ı aşağıdaki API
                  bağlantı bilgileri bölümüne girin.
                </p>
              )}
              <p className="mt-2 text-xs text-slate-500">
                Redirect / Direkt Kart seçimi sağlayıcının kendi panelinde
                website_id başına yapılır; burada bir karşılığı yoktur.
              </p>
            </section>
            <div
              className="col-span-full mt-1 overflow-hidden rounded-xl"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--v2-line, rgba(255,255,255,0.08))',
              }}
            >
              <button
                type="button"
                onClick={() => setHemenApiOpen((value) => !value)}
                aria-expanded={hemenApiOpen}
                className="flex w-full items-start gap-2 px-4 py-3 text-left"
              >
                <ChevronDown
                  size={16}
                  className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${hemenApiOpen ? '' : '-rotate-90'}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">
                    API bağlantı bilgileri
                  </div>
                  {hemenApiOpen ? (
                    <div className="mt-1 text-xs text-slate-500">
                      API tipi, bayi hesabı, website_id, callback ve yönlendirme
                      adresleri.
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">
                      {s.hemensystem_email && s.hemensystem_password ? (
                        'Bayi bilgileri girilmiş — düzenlemek için tıklayın.'
                      ) : (
                        <span className="text-amber-300/90">
                          Bayi e-postası veya şifresi girilmemiş — girmek için
                          tıklayın.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
              {hemenApiOpen && (
              <div
                className="grid grid-cols-1 gap-3 px-4 pb-4 pt-4 md:grid-cols-2"
                style={{ borderTop: '1px solid var(--v2-line, rgba(255,255,255,0.06))' }}
              >
                <F l="API URL" full>
                  <input
                    value={s.hemensystem_api_url || ''}
                    onChange={(e) => setS({
                      ...s, hemensystem_api_url: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="https://hemensystem.com/api"
                  />
                </F>
                <F l="API tipi">
                  <select
                    value={s.hemensystem_api_type || '1'}
                    onChange={(e) => setS({
                      ...s, hemensystem_api_type: e.target.value,
                    })}
                    className="admin-input"
                  >
                    <option value="1">Tip 1 — e-posta + şifre</option>
                    <option value="2">Tip 2 — HMAC imza + IP izin listesi</option>
                  </select>
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Tipi bayi hesabınız belirler. Tip 2'de her istek imzalanır
                    ve sunucunun çıkış IP adresi sağlayıcıya bildirilmiş
                    olmalıdır.
                  </div>
                </F>
                <F l="Bayi e-postası">
                  <input
                    value={s.hemensystem_email || ''}
                    onChange={(e) => setS({
                      ...s, hemensystem_email: e.target.value,
                    })}
                    className="admin-input"
                    autoComplete="off"
                  />
                </F>
                <F l="Bayi şifresi">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.hemensystem_password || ''}
                    onChange={(e) => setS({
                      ...s, hemensystem_password: e.target.value,
                    })}
                    className="admin-input"
                  />
                </F>
                <F l="API Secret (Tip 2 ve kredi kartı)">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.hemensystem_api_secret || ''}
                    onChange={(e) => setS({
                      ...s, hemensystem_api_secret: e.target.value,
                    })}
                    className="admin-input"
                    // Kart akışı Tip 1 hesapta da secret ister: sonuç
                    // bildiriminin tek kimlik kanıtı imzadır. Alan bu yüzden
                    // yalnızca ikisi de kapalıyken kilitlenir.
                    disabled={
                      (s.hemensystem_api_type || '1') !== '2'
                      && !s.hemensystem_creditcard_enabled
                    }
                  />
                  <div className="mt-1 text-[11px] text-slate-500">
                    Tip 2&apos;de istek ve callback imzasının anahtarıdır;
                    kredi kartı akışında tipten bağımsız zorunludur.
                  </div>
                </F>
                <F l="website_id">
                  <div className="flex gap-2">
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={s.hemensystem_website_id ?? 0}
                      onChange={(e) => setS({
                        ...s,
                        hemensystem_website_id: Number(e.target.value),
                      })}
                      className="admin-input"
                      readOnly={websiteIdLocked}
                      title={websiteIdLocked
                        ? 'Numara sabitlendi; değiştirmek için kilidi açın'
                        : undefined}
                    />
                    {websiteIdLocked ? (
                      <button
                        type="button"
                        onClick={unlockWebsiteId}
                        className="admin-btn-secondary shrink-0 px-3 text-xs"
                      >
                        Değiştir
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setS({
                          ...s, hemensystem_website_id: generateWebsiteId(),
                        })}
                        className="admin-btn-secondary shrink-0 px-3 text-xs"
                      >
                        Üret
                      </button>
                    )}
                  </div>
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Sağlayıcı bu numarayı vermez ve
                    {' '}<strong>doğrulamaz</strong>: her sitenin kendine özgü,
                    başka hiçbir müşteride bulunmayan bir sayısı olmalıdır.
                    Yanlış bir numara hatasız kabul edilir ve işlemler sessizce
                    başka bir sitenin altına yazılır. <strong>Üret</strong>
                    {' '}rastgele benzersiz bir numara verir; kaydedildikten
                    sonra <strong>sabitlenir</strong>.
                    {websiteIdLocked && (
                      <span className="text-amber-300/90">
                        {' '}Değiştirmek sağlayıcıdaki işlem geçmişini bu
                        siteden koparır — yalnızca gerçekten yeni bir kurulum
                        için yapın.
                      </span>
                    )}
                  </div>
                </F>
                <CallbackField
                  label="Callback URL"
                  path="/api/hemensystem/callback"
                  note="Her yatırım ve çekim isteğinde zorunlu olarak gönderilir ve Hemen Havale paneline de yazılmalıdır. Sağlayıcı 10 saniye içinde 2xx bekler; doğrulanamayan bildirim reddedilir ve iki dakika arayla beş kez tekrar denenir."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
              </div>
              )}
            </div>
            {hemenStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: hemenStatus.connected
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${hemenStatus.connected
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={hemenStatus.connected ? 'text-emerald-300' : 'text-red-300'}>
                  {hemenStatus.connected
                    ? 'Kimlik bilgileri kabul edildi (test isteği doğrulama hatasıyla döndü — beklenen).'
                    : 'Kimlik doğrulanamadı.'}
                </span>
                {hemenStatus.ip_allowed === false && (
                  <span className="ml-2 text-amber-300">
                    Sunucu IP adresi izin listesinde değil — yatırım/çekim açılmaz.
                  </span>
                )}
                {(hemenStatus.error || hemenStatus.message) && (
                  <span className="ml-2 text-slate-400">
                    {hemenStatus.error || hemenStatus.message}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={testHemenSystem}
              disabled={busy || canSave || !s.hemensystem_enabled}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
              title={canSave ? 'Önce değişiklikleri kaydedin' : undefined}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Hemen Havale Bağlantısını Test Et
            </button>
            </>
            )}

            {financeSection === 'hera' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">Hera</h3>
              <p className="mt-1 text-xs text-slate-400">
                Havale/EFT, kart-kart ve kredi kartı yatırımı ile IBAN&apos;a
                çekim. Havalede oyuncuya özel IBAN doğrudan bizim ekranımızda
                gösterilir; kart akışları sağlayıcının ödeme sayfasında
                tamamlanır. Sonuç sağlayıcının callback bildirimiyle
                kesinleşir.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!s.hera_enabled}
                  onChange={(e) => setS({
                    ...s, hera_enabled: e.target.checked,
                  })}
                />
                Hera entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için API anahtarı, secret, callback adresi ve
                <strong> bildirim adresinin sağlayıcı paneline tanımlandığı
                onayı</strong> zorunludur.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.hera_deposit_enabled !== false}
                    onChange={(e) => setS({
                      ...s, hera_deposit_enabled: e.target.checked,
                    })}
                  />
                  Hera yatırım açık
                </label>
                <SharedLimitFields
                  minLabel="Minimum yatırım (₺)"
                  maxLabel="Maksimum yatırım (₺)"
                  minValue={s.hera_deposit_min_amount ?? 100}
                  maxValue={s.hera_deposit_max_amount ?? 100000}
                  onMinChange={(value) => setS({ ...s, hera_deposit_min_amount: value })}
                  onMaxChange={(value) => setS({ ...s, hera_deposit_max_amount: value })}
                  provider="hera"
                  direction="deposit"
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                <div className="mt-3 space-y-2">
                  <div className="text-xs font-semibold text-slate-300">
                    Açık yatırım yöntemleri
                  </div>
                  <MethodLimitPicker
                    options={HERA_DEPOSIT_METHODS}
                    selected={heraDepositMethods}
                    onToggle={toggleHeraMethod}
                    provider="hera"
                    direction="deposit"
                    sharedMin={s.hera_deposit_min_amount ?? 100}
                    sharedMax={s.hera_deposit_max_amount ?? 100000}
                    limits={s.payment_method_limits}
                    onLimitsChange={setMethodLimits}
                  />
                  {!heraDepositMethods.length && (
                    <p className="text-[11px] text-amber-300/90">
                      Hiçbir yöntem seçilmedi; kayıtta havale açık kabul
                      edilir.
                    </p>
                  )}
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Hera talebi 10 dakika bekler; mevcut bekleyen kayıt yeni
                  talebi sitede engellemez, istek sağlayıcıya gönderilir.
                  Sağlayıcı yine de unfinished_transaction döndürebilir ve
                  15 dakikada en fazla iki denemeye izin verir. Kredi kartında
                  alt sınır her hâlükârda 500 ₺&apos;dir.
                </p>
              </section>
              <section className="provider-money is-withdraw">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.hera_withdraw_enabled !== false}
                    onChange={(e) => setS({
                      ...s, hera_withdraw_enabled: e.target.checked,
                    })}
                  />
                  Hera çekim açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum çekim (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.hera_withdraw_min_amount ?? 100}
                      onChange={(e) => setS({
                        ...s,
                        hera_withdraw_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum çekim (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.hera_withdraw_max_amount ?? 100000}
                      onChange={(e) => setS({
                        ...s,
                        hera_withdraw_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  Genel minimum çekim (
                  {Number(s.withdraw_min_amount ?? 100).toLocaleString('tr-TR')} ₺)
                  bu tutarın üzerine biner: oyuncunun görebileceği alt sınır
                  ikisinin büyüğü olan{' '}
                  <span className="font-semibold text-slate-300">
                    {Math.max(
                      Number(s.withdraw_min_amount ?? 100) || 0,
                      Number(s.hera_withdraw_min_amount ?? 100) || 0,
                    ).toLocaleString('tr-TR')} ₺
                  </span>
                  {' '}olur.
                </p>
                <p className="mt-2 text-xs text-slate-500">
                  Çekim yalnızca IBAN&apos;a yapılır; hedef banka IBAN&apos;dan
                  otomatik çözülür. Talep önce{' '}
                  <strong>yönetici onayından</strong> geçer — onay talebi
                  sağlayıcıya açar, ödemeyi tamamlamaz.
                </p>
              </section>
            </div>
            <div
              className="col-span-full mt-1 overflow-hidden rounded-xl"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--v2-line, rgba(255,255,255,0.08))',
              }}
            >
              <button
                type="button"
                onClick={() => setHeraApiOpen((value) => !value)}
                aria-expanded={heraApiOpen}
                className="flex w-full items-start gap-2 px-4 py-3 text-left"
              >
                <ChevronDown
                  size={16}
                  className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${heraApiOpen ? '' : '-rotate-90'}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">
                    API bağlantı bilgileri
                  </div>
                  {heraApiOpen ? (
                    <div className="mt-1 text-xs text-slate-500">
                      API adresi, anahtar/secret, dönüş ve bildirim adresi.
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">
                      {s.hera_api_key && s.hera_api_secret ? (
                        'API bilgileri girilmiş — düzenlemek için tıklayın.'
                      ) : (
                        <span className="text-amber-300/90">
                          API anahtarı veya secret girilmemiş — girmek için
                          tıklayın.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
              {heraApiOpen && (
              <div
                className="grid grid-cols-1 gap-3 px-4 pb-4 pt-4 md:grid-cols-2"
                style={{ borderTop: '1px solid var(--v2-line, rgba(255,255,255,0.06))' }}
              >
                <F l="API URL" full>
                  <input
                    value={s.hera_api_url || ''}
                    onChange={(e) => setS({
                      ...s, hera_api_url: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="https://havalepayhera.com/api"
                  />
                </F>
                <F l="API Key">
                  <input
                    value={s.hera_api_key || ''}
                    onChange={(e) => setS({
                      ...s, hera_api_key: e.target.value,
                    })}
                    className="admin-input"
                    autoComplete="off"
                  />
                </F>
                <F l="API Secret">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.hera_api_secret || ''}
                    onChange={(e) => setS({
                      ...s, hera_api_secret: e.target.value,
                    })}
                    className="admin-input"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Callback imzası bu secret ile üretilir; değişirse
                    sağlayıcıdan gelen bildirimler doğrulanamaz.
                  </div>
                </F>
                <CallbackField
                  label="Callback URL"
                  path="/api/hera/callback"
                  note="Yatırım ve çekim bildirimleri aynı adrese düşer. Bu adres istekte gönderilmez: Hera panelinde yatırım ve çekim için ayrı ayrı tanımlanmalıdır."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
                <div
                  className="col-span-full rounded-lg p-3.5"
                  style={{
                    background: 'rgba(251,191,36,0.07)',
                    border: '1px solid rgba(251,191,36,0.25)',
                  }}
                >
                  <label className="flex items-start gap-2 text-sm font-semibold text-white">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!s.hera_callback_registered}
                      onChange={(e) => setS({
                        ...s, hera_callback_registered: e.target.checked,
                      })}
                    />
                    Bu adresi Hera paneline (yatırım ve çekim için ayrı ayrı)
                    tanımladım
                  </label>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    Bildirim adresi <strong>istekte gönderilmez</strong>;
                    sağlayıcının kendi panelinde tanımlıdır. Tanımlanmazsa
                    oyuncu parayı yatırır ve bakiyesi hiç artmaz — bu yüzden
                    entegrasyon onay verilmeden açılamaz. Callback adresini
                    değiştirirseniz onay kendiliğinden düşer.
                  </p>
                </div>
              </div>
              )}
            </div>
            {heraStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: heraStatus.connected
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${heraStatus.connected
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={heraStatus.connected ? 'text-emerald-300' : 'text-red-300'}>
                  {heraStatus.connected
                    ? `Kimlik bilgileri kabul edildi (${heraStatus.banks ?? 0} aktif banka).`
                    : 'Kimlik doğrulanamadı.'}
                </span>
                {(heraStatus.error || heraStatus.message) && (
                  <span className="ml-2 text-slate-400">
                    {heraStatus.error || heraStatus.message}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={testHera}
              disabled={busy || canSave || !s.hera_enabled}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
              title={canSave ? 'Önce değişiklikleri kaydedin' : undefined}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Hera Bağlantısını Test Et
            </button>
            </>
            )}

            {financeSection === 'payurus' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">Payurus</h3>
              <p className="mt-1 text-xs text-slate-400">
                Yatırım, sağlayıcının kendi ödeme sayfasında (CepPay)
                tamamlanır; oyuncu oraya yönlendirilir ve sonuç callback ile
                kesinleşir. Çekim IBAN&apos;a değil, oyuncunun
                <strong> CepPay cüzdan numarasına </strong> yapılır.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!s.payurus_enabled}
                  onChange={(e) => setS({
                    ...s, payurus_enabled: e.target.checked,
                  })}
                />
                Payurus entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için API adresi, secret, callback adresi ve
                <strong> bildirim adresinin sağlayıcı paneline tanımlandığı
                onayı</strong> zorunludur.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.payurus_deposit_enabled !== false}
                    onChange={(e) => setS({
                      ...s, payurus_deposit_enabled: e.target.checked,
                    })}
                  />
                  Payurus yatırım açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum yatırım (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.payurus_deposit_min_amount ?? 100}
                      onChange={(e) => setS({
                        ...s,
                        payurus_deposit_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum yatırım (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.payurus_deposit_max_amount ?? 100000}
                      onChange={(e) => setS({
                        ...s,
                        payurus_deposit_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Sağlayıcı <strong>iptal bildirimi göndermez</strong>:
                  ödenmeyen talep 30 dakika sonra kendiliğinden kapatılır. Geç
                  gelen bir onay bildirimi o kaydı yine kredileyebilir.
                </p>
              </section>
              <section className="provider-money is-withdraw">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.payurus_withdraw_enabled !== false}
                    onChange={(e) => setS({
                      ...s, payurus_withdraw_enabled: e.target.checked,
                    })}
                  />
                  Payurus çekim açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum çekim (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.payurus_withdraw_min_amount ?? 100}
                      onChange={(e) => setS({
                        ...s,
                        payurus_withdraw_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum çekim (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.payurus_withdraw_max_amount ?? 100000}
                      onChange={(e) => setS({
                        ...s,
                        payurus_withdraw_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Oyuncu çekim ekranında IBAN yerine
                  <strong> CepPay cüzdan numarasını </strong> girer; ad soyad
                  hesabın kayıtlı adından gider. Talep önce
                  <strong> yönetici onayından </strong> geçer. Sağlayıcı
                  <strong> aynı cüzdana saatte yalnızca bir çekim </strong>
                  kabul eder; reddedilen talebin tutarı oyuncuya iade edilir.
                </p>
              </section>
            </div>
            <div
              className="col-span-full mt-1 overflow-hidden rounded-xl"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--v2-line, rgba(255,255,255,0.08))',
              }}
            >
              <button
                type="button"
                onClick={() => setPayurusApiOpen((value) => !value)}
                aria-expanded={payurusApiOpen}
                className="flex w-full items-start gap-2 px-4 py-3 text-left"
              >
                <ChevronDown
                  size={16}
                  className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${payurusApiOpen ? '' : '-rotate-90'}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">
                    API bağlantı bilgileri
                  </div>
                  {payurusApiOpen ? (
                    <div className="mt-1 text-xs text-slate-500">
                      API adresi, secret, yönlendirme kipi ve callback adresi.
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">
                      {s.payurus_secret || initial?.payurus_secret_configured ? (
                        'Secret girilmiş — düzenlemek için tıklayın.'
                      ) : (
                        <span className="text-amber-300/90">
                          Secret girilmemiş — girmek için tıklayın.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
              {payurusApiOpen && (
              <div
                className="grid grid-cols-1 gap-3 px-4 pb-4 pt-4 md:grid-cols-2"
                style={{ borderTop: '1px solid var(--v2-line, rgba(255,255,255,0.06))' }}
              >
                <F l="API URL" full>
                  <input
                    value={s.payurus_api_url || ''}
                    onChange={(e) => setS({
                      ...s, payurus_api_url: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="https://api.ceppay.me/api"
                  />
                </F>
                <F l="Secret">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.payurus_secret || ''}
                    onChange={(e) => setS({
                      ...s, payurus_secret: e.target.value,
                    })}
                    className="admin-input"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Tek kimlik bilgisidir: giden isteklerin gövdesine yazılır ve
                    gelen bildirimin <code>token</code> alanı da bununla
                    doğrulanır. Değişirse callback&apos;ler doğrulanamaz.
                  </div>
                </F>
                <F l="Ödeme sonrası yönlendirme">
                  <select
                    value={s.payurus_redirect_mode || 'auto'}
                    onChange={(e) => setS({
                      ...s, payurus_redirect_mode: e.target.value,
                    })}
                    className="admin-input"
                  >
                    <option value="auto">Otomatik (sayfa kendi döner)</option>
                    <option value="manual">Elle (oyuncu düğmeye basar)</option>
                  </select>
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Ödeme bittiğinde oyuncunun yatırım geçmişine dönüş biçimi.
                    Dönüş adresi site alan adından türetilir.
                  </div>
                </F>
                <CallbackField
                  label="Callback URL"
                  path="/api/payurus/callback"
                  note="Yatırım ve çekim bildirimleri aynı adrese düşer. Bu adres istekte gönderilmez: Payurus panelinde tanımlanmalıdır."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
                <div
                  className="col-span-full rounded-lg p-3.5"
                  style={{
                    background: 'rgba(251,191,36,0.07)',
                    border: '1px solid rgba(251,191,36,0.25)',
                  }}
                >
                  <label className="flex items-start gap-2 text-sm font-semibold text-white">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!s.payurus_callback_registered}
                      onChange={(e) => setS({
                        ...s, payurus_callback_registered: e.target.checked,
                      })}
                    />
                    Bu adresi Payurus paneline tanımladım
                  </label>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    Bildirim adresi <strong>istekte gönderilmez</strong>;
                    sağlayıcının kendi panelinde tanımlıdır. Tanımlanmazsa
                    oyuncu parayı yatırır ve bakiyesi hiç artmaz — bu yüzden
                    entegrasyon onay verilmeden açılamaz. Callback adresini
                    değiştirirseniz onay kendiliğinden düşer.
                  </p>
                </div>
              </div>
              )}
            </div>
            {payurusStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: payurusStatus.connected
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${payurusStatus.connected
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={payurusStatus.connected ? 'text-emerald-300' : 'text-red-300'}>
                  {payurusStatus.connected
                    ? 'Secret kabul edildi (sorgulanan test işlemi sağlayıcıda bulunamadı — beklenen).'
                    : 'Secret doğrulanamadı.'}
                </span>
                {(payurusStatus.error || payurusStatus.message) && (
                  <span className="ml-2 text-slate-400">
                    {payurusStatus.error || payurusStatus.message}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={testPayurus}
              disabled={busy || canSave || !s.payurus_enabled}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
              title={canSave ? 'Önce değişiklikleri kaydedin' : undefined}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Payurus Bağlantısını Test Et
            </button>
            </>
            )}

            {financeSection === 'ultrapay' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">UltraPay</h3>
              <p className="mt-1 text-xs text-slate-400">
                Havale, Papara, kart ve kripto yöntemlerini tek entegrasyonda
                toplar. <strong>H2H Havale</strong> alıcı IBAN&apos;ı doğrudan
                cevapta döndürür ve kendi ekranımızda gösterilir; kalan
                yöntemlerde oyuncu sağlayıcının ödeme sayfasına yönlenir.
                Sonuç her iki yönde de callback ile kesinleşir.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!s.ultrapay_enabled}
                  onChange={(e) => setS({
                    ...s, ultrapay_enabled: e.target.checked,
                  })}
                />
                UltraPay entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için API adresi, api anahtarı, api secret,
                callback adresi ve <strong>en az bir açık yöntem</strong>
                {' '}zorunludur. Sağlayıcı <strong>IP beyaz listesi</strong>
                {' '}uygular: sunucunun çıkış IP&apos;si UltraPay panelinde
                ekli değilse anahtar doğru olsa bile istek reddedilir.
              </p>
              <p className="mt-2 text-xs leading-5 text-amber-300/90">
                Tutarlar <strong>tam sayı</strong> olmalıdır; sağlayıcı kuruş
                kabul etmez ve kuruşlu talep oyuncuya hata olarak döner.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.ultrapay_deposit_enabled !== false}
                    onChange={(e) => setS({
                      ...s, ultrapay_deposit_enabled: e.target.checked,
                    })}
                  />
                  UltraPay yatırım açık
                </label>
                <SharedLimitFields
                  minLabel="Minimum yatırım (₺)"
                  maxLabel="Maksimum yatırım (₺)"
                  minValue={s.ultrapay_deposit_min_amount ?? 100}
                  maxValue={s.ultrapay_deposit_max_amount ?? 100000}
                  onMinChange={(value) => setS({ ...s, ultrapay_deposit_min_amount: value })}
                  onMaxChange={(value) => setS({ ...s, ultrapay_deposit_max_amount: value })}
                  provider="ultrapay"
                  direction="deposit"
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                <div className="mt-3 text-xs font-semibold text-white">
                  Açık yatırım yöntemleri
                </div>
                <MethodLimitPicker
                  options={ultrapayOptions}
                  selected={ultrapayDepositCodes}
                  onToggle={(code) => toggleUltraPayMethod(
                    'ultrapay_deposit_methods', code)}
                  provider="ultrapay"
                  direction="deposit"
                  sharedMin={s.ultrapay_deposit_min_amount ?? 100}
                  sharedMax={s.ultrapay_deposit_max_amount ?? 100000}
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                {!ultrapayDepositCodes.length && (
                  <p className="mt-2 text-[11px] text-amber-300/90">
                    Hiçbir yöntem seçilmedi; yatırım ekranında UltraPay
                    görünmez.
                  </p>
                )}
              </section>
              <section className="provider-money is-alt">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.ultrapay_withdraw_enabled !== false}
                    onChange={(e) => setS({
                      ...s, ultrapay_withdraw_enabled: e.target.checked,
                    })}
                  />
                  UltraPay çekim açık
                </label>
                <SharedLimitFields
                  minLabel="Minimum çekim (₺)"
                  maxLabel="Maksimum çekim (₺)"
                  minValue={s.ultrapay_withdraw_min_amount ?? 100}
                  maxValue={s.ultrapay_withdraw_max_amount ?? 100000}
                  onMinChange={(value) => setS({ ...s, ultrapay_withdraw_min_amount: value })}
                  onMaxChange={(value) => setS({ ...s, ultrapay_withdraw_max_amount: value })}
                  provider="ultrapay"
                  direction="withdrawal"
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                <div className="mt-3 text-xs font-semibold text-white">
                  Açık çekim yöntemleri
                </div>
                <MethodLimitPicker
                  options={ultrapayOptions.filter((item) => item.withdraw)}
                  selected={ultrapayWithdrawCodes}
                  onToggle={(code) => toggleUltraPayMethod(
                    'ultrapay_withdraw_methods', code)}
                  provider="ultrapay"
                  direction="withdrawal"
                  sharedMin={s.ultrapay_withdraw_min_amount ?? 100}
                  sharedMax={s.ultrapay_withdraw_max_amount ?? 100000}
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                {ultrapayWithdrawCodes.includes(4) && (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-white">
                      Kripto çekiminde açık para birimi / ağ
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {ULTRAPAY_CRYPTO_PAIRS.map((pair) => (
                        <label
                          key={pair}
                          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] text-slate-300"
                          style={{
                            background: 'var(--v2-hover, rgba(255,255,255,0.04))',
                            border: '1px solid var(--v2-line, rgba(255,255,255,0.10))',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={ultrapayPairs.includes(pair)}
                            onChange={() => toggleUltraPayPair(pair)}
                          />
                          {pair.replace(':', ' · ')}
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] leading-4 text-amber-300/90">
                      Yalnızca bu ikililer geçerlidir. Yanlış ağa gönderilen
                      çekim <strong>geri alınamaz</strong>; her ikili çekim
                      ekranında ayrı bir seçenek olarak çıkar.
                    </p>
                  </div>
                )}
                <p className="mt-3 text-xs leading-5 text-slate-400">
                  Çekim talebi doğrudan sağlayıcıya gitmez, önce
                  <strong> yönetici onayından </strong> geçer. Havale
                  yöntemlerinde oyuncunun seçtiği banka, kalan yöntemlerde
                  yöntemin kendi adı sağlayıcıya hesap başlığı olarak
                  gönderilir.
                </p>
              </section>
            </div>
            <div
              className="col-span-full mt-1 overflow-hidden rounded-xl"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--v2-line, rgba(255,255,255,0.08))',
              }}
            >
              <button
                type="button"
                onClick={() => setUltrapayApiOpen((value) => !value)}
                aria-expanded={ultrapayApiOpen}
                className="flex w-full items-start gap-2 px-4 py-3 text-left"
              >
                <ChevronDown
                  size={16}
                  className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${ultrapayApiOpen ? '' : '-rotate-90'}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">
                    API bağlantı bilgileri
                  </div>
                  {ultrapayApiOpen ? (
                    <div className="mt-1 text-xs text-slate-500">
                      API adresi, api anahtarı, secret, referans ön eki ve
                      callback adresi.
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">
                      {s.ultrapay_api_secret
                        || initial?.ultrapay_api_secret_configured ? (
                          'Secret girilmiş — düzenlemek için tıklayın.'
                        ) : (
                          <span className="text-amber-300/90">
                            Secret girilmemiş — girmek için tıklayın.
                          </span>
                        )}
                    </div>
                  )}
                </div>
              </button>
              {ultrapayApiOpen && (
              <div
                className="grid grid-cols-1 gap-3 px-4 pb-4 pt-4 md:grid-cols-2"
                style={{ borderTop: '1px solid var(--v2-line, rgba(255,255,255,0.06))' }}
              >
                <F l="API URL" full>
                  <input
                    value={s.ultrapay_api_url || ''}
                    onChange={(e) => setS({
                      ...s, ultrapay_api_url: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="https://api.ultrafinans.com"
                  />
                </F>
                <F l="API Key">
                  <input
                    value={s.ultrapay_api_key || ''}
                    onChange={(e) => setS({
                      ...s, ultrapay_api_key: e.target.value,
                    })}
                    className="admin-input"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Her isteğin gövdesine <code>apikey</code> olarak yazılır.
                  </div>
                </F>
                <F l="API Secret">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.ultrapay_api_secret || ''}
                    onChange={(e) => setS({
                      ...s, ultrapay_api_secret: e.target.value,
                    })}
                    className="admin-input"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    İmza anahtarıdır:{' '}
                    <code>md5(site_user_id + site_transaction_id + secret)</code>.
                    Gelen bildirimler de bununla doğrulanır; değişirse
                    callback&apos;ler kabul edilmez.
                  </div>
                </F>
                <F l="Referans ön eki">
                  <input
                    value={s.ultrapay_transaction_prefix || ''}
                    onChange={(e) => setS({
                      ...s, ultrapay_transaction_prefix: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="BET"
                    maxLength={8}
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Sağlayıcı işlem referansının firma adının ilk 3 harfiyle
                    başlamasını ister. Sonradan değiştirilse bile eski
                    bildirimler çözülmeye devam eder.
                  </div>
                </F>
                <F l="Test modu">
                  <label className="flex items-center gap-2 text-sm text-slate-200">
                    <input
                      type="checkbox"
                      checked={!!s.ultrapay_test_mode}
                      onChange={(e) => setS({
                        ...s, ultrapay_test_mode: e.target.checked,
                      })}
                    />
                    İstekleri <code className="mx-1">istest=1</code> ile gönder
                  </label>
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Açıkken sağlayıcı işlemi test sayar. Canlıda kapalı
                    olmalıdır — açık unutulursa gerçek ödeme alınmaz.
                  </div>
                </F>
                <CallbackField
                  label="Callback URL"
                  path="/api/ultrapay/callback"
                  note="Yatırım ve çekim bildirimleri aynı adrese düşer. Adres her istekte sağlayıcıya gönderilir; kaydedildiği an geçerlidir."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
              </div>
              )}
            </div>
            {ultrapayStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: ultrapayStatus.connected
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${ultrapayStatus.connected
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={ultrapayStatus.connected ? 'text-emerald-300' : 'text-red-300'}>
                  {ultrapayStatus.connected
                    ? 'API anahtarı ve sunucu IP adresi kabul edildi.'
                    : 'Bağlantı doğrulanamadı (anahtar ya da IP beyaz listesi).'}
                </span>
                {(ultrapayStatus.error || ultrapayStatus.message) && (
                  <span className="ml-2 text-slate-400">
                    {ultrapayStatus.error || ultrapayStatus.message}
                  </span>
                )}
                {ultrapayStatus.available_balance !== undefined && (
                  <span className="ml-2 text-slate-400">
                    Kullanılabilir bakiye: {ultrapayStatus.available_balance}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={testUltraPay}
              disabled={busy || canSave || !s.ultrapay_enabled}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
              title={canSave ? 'Önce değişiklikleri kaydedin' : undefined}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              UltraPay Bağlantısını Test Et
            </button>
            </>
            )}

            {financeSection === 'linkhavale' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">Link Havale</h3>
              <p className="mt-1 text-xs text-slate-400">
                Havale/EFT, kredi kartı, Papara ve kripto yöntemlerini tek uçta
                toplar. Yatırım <strong>sağlayıcının kendi ödeme sayfasında</strong>
                {' '}tamamlanır (bağlantı 30 dakika geçerlidir, tek
                kullanımlıktır); çekim doğrudan IBAN&apos;a, Papara hesabına ya
                da kripto cüzdanına yapılır. Sonuç her iki yönde de callback
                ile kesinleşir.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!s.linkhavale_enabled}
                  onChange={(e) => setS({
                    ...s, linkhavale_enabled: e.target.checked,
                  })}
                />
                Link Havale entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için API adresi, api anahtarı, api secret,
                callback adresi, dönüş adresi ve
                <strong> en az bir açık yöntem </strong> zorunludur. Sağlayıcı
                <strong> IP beyaz listesi </strong> uygulayabilir: sunucunun
                çıkış IP&apos;si listede değilse anahtar doğru olsa bile istek
                sağlayıcının güvenlik katmanında engellenir.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.linkhavale_deposit_enabled !== false}
                    onChange={(e) => setS({
                      ...s, linkhavale_deposit_enabled: e.target.checked,
                    })}
                  />
                  Link Havale yatırım açık
                </label>
                <SharedLimitFields
                  minLabel="Minimum yatırım (₺)"
                  maxLabel="Maksimum yatırım (₺)"
                  minValue={s.linkhavale_deposit_min_amount ?? 100}
                  maxValue={s.linkhavale_deposit_max_amount ?? 100000}
                  onMinChange={(value) => setS({ ...s, linkhavale_deposit_min_amount: value })}
                  onMaxChange={(value) => setS({ ...s, linkhavale_deposit_max_amount: value })}
                  provider="linkhavale"
                  direction="deposit"
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                <div className="mt-3 text-xs font-semibold text-white">
                  Açık yatırım yöntemleri
                </div>
                <MethodLimitPicker
                  options={linkhavaleOptions}
                  selected={linkhavaleDepositSlugs}
                  onToggle={(slug) => toggleLinkHavaleMethod(
                    'linkhavale_deposit_methods', slug)}
                  provider="linkhavale"
                  direction="deposit"
                  sharedMin={s.linkhavale_deposit_min_amount ?? 100}
                  sharedMax={s.linkhavale_deposit_max_amount ?? 100000}
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Ödeme bağlantısı <strong>30 dakika</strong> geçerlidir ve
                  süresi dolan talep sağlayıcının bildirimiyle kapanır. Oyuncu
                  farklı bir tutar öderse bakiyeye
                  <strong> sağlayıcının bildirdiği tutar </strong> yazılır.
                </p>
              </section>
              <section className="provider-money is-withdraw">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.linkhavale_withdraw_enabled !== false}
                    onChange={(e) => setS({
                      ...s, linkhavale_withdraw_enabled: e.target.checked,
                    })}
                  />
                  Link Havale çekim açık
                </label>
                <SharedLimitFields
                  minLabel="Minimum çekim (₺)"
                  maxLabel="Maksimum çekim (₺)"
                  minValue={s.linkhavale_withdraw_min_amount ?? 100}
                  maxValue={s.linkhavale_withdraw_max_amount ?? 100000}
                  onMinChange={(value) => setS({ ...s, linkhavale_withdraw_min_amount: value })}
                  onMaxChange={(value) => setS({ ...s, linkhavale_withdraw_max_amount: value })}
                  provider="linkhavale"
                  direction="withdrawal"
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                <div className="mt-3 text-xs font-semibold text-white">
                  Açık çekim yöntemleri
                </div>
                <MethodLimitPicker
                  options={linkhavaleOptions.filter((item) => item.withdraw)}
                  selected={linkhavaleWithdrawSlugs}
                  onToggle={(slug) => toggleLinkHavaleMethod(
                    'linkhavale_withdraw_methods', slug)}
                  provider="linkhavale"
                  direction="withdrawal"
                  sharedMin={s.linkhavale_withdraw_min_amount ?? 100}
                  sharedMax={s.linkhavale_withdraw_max_amount ?? 100000}
                  limits={s.payment_method_limits}
                  onLimitsChange={setMethodLimits}
                />
                {linkhavaleWithdrawSlugs.includes('crypto') && (
                  <div className="mt-3">
                    <div className="text-xs font-semibold text-white">
                      Kripto çekiminde açık para birimi / ağ
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {LINKHAVALE_CRYPTO_PAIRS.map((pair) => (
                        <label
                          key={pair}
                          className="flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] text-slate-300"
                          style={{
                            background: 'var(--v2-hover, rgba(255,255,255,0.04))',
                            border: '1px solid var(--v2-line, rgba(255,255,255,0.10))',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={linkhavalePairs.includes(pair)}
                            onChange={() => toggleLinkHavalePair(pair)}
                          />
                          {pair.replace(':', ' · ')}
                        </label>
                      ))}
                    </div>
                    <p className="mt-2 text-[11px] leading-4 text-amber-300/90">
                      Sağlayıcı çekimde yalnızca <strong>ağı</strong> sorar;
                      liste bu yüzden USDT ile sınırlıdır. Yanlış ağa
                      gönderilen çekim <strong>geri alınamaz</strong> ve her
                      ikili çekim ekranında ayrı bir seçenek olarak çıkar.
                    </p>
                  </div>
                )}
                <p className="mt-3 text-xs leading-5 text-slate-400">
                  Çekim talebi doğrudan sağlayıcıya gitmez, önce
                  <strong> yönetici onayından </strong> geçer. Havalede hedef
                  banka oyuncunun kayıtlı bankasından çözülür, Papara&apos;da
                  hesap numarası istenir. Kredi kartına çekim yapılamaz.
                </p>
              </section>
            </div>
            <div
              className="col-span-full mt-1 overflow-hidden rounded-xl"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--v2-line, rgba(255,255,255,0.08))',
              }}
            >
              <button
                type="button"
                onClick={() => setLinkhavaleApiOpen((value) => !value)}
                aria-expanded={linkhavaleApiOpen}
                className="flex w-full items-start gap-2 px-4 py-3 text-left"
              >
                <ChevronDown
                  size={16}
                  className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${linkhavaleApiOpen ? '' : '-rotate-90'}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">
                    API bağlantı bilgileri
                  </div>
                  {linkhavaleApiOpen ? (
                    <div className="mt-1 text-xs text-slate-500">
                      API adresi, api anahtarı, secret ve callback adresi.
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">
                      {s.linkhavale_api_secret
                        || initial?.linkhavale_api_secret_configured ? (
                          'Secret girilmiş — düzenlemek için tıklayın.'
                        ) : (
                          <span className="text-amber-300/90">
                            Secret girilmemiş — girmek için tıklayın.
                          </span>
                        )}
                    </div>
                  )}
                </div>
              </button>
              {linkhavaleApiOpen && (
              <div
                className="grid grid-cols-1 gap-3 px-4 pb-4 pt-4 md:grid-cols-2"
                style={{ borderTop: '1px solid var(--v2-line, rgba(255,255,255,0.06))' }}
              >
                <F l="API URL" full>
                  <input
                    value={s.linkhavale_api_url || ''}
                    onChange={(e) => setS({
                      ...s, linkhavale_api_url: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="https://api.havale.link"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Taban adrestir; <code>/merchant-api/v1</code> ekini sistem
                    kendisi koyar. Test ortamı için
                    {' '}<code>https://sandbox.havale.link</code> yazın —
                    <strong> sandbox kimlik bilgileri ayrıdır</strong>.
                  </div>
                </F>
                <F l="API Key">
                  <input
                    value={s.linkhavale_api_key || ''}
                    onChange={(e) => setS({
                      ...s, linkhavale_api_key: e.target.value,
                    })}
                    className="admin-input"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Her isteğe <code>X-API-Key</code> başlığı olarak yazılır.
                  </div>
                </F>
                <F l="API Secret">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.linkhavale_api_secret || ''}
                    onChange={(e) => setS({
                      ...s, linkhavale_api_secret: e.target.value,
                    })}
                    className="admin-input"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    İmza anahtarıdır: giden istekler
                    {' '}<code>HMAC-SHA256(damga + metot + yol + gövde)</code>
                    {' '}ile, gelen bildirimler <code>X-LH-Signature</code>
                    {' '}başlığındaki gövde imzasıyla doğrulanır. Değişirse
                    callback&apos;ler kabul edilmez.
                  </div>
                </F>
                <CallbackField
                  label="Callback URL"
                  path="/api/linkhavale/callback"
                  note="Yatırım ve çekim bildirimleri aynı adrese düşer. Adres her istekte sağlayıcıya gönderilir; kaydedildiği an geçerlidir."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
              </div>
              )}
            </div>
            {linkhavaleStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: linkhavaleStatus.connected
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${linkhavaleStatus.connected
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={linkhavaleStatus.connected ? 'text-emerald-300' : 'text-red-300'}>
                  {linkhavaleStatus.connected
                    ? 'API anahtarı ve imza kabul edildi.'
                    : 'Bağlantı doğrulanamadı (anahtar, imza ya da IP beyaz listesi).'}
                </span>
                {(linkhavaleStatus.error || linkhavaleStatus.message) && (
                  <span className="ml-2 text-slate-400">
                    {linkhavaleStatus.error || linkhavaleStatus.message}
                  </span>
                )}
                {linkhavaleStatus.sandbox && (
                  <span className="ml-2 text-amber-300/90">
                    Sandbox adresi kullanılıyor.
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={testLinkHavale}
              disabled={busy || canSave || !s.linkhavale_enabled}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
              title={canSave ? 'Önce değişiklikleri kaydedin' : undefined}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Link Havale Bağlantısını Test Et
            </button>
            </>
            )}

            {financeSection === 'vevopay' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">VEVO PAY</h3>
              <p className="mt-1 text-xs text-slate-400">
                Havale/EFT entegrasyonu. Yatırımda sağlayıcı
                <strong> hem bu işleme özel IBAN&apos;ı hem de ödeme
                sayfasının adresini </strong> döndürür; ikisi de oyuncuya
                gösterilir ve otomatik yönlendirme yapılmaz. Çekim doğrudan
                oyuncunun IBAN&apos;ına yapılır ve önce
                <strong> yönetici onayından </strong> geçer. Sonuç her iki
                yönde de callback ile kesinleşir.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!s.vevopay_enabled}
                  onChange={(e) => setS({
                    ...s, vevopay_enabled: e.target.checked,
                  })}
                />
                VEVO PAY entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için API adresi, kanal anahtarı
                (<code>Client-Token</code>), callback secret
                (<code>Client-Secret</code>), callback adresi ve
                <strong> bildirim adresinin sağlayıcı kanal ayarlarına
                tanımlandığı onayı</strong> zorunludur.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.vevopay_deposit_enabled !== false}
                    onChange={(e) => setS({
                      ...s, vevopay_deposit_enabled: e.target.checked,
                    })}
                  />
                  VEVO PAY yatırım açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum yatırım (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.vevopay_deposit_min_amount ?? 100}
                      onChange={(e) => setS({
                        ...s,
                        vevopay_deposit_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum yatırım (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.vevopay_deposit_max_amount ?? 100000}
                      onChange={(e) => setS({
                        ...s,
                        vevopay_deposit_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <label className="mt-3 flex items-start gap-2 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!s.vevopay_hosted_deposit}
                    onChange={(e) => setS({
                      ...s, vevopay_hosted_deposit: e.target.checked,
                    })}
                  />
                  <span>
                    Gönderen adını sağlayıcının ödeme sayfası sorsun
                    <span className="block text-slate-500">
                      Kapalıyken üyenin KYC&apos;deki ad soyadı gönderilir ve
                      cevap IBAN&apos;ı da taşır. Ad kayıtlı değilse istek
                      zaten sayfaya düşer — havale eşleştirmesi gerçek gönderen
                      adına dayandığı için uydurma ad gönderilmez.
                    </span>
                  </span>
                </label>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Ödenmeyen talep sağlayıcının bildirdiği süre dolunca
                  kendiliğinden kapatılır; geç gelen bir onay bildirimi o
                  kaydı yine kredileyebilir. Bakiyeye
                  <strong> sağlayıcının bildirdiği tutar </strong> girer
                  (komisyon düşülmüş <code>credited</code> değeri değil).
                </p>
              </section>
              <section className="provider-money is-withdraw">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={s.vevopay_withdraw_enabled !== false}
                    onChange={(e) => setS({
                      ...s, vevopay_withdraw_enabled: e.target.checked,
                    })}
                  />
                  VEVO PAY çekim açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum çekim (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.vevopay_withdraw_min_amount ?? 100}
                      onChange={(e) => setS({
                        ...s,
                        vevopay_withdraw_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum çekim (₺)
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={s.vevopay_withdraw_max_amount ?? 100000}
                      onChange={(e) => setS({
                        ...s,
                        vevopay_withdraw_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Oyuncu kendi adına kayıtlı IBAN&apos;ı girer; talep
                  <strong> yönetici onayından </strong> sonra sağlayıcıya
                  iletilir. Reddedilen talebin tutarı oyuncuya iade edilir.
                </p>
              </section>
            </div>
            <div
              className="col-span-full mt-1 overflow-hidden rounded-xl"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid var(--v2-line, rgba(255,255,255,0.08))',
              }}
            >
              <button
                type="button"
                onClick={() => setVevopayApiOpen((value) => !value)}
                aria-expanded={vevopayApiOpen}
                className="flex w-full items-start gap-2 px-4 py-3 text-left"
              >
                <ChevronDown
                  size={16}
                  className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${vevopayApiOpen ? '' : '-rotate-90'}`}
                />
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white">
                    API bağlantı bilgileri
                  </div>
                  {vevopayApiOpen ? (
                    <div className="mt-1 text-xs text-slate-500">
                      API adresi, kanal anahtarı, callback secret ve ödeme
                      sayfası alan adı.
                    </div>
                  ) : (
                    <div className="mt-1 text-xs text-slate-500">
                      {(s.vevopay_client_token
                        || initial?.vevopay_client_token_configured)
                        && (s.vevopay_client_secret
                          || initial?.vevopay_client_secret_configured) ? (
                        'Anahtar ve secret girilmiş — düzenlemek için tıklayın.'
                      ) : (
                        <span className="text-amber-300/90">
                          Anahtar ya da secret girilmemiş — girmek için tıklayın.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </button>
              {vevopayApiOpen && (
              <div
                className="grid grid-cols-1 gap-3 px-4 pb-4 pt-4 md:grid-cols-2"
                style={{ borderTop: '1px solid var(--v2-line, rgba(255,255,255,0.06))' }}
              >
                <F l="API URL" full>
                  <input
                    value={s.vevopay_api_url || ''}
                    onChange={(e) => setS({
                      ...s, vevopay_api_url: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="https://panel.void.cfd"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Taban adrestir; <code>/merchant/api</code> ekini kod
                    kendisi koyar.
                  </div>
                </F>
                <F l="Client-Token (kanal anahtarı)">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.vevopay_client_token || ''}
                    onChange={(e) => setS({
                      ...s, vevopay_client_token: e.target.value,
                    })}
                    className="admin-input"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Giden isteklerin tek kimliğidir ve <b>imza yoktur</b>:
                    anahtarı bilen biri doğrudan çekim açabilir. Bu yüzden
                    değer panele geri dönmez; değiştirmek için yenisini yazın.
                  </div>
                </F>
                <F l="Client-Secret (callback imzası)">
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={s.vevopay_client_secret || ''}
                    onChange={(e) => setS({
                      ...s, vevopay_client_secret: e.target.value,
                    })}
                    className="admin-input"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Gelen bildirimin tek kanıtıdır:
                    {' '}<code>SHA256(secret|id|state_code)</code>
                    {' '}değeri <code>Client-Signature</code> başlığıyla
                    karşılaştırılır. Değişirse callback&apos;ler kabul edilmez.
                  </div>
                </F>
                <F l="Ödeme sayfası alan adı (isteğe bağlı)">
                  <input
                    value={s.vevopay_payment_domain || ''}
                    onChange={(e) => setS({
                      ...s, vevopay_payment_domain: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="pay.void.cfd"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Boş bırakılırsa istekte gönderilmez. Sağlayıcıda kayıtlı
                    olmayan bir adres <code>DOMAIN_INVALID</code> ile
                    reddedilir.
                  </div>
                </F>
                <CallbackField
                  label="Callback URL"
                  path="/api/vevopay/callback"
                  note="Yatırım ve çekim bildirimleri aynı adrese düşer. Bu adres istekte gönderilmez: VEVO PAY kanal ayarlarında tanımlanmalıdır."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
                <div
                  className="col-span-full rounded-lg p-3.5"
                  style={{
                    background: 'rgba(251,191,36,0.07)',
                    border: '1px solid rgba(251,191,36,0.25)',
                  }}
                >
                  <label className="flex items-start gap-2 text-sm font-semibold text-white">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!s.vevopay_callback_registered}
                      onChange={(e) => setS({
                        ...s, vevopay_callback_registered: e.target.checked,
                      })}
                    />
                    Bu adresi VEVO PAY kanal ayarlarına tanımladım
                  </label>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    Bildirim adresi <strong>istekte gönderilmez</strong>;
                    sağlayıcının kendi kanal ayarlarında tanımlıdır.
                    Tanımlanmazsa oyuncu parayı yatırır ve bakiyesi hiç artmaz
                    — bu yüzden entegrasyon onay verilmeden açılamaz. Callback
                    adresini, API adresini ya da kanal anahtarını
                    değiştirirseniz onay kendiliğinden düşer.
                  </p>
                </div>
              </div>
              )}
            </div>
            {vevopayStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: vevopayStatus.connected
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${vevopayStatus.connected
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={vevopayStatus.connected ? 'text-emerald-300' : 'text-red-300'}>
                  {vevopayStatus.connected
                    ? 'Kanal anahtarı kabul edildi (sorgulanan test işlemi sağlayıcıda bulunamadı — beklenen).'
                    : 'Kanal anahtarı doğrulanamadı.'}
                </span>
                {!vevopayStatus.connected
                  && (vevopayStatus.error || vevopayStatus.message) && (
                  <span className="ml-2 text-slate-400">
                    {vevopayStatus.error || vevopayStatus.message}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={testVevoPay}
              disabled={busy || canSave || !s.vevopay_enabled}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
              title={canSave ? 'Önce değişiklikleri kaydedin' : undefined}
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              VEVO PAY Bağlantısını Test Et
            </button>
            </>
            )}

            {financeSection === 'hizlicaparalar' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">HızlıcaParalar</h3>
              <p className="mt-1 text-xs text-slate-400">
                Havale/EFT, Havale FAST, kredi kartı ve kripto yatırımı;
                havale/EFT ve kripto çekimi. Yatırım
                <strong> tamamen sağlayıcının ödeme sayfasında </strong>
                tamamlanır ve <strong>tutar orada yazılır</strong> — isteğimiz
                tutar taşımaz, buradaki limitler yalnızca vitrini ve talebi
                sınırlar. Bakiyeye giren para sağlayıcının bildirdiği tutardır;
                oyuncu farklı bir tutar ödeyebilir. Çekim önce
                <strong> yönetici onayından </strong> geçer, sonuç her iki
                yönde de callback ile kesinleşir. Sağlayıcının
                <strong> durum sorgulama ucu yoktur</strong>: yatırım panelden
                karara bağlanamaz.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!hizlica.enabled}
                  onChange={(e) => setHizlica({
                    ...hizlica, enabled: e.target.checked,
                  })}
                />
                HızlıcaParalar entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için API adresi, API anahtarı, secret, callback
                adresi ve <strong>bildirim adresinin sağlayıcı paneline
                tanımlandığı onayı</strong> zorunludur.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={hizlica.deposit_enabled !== false}
                    onChange={(e) => setHizlica({
                      ...hizlica, deposit_enabled: e.target.checked,
                    })}
                  />
                  HızlıcaParalar yatırım açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum yatırım (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={hizlica.deposit_min_amount ?? 100}
                      onChange={(e) => setHizlica({
                        ...hizlica,
                        deposit_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum yatırım (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={hizlica.deposit_max_amount ?? 100000}
                      onChange={(e) => setHizlica({
                        ...hizlica,
                        deposit_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <div className="mt-3 space-y-1.5">
                  {HIZLICAPARALAR_METHODS.filter((m) => m.deposit).map((m) => (
                    <label
                      key={`dep-${m.slug}`}
                      className="flex items-start gap-2 text-xs text-slate-300"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={hizlicaparalarHasMethod(
                          hizlica.deposit_methods, m.slug)}
                        onChange={(e) => setHizlica({
                          ...hizlica,
                          deposit_methods: hizlicaparalarToggleMethod(
                            hizlica.deposit_methods, m.slug, e.target.checked),
                        })}
                      />
                      <span>
                        {m.name}
                        <span className="block text-slate-500">{m.note}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Ödenmeyen talep 10 dakika sonra kendiliğinden kapatılır; geç
                  gelen bir onay bildirimi o kaydı yine kredileyebilir.
                  Sağlayıcının kendi alt/üst sınırı buradaki değerlerin
                  <strong> üstüne biner</strong>.
                </p>
              </section>
              <section className="provider-money is-withdraw">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={hizlica.withdraw_enabled !== false}
                    onChange={(e) => setHizlica({
                      ...hizlica, withdraw_enabled: e.target.checked,
                    })}
                  />
                  HızlıcaParalar çekim açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum çekim (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={hizlica.withdraw_min_amount ?? 150}
                      onChange={(e) => setHizlica({
                        ...hizlica,
                        withdraw_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum çekim (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={hizlica.withdraw_max_amount ?? 100000}
                      onChange={(e) => setHizlica({
                        ...hizlica,
                        withdraw_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <div className="mt-3 space-y-1.5">
                  {HIZLICAPARALAR_METHODS.filter((m) => m.withdraw).map((m) => (
                    <label
                      key={`wit-${m.slug}`}
                      className="flex items-start gap-2 text-xs text-slate-300"
                    >
                      <input
                        type="checkbox"
                        className="mt-0.5"
                        checked={hizlicaparalarHasMethod(
                          hizlica.withdraw_methods, m.slug)}
                        onChange={(e) => setHizlica({
                          ...hizlica,
                          withdraw_methods: hizlicaparalarToggleMethod(
                            hizlica.withdraw_methods, m.slug, e.target.checked),
                        })}
                      />
                      <span>
                        {m.name}
                        <span className="block text-slate-500">{m.note}</span>
                      </span>
                    </label>
                  ))}
                </div>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Çekim talebi <strong>önce yönetici onayından</strong> geçer;
                  onay talebi sağlayıcıya açar, ödemeyi tamamlamaz. Kripto
                  çekimi yalnızca <strong>TRC20</strong> ağındadır — yanlış ağa
                  gönderilen transfer geri alınamaz.
                </p>
              </section>
            </div>
            <div className="col-span-full">
              <button
                type="button"
                onClick={() => setHizlicaApiOpen((open) => !open)}
                className="admin-btn-secondary w-full justify-between py-3"
              >
                <span>API bilgileri ve bildirim adresi</span>
                <span className="text-xs text-slate-400">
                  {hizlicaApiOpen ? 'gizle' : 'göster'}
                </span>
              </button>
              {hizlicaApiOpen && (
              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                <F l="API adresi">
                  <input
                    value={hizlica.api_url || ''}
                    onChange={(e) => setHizlica({
                      ...hizlica, api_url: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="https://apiws.extracuzdan.com"
                  />
                </F>
                <F l="API anahtarı (istek başlığının adı)">
                  <input
                    value={hizlica.api_key || ''}
                    onChange={(e) => setHizlica({
                      ...hizlica, api_key: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="apikey-69343b0e-..."
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Sağlayıcının verdiği anahtarı <strong>olduğu gibi</strong>
                    {' '}yapıştırın: <code>apikey-</code> öneki anahtarın
                    parçasıdır ve istek başlığının <strong>adı</strong> odur.
                    Önek kırpılırsa her istek <code>802</code>/<code>902</code>
                    {' '}(Api False Input) alır — unutulursa burada tamamlanır.
                  </div>
                </F>
                <F l="Secret (başlık değeri + callback imzası)">
                  <input
                    type="password"
                    value={hizlica.api_secret || ''}
                    onChange={(e) => setHizlica({
                      ...hizlica, api_secret: e.target.value,
                    })}
                    className="admin-input"
                    placeholder={hizlica.api_secret_configured
                      ? MASKED_SECRET : ''}
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Aynı değer iki iş yapar: giden isteğin başlık değeridir ve
                    gelen bildirimin tek kanıtı olan
                    {' '}<code>sha1(secret + referenceno)</code> imzasını
                    üretir. Panele geri dönmez; boş bırakılırsa kayıtlı değer
                    korunur.
                  </div>
                </F>
                <CallbackField
                  label="Callback URL"
                  path={HIZLICAPARALAR_CALLBACK_PATH}
                  note="Yatırım ve çekim bildirimleri aynı adrese düşer. Bu adres istekte gönderilmez: sağlayıcının kendi panelinde tanımlanmalıdır."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
                <div
                  className="col-span-full rounded-lg p-3.5"
                  style={{
                    background: 'rgba(251,191,36,0.07)',
                    border: '1px solid rgba(251,191,36,0.25)',
                  }}
                >
                  <label className="flex items-start gap-2 text-sm font-semibold text-white">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!hizlica.callback_registered}
                      onChange={(e) => setHizlica({
                        ...hizlica, callback_registered: e.target.checked,
                      })}
                    />
                    Bu adresi HızlıcaParalar paneline tanımladım
                  </label>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    Bildirim adresi <strong>istekte gönderilmez</strong>;
                    sağlayıcının kendi panelinde tanımlıdır ve yatırım ile
                    çekim <strong>aynı adresi</strong> kullanır. Tanımlanmazsa
                    oyuncu parayı yatırır ve bakiyesi hiç artmaz — bu yüzden
                    entegrasyon onay verilmeden açılamaz. Callback adresini,
                    API adresini ya da anahtarı değiştirirseniz onay
                    kendiliğinden düşer.
                  </p>
                </div>
                <div className="col-span-full flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={syncHizlicaBanks}
                    disabled={busy || !hizlica.enabled}
                    className="admin-btn-secondary py-2.5"
                  >
                    Banka listesini sağlayıcıdan al
                  </button>
                  <span className="text-xs text-slate-400">
                    Kayıtlı banka sayısı: {(hizlica.banks || []).length}
                    {' — '}
                    çekimde hedef banka sağlayıcının <strong>kendi
                    kodudur</strong>; liste boşken kod banka adından türetilir
                    ve tanınmazsa çekim <code>908</code> ile reddedilir.
                  </span>
                </div>
              </div>
              )}
            </div>
            {hizlicaStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: hizlicaStatus.ok
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${hizlicaStatus.ok
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={hizlicaStatus.ok ? 'text-emerald-300' : 'text-red-300'}>
                  {hizlicaStatus.ok
                    ? hizlicaStatus.message
                    : 'Bağlantı doğrulanamadı.'}
                </span>
                {!hizlicaStatus.ok && hizlicaStatus.error && (
                  <span className="ml-2 text-slate-400">
                    {hizlicaStatus.error}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={testHizlica}
              disabled={busy || !hizlica.enabled}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              HızlıcaParalar Bağlantısını Test Et
            </button>
            </>
            )}

            {financeSection === 'hizlihavale' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">Hızlı Havale</h3>
              <p className="mt-1 text-xs text-slate-400">
                Tek yöntemi <strong>havale/EFT</strong>'tir: yatırımda
                sağlayıcı işleme bir banka hesabı atar ve IBAN ekranımızda
                görünür, çekim doğrudan oyuncunun IBAN'ına yapılır. Çekim önce
                <strong> yönetici onayından </strong> geçer. Sağlayıcı
                bildirimi <strong>imzalamaz</strong>; bu yüzden sonucu her
                zaman <strong>durum sorgusu</strong> belirler — gelen bildirim
                yalnızca sorguyu tetikler ve bekleyen her kayıt arka planda da
                sorulur. Pratik sonucu: bildirim adresi yanlış tanımlansa bile
                para doğru yere gider, yalnızca gecikir.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!hizliHavale.enabled}
                  onChange={(e) => setHizliHavale({
                    ...hizliHavale, enabled: e.target.checked,
                  })}
                />
                Hızlı Havale entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için API adresi, <strong>bayi adı</strong>, API
                anahtarı, secret, callback adresi ve <strong>bildirim
                adresinin sağlayıcı paneline tanımlandığı onayı</strong>
                {' '}zorunludur.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={hizliHavale.deposit_enabled !== false}
                    onChange={(e) => setHizliHavale({
                      ...hizliHavale, deposit_enabled: e.target.checked,
                    })}
                  />
                  Hızlı Havale yatırım açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum yatırım (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={hizliHavale.deposit_min_amount ?? 100}
                      onChange={(e) => setHizliHavale({
                        ...hizliHavale,
                        deposit_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum yatırım (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={hizliHavale.deposit_max_amount ?? 100000}
                      onChange={(e) => setHizliHavale({
                        ...hizliHavale,
                        deposit_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <label className="mt-3 flex items-start gap-2 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!hizliHavale.iframe_deposit}
                    onChange={(e) => setHizliHavale({
                      ...hizliHavale, iframe_deposit: e.target.checked,
                    })}
                  />
                  <span>
                    Yatırımı <strong>sağlayıcının ödeme sayfasında</strong> aç
                    <span className="block text-slate-500">
                      Kapalıyken hesap bilgisi bizim ekranımızda çizilir
                      (varsayılan). Açıkken sağlayıcının sayfası açılır; o
                      akış ad soyad ister, bu yüzden adı kayıtlı olmayan üyede
                      kendiliğinden hesap bilgisine düşülür. Oturum
                      <strong> 30 dakikada </strong> dolar ve aynı üyenin açık
                      oturumu varken yeni talep açılmaz.
                    </span>
                  </span>
                </label>
              </section>
              <section className="provider-money is-withdraw">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={hizliHavale.withdraw_enabled !== false}
                    onChange={(e) => setHizliHavale({
                      ...hizliHavale, withdraw_enabled: e.target.checked,
                    })}
                  />
                  Hızlı Havale çekim açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum çekim (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={hizliHavale.withdraw_min_amount ?? 100}
                      onChange={(e) => setHizliHavale({
                        ...hizliHavale,
                        withdraw_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum çekim (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={hizliHavale.withdraw_max_amount ?? 100000}
                      onChange={(e) => setHizliHavale({
                        ...hizliHavale,
                        withdraw_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Çekim talebi <strong>önce yönetici onayından</strong> geçer;
                  onay talebi sağlayıcıya açar, ödemeyi tamamlamaz. Sağlayıcı
                  kasanız yetmiyorsa talebi <strong>oluşturur ama reddeder</strong>
                  {' '}(Balance Guard) — tutar o anda oyuncuya iade edilir.
                </p>
              </section>
            </div>
            <div className="col-span-full">
              <button
                type="button"
                onClick={() => setHizliHavaleApiOpen((open) => !open)}
                className="admin-btn-secondary w-full justify-between py-3"
              >
                <span>API bilgileri ve bildirim adresi</span>
                <span className="text-xs text-slate-400">
                  {hizliHavaleApiOpen ? 'gizle' : 'göster'}
                </span>
              </button>
              {hizliHavaleApiOpen && (
              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                <F l="API adresi">
                  <input
                    value={hizliHavale.api_url || ''}
                    onChange={(e) => setHizliHavale({
                      ...hizliHavale, api_url: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="https://api.hizli.finance"
                  />
                </F>
                <F l="Bayi adı (dealerName)">
                  <input
                    value={hizliHavale.dealer_name || ''}
                    onChange={(e) => setHizliHavale({
                      ...hizliHavale, dealer_name: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="yourmerchant"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Sağlayıcının size verdiği bayi kimliği. Bütün uçlar
                    {' '}<code>/dealer/&lt;bayi&gt;/...</code> yoluyla
                    çağrıldığı için bu değer <strong>adresin parçasıdır</strong>;
                    yanlış yazılırsa istek başka bir bayiye gider ya da 404
                    alır.
                  </div>
                </F>
                <F l="X-API-Key">
                  <input
                    value={hizliHavale.api_key || ''}
                    onChange={(e) => setHizliHavale({
                      ...hizliHavale, api_key: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="your-api-key"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Sağlayıcının panelindeki <strong>API Key</strong>. O panel
                    aynı değeri bir de <code>anahtar:secret</code> biçiminde
                    ("X-API-Key başlık değeri") verir; o satırı olduğu gibi
                    yapıştırırsanız kaydederken ikiye ayrılır ve secret alanını
                    ayrıca doldurmanız gerekmez.
                  </div>
                </F>
                <F l="X-API-Secret">
                  <input
                    type="password"
                    value={hizliHavale.api_secret || ''}
                    onChange={(e) => setHizliHavale({
                      ...hizliHavale, api_secret: e.target.value,
                    })}
                    className="admin-input"
                    placeholder={hizliHavale.api_secret_configured
                      ? MASKED_SECRET : ''}
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Giden isteklerin kimliğidir. Panele geri dönmez; boş
                    bırakılırsa kayıtlı değer korunur. Sağlayıcı merchant
                    başına <strong>IP beyaz listesi</strong> uygulayabilir —
                    anahtar doğru olsa da sunucunun çıkış IPv4 adresi ekli
                    değilse istek kenar katmanında durur.
                  </div>
                </F>
                <CallbackField
                  label="Callback URL"
                  path={HIZLIHAVALE_CALLBACK_PATH}
                  note="Yatırım ve çekim bildirimleri aynı adrese düşer. Bu adres istekte gönderilmez: sağlayıcının kendi panelinde tanımlanmalıdır."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
                <div
                  className="col-span-full rounded-lg p-3.5"
                  style={{
                    background: 'rgba(251,191,36,0.07)',
                    border: '1px solid rgba(251,191,36,0.25)',
                  }}
                >
                  <label className="flex items-start gap-2 text-sm font-semibold text-white">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={!!hizliHavale.callback_registered}
                      onChange={(e) => setHizliHavale({
                        ...hizliHavale, callback_registered: e.target.checked,
                      })}
                    />
                    Bu adresi Hızlı Havale paneline tanımladım
                  </label>
                  <p className="mt-2 text-xs leading-5 text-slate-400">
                    Bildirim adresi <strong>istekte gönderilmez</strong>;
                    sağlayıcının kendi panelinde yatırım ve çekim için ayrı
                    ayrı tanımlanır. Bu sağlayıcıda tanımlanmaması parayı
                    kaybettirmez — sonucu zaten durum sorgusu belirler — ama
                    yatırımın onaylanması dakikalar sürer. Callback adresini,
                    API adresini, bayi adını ya da anahtarı değiştirirseniz
                    onay kendiliğinden düşer.
                  </p>
                </div>
              </div>
              )}
            </div>
            {hizliHavaleStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: hizliHavaleStatus.ok
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${hizliHavaleStatus.ok
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={hizliHavaleStatus.ok ? 'text-emerald-300' : 'text-red-300'}>
                  {hizliHavaleStatus.ok
                    ? hizliHavaleStatus.message
                    : 'Bağlantı doğrulanamadı.'}
                </span>
                {!hizliHavaleStatus.ok && hizliHavaleStatus.error && (
                  <span className="ml-2 text-slate-400">
                    {hizliHavaleStatus.error}
                  </span>
                )}
              </div>
            )}
            <button
              type="button"
              onClick={testHizliHavale}
              disabled={busy || !hizliHavale.enabled}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Hızlı Havale Bağlantısını Test Et
            </button>
            </>
            )}

            {financeSection === 'cryptobox' && (
              <CryptoBoxSettings
                paymentBase={paymentBase}
                onEnabledChange={setCryptoboxEnabled}
              />
            )}

            {financeSection === 'cashmoney' && (
              <CashMoneySettings
                paymentBase={paymentBase}
                onEnabledChange={setCashmoneyEnabled}
              />
            )}

            {financeSection === 'paylux' && (
              <PayluxSettings
                paymentBase={paymentBase}
                siteBase={siteBase}
                onEnabledChange={setPayluxEnabled}
              />
            )}

            {financeSection === 'primevpay' && (
              <PrimevpaySettings
                paymentBase={paymentBase}
                siteBase={siteBase}
                onEnabledChange={setPrimevpayEnabled}
              />
            )}

            {financeSection === 'expay' && (
              <ExpaySettings
                paymentBase={paymentBase}
                siteBase={siteBase}
                onEnabledChange={setExpayEnabled}
              />
            )}

            {financeSection === 'zelvoq' && (
            <>
            <div className="col-span-full mt-1">
              <h3 className="text-sm font-bold text-white">Zelvoq</h3>
              <p className="mt-1 text-xs text-slate-400">
                Dört yatırım yöntemi vardır (havale, kredi kartı, Papara,
                kripto); <strong>çekim yalnızca havaledir</strong> — sağlayıcı
                çekimde sayısal bir banka kimliği zorunlu tutar ve diğer
                yöntemlerin banka listesinde karşılığı yoktur. Çekim önce
                <strong> yönetici onayından </strong> geçer. Bildirim adresi
                <strong> her istekte gönderilir</strong>, yani kaydedildiği an
                geçerlidir; ayrıca "sağlayıcı paneline tanımladım" onayı
                gerekmez.
              </p>
            </div>
            <div className="col-span-full provider-enable">
              <label className="flex items-center gap-2 text-sm font-semibold text-white">
                <input
                  type="checkbox"
                  checked={!!zelvoq.enabled}
                  onChange={(e) => setZelvoq({
                    ...zelvoq, enabled: e.target.checked,
                  })}
                />
                Zelvoq entegrasyonu aktif
              </label>
              <p className="mt-2 text-xs leading-5 text-slate-400">
                Etkinleştirmek için API adresi, <strong>merchant kimliği
                (sid)</strong>, secret anahtarı, bildirim adresi ve dönüş
                adresi zorunludur. Havale çekimi ya da hesap akışı açıksa
                ayrıca <strong>banka listesi</strong> çekilmiş olmalıdır.
              </p>
            </div>
            <div className="col-span-full grid grid-cols-1 gap-3 lg:grid-cols-2">
              <section className="provider-money is-deposit">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={zelvoq.deposit_enabled !== false}
                    onChange={(e) => setZelvoq({
                      ...zelvoq, deposit_enabled: e.target.checked,
                    })}
                  />
                  Zelvoq yatırım açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum yatırım (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={zelvoq.deposit_min_amount ?? 100}
                      onChange={(e) => setZelvoq({
                        ...zelvoq,
                        deposit_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum yatırım (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={zelvoq.deposit_max_amount ?? 100000}
                      onChange={(e) => setZelvoq({
                        ...zelvoq,
                        deposit_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <div className="mt-3 text-xs font-semibold text-slate-300">
                  Açık yatırım yöntemleri
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {ZELVOQ_METHODS.filter((m) => m.deposit).map((m) => (
                    <label
                      key={`zelvoq-dep-${m.slug}`}
                      className="flex items-center gap-2 text-xs text-slate-300"
                    >
                      <input
                        type="checkbox"
                        checked={zelvoqHasMethod(
                          zelvoq.deposit_methods, m.slug)}
                        onChange={(e) => setZelvoq({
                          ...zelvoq,
                          deposit_methods: zelvoqToggleMethod(
                            zelvoq.deposit_methods, m.slug, e.target.checked),
                        })}
                      />
                      {m.label}
                    </label>
                  ))}
                </div>
                <label className="mt-3 flex items-start gap-2 text-xs text-slate-300">
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={!!zelvoq.show_bank}
                    onChange={(e) => setZelvoq({
                      ...zelvoq, show_bank: e.target.checked,
                    })}
                  />
                  <span>
                    Havale yatırımında <strong>hesap bilgisini bizim
                    ekranımızda</strong> göster
                    <span className="block text-slate-500">
                      Kapalıyken sağlayıcının ödeme sayfası açılır
                      (varsayılan). Açıkken sağlayıcı işleme bir banka hesabı
                      atar ve IBAN ekranımızda görünür; o akış sayısal bir
                      banka kimliği ister, bu yüzden aşağıdan bir banka
                      seçilmelidir. Yalnızca <strong>havale</strong>
                      {' '}yönteminde geçerlidir.
                    </span>
                  </span>
                </label>
                {zelvoq.show_bank && (
                <label className="settings-inline-field mt-3">
                  Hesap akışının bankası
                  <select
                    value={String(zelvoq.bank_id || '')}
                    onChange={(e) => setZelvoq({
                      ...zelvoq, bank_id: Number(e.target.value) || 0,
                    })}
                    className="admin-input mt-1"
                  >
                    <option value="">Seçiniz</option>
                    {(zelvoq.banks || []).map((bank) => (
                      <option key={`zelvoq-bank-${bank.code}`} value={bank.code}>
                        {bank.name}
                      </option>
                    ))}
                  </select>
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Sağlayıcı hesabı zaten <strong>tutara göre</strong>
                    {' '}eşleştirir; uygun hesap yoksa reddi kendisi bildirir
                    ve oyuncuya "farklı bir tutar deneyin" mesajı çıkar.
                  </div>
                </label>
                )}
              </section>
              <section className="provider-money is-withdraw">
                <label className="flex items-center gap-2 text-sm font-bold text-white">
                  <input
                    type="checkbox"
                    checked={zelvoq.withdraw_enabled !== false}
                    onChange={(e) => setZelvoq({
                      ...zelvoq, withdraw_enabled: e.target.checked,
                    })}
                  />
                  Zelvoq çekim açık
                </label>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className="settings-inline-field">
                    Minimum çekim (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={zelvoq.withdraw_min_amount ?? 100}
                      onChange={(e) => setZelvoq({
                        ...zelvoq,
                        withdraw_min_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                  <label className="settings-inline-field">
                    Maksimum çekim (₺)
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={zelvoq.withdraw_max_amount ?? 100000}
                      onChange={(e) => setZelvoq({
                        ...zelvoq,
                        withdraw_max_amount: Number(e.target.value),
                      })}
                      className="admin-input mt-1"
                    />
                  </label>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  {ZELVOQ_METHODS.filter((m) => m.withdraw).map((m) => (
                    <label
                      key={`zelvoq-wd-${m.slug}`}
                      className="flex items-center gap-2 text-xs text-slate-300"
                    >
                      <input
                        type="checkbox"
                        checked={zelvoqHasMethod(
                          zelvoq.withdraw_methods, m.slug)}
                        onChange={(e) => setZelvoq({
                          ...zelvoq,
                          withdraw_methods: zelvoqToggleMethod(
                            zelvoq.withdraw_methods, m.slug, e.target.checked),
                        })}
                      />
                      {m.label}
                    </label>
                  ))}
                </div>
                <p className="mt-3 text-xs leading-5 text-slate-500">
                  Çekim talebi <strong>önce yönetici onayından</strong> geçer;
                  onay talebi sağlayıcıya açar, ödemeyi tamamlamaz. Hedef banka
                  serbest bir ad değil sağlayıcının <strong>sayısal
                  kimliğidir</strong>, bu yüzden oyuncu çekim ekranında
                  listeden seçer.
                </p>
              </section>
            </div>
            <div className="col-span-full">
              <div
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg p-3"
                style={{
                  background: 'var(--v2-hover, rgba(148,163,184,0.06))',
                  border: '1px solid var(--v2-line, rgba(148,163,184,0.2))',
                }}
              >
                <div className="text-xs leading-5 text-slate-400">
                  Kayıtlı banka listesi:{' '}
                  <strong className="text-slate-200">
                    {(zelvoq.banks || []).length} kayıt
                  </strong>
                  <span className="block text-slate-500">
                    Liste <strong>bir kez</strong> çekilir ve kayda yazılır;
                    çekim anında ağa çıkmak, talebi sağlayıcının gecikmesine
                    bağlamak olurdu.
                  </span>
                </div>
                <button
                  type="button"
                  onClick={syncZelvoqBanks}
                  disabled={busy || !zelvoq.sid}
                  className="admin-btn-secondary py-2 flex items-center gap-2"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                  Banka listesini sağlayıcıdan al
                </button>
              </div>
            </div>
            <div className="col-span-full">
              <button
                type="button"
                onClick={() => setZelvoqApiOpen((open) => !open)}
                className="admin-btn-secondary w-full justify-between py-3"
              >
                <span>API bilgileri ve bildirim adresi</span>
                <span className="text-xs text-slate-400">
                  {zelvoqApiOpen ? 'gizle' : 'göster'}
                </span>
              </button>
              {zelvoqApiOpen && (
              <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
                <F l="API adresi">
                  <input
                    value={zelvoq.api_url || ''}
                    onChange={(e) => setZelvoq({
                      ...zelvoq, api_url: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="https://ryl.zelvoq.com"
                  />
                </F>
                <F l="Merchant kimliği (sid)">
                  <input
                    value={zelvoq.sid || ''}
                    onChange={(e) => setZelvoq({
                      ...zelvoq, sid: e.target.value,
                    })}
                    className="admin-input"
                    placeholder="DCCBC2356BB3652E8FB5"
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Sağlayıcının size verdiği site kimliği. Her istekte gövdede
                    gönderilir; yanlışsa istek <code>Address not found</code>
                    {' '}ile reddedilir.
                  </div>
                </F>
                <F l="Secret anahtarı (yatırım)">
                  <input
                    type="password"
                    value={zelvoq.secret_key || ''}
                    onChange={(e) => setZelvoq({
                      ...zelvoq, secret_key: e.target.value,
                    })}
                    className="admin-input"
                    placeholder={zelvoq.secret_key_configured
                      ? MASKED_SECRET : ''}
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    İmza <code>md5(data_id + kullanıcı adı + secret)</code> ile
                    üretilir. Panele geri dönmez; boş bırakılırsa kayıtlı değer
                    korunur.
                  </div>
                </F>
                <F l="Secret anahtarı (çekim, isteğe bağlı)">
                  <input
                    type="password"
                    value={zelvoq.secret_with || ''}
                    onChange={(e) => setZelvoq({
                      ...zelvoq, secret_with: e.target.value,
                    })}
                    className="admin-input"
                    placeholder={zelvoq.secret_with_configured
                      ? MASKED_SECRET : ''}
                  />
                  <div className="mt-1.5 text-[11px] leading-4 text-slate-400">
                    Sağlayıcı çekim için <strong>ayrı bir sır</strong>
                    {' '}verebilir. <strong>Boş bırakılırsa yatırım sırrı
                    kullanılır</strong> — sağlayıcı size ikinci bir anahtar
                    vermediyse burayı doldurmayın.
                  </div>
                </F>
                <CallbackField
                  label="Callback URL"
                  path={ZELVOQ_CALLBACK_PATH}
                  note="Yatırım ve çekim bildirimleri aynı adrese düşer. Bu adres her istekte sağlayıcıya gönderilir; ayrıca sağlayıcı paneline tanımlamanız gerekmez."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
                <CallbackField
                  label="Dönüş adresi (redirect_url)"
                  path={ZELVOQ_REDIRECT_PATH}
                  note="Oyuncunun ödeme sonrası döndüğü sayfa. Sitenin güncel kökünden türetilir ve her kayıtta tazelenir."
                  copied={copiedCallback}
                  onCopy={copyCallback}
                  base={paymentBase}
                />
              </div>
              )}
            </div>
            {zelvoqStatus && (
              <div
                className="col-span-full rounded-lg p-3 text-xs"
                style={{
                  background: zelvoqStatus.ok
                    ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
                  border: `1px solid ${zelvoqStatus.ok
                    ? 'rgba(34,197,94,0.25)' : 'rgba(239,68,68,0.25)'}`,
                }}
              >
                <span className={zelvoqStatus.ok ? 'text-emerald-300' : 'text-red-300'}>
                  {zelvoqStatus.ok
                    ? zelvoqStatus.message
                    : 'Bağlantı doğrulanamadı.'}
                </span>
                {!zelvoqStatus.ok && zelvoqStatus.error && (
                  <span className="ml-2 text-slate-400">
                    {zelvoqStatus.error}
                  </span>
                )}
              </div>
            )}
            <p className="col-span-full text-xs text-slate-500">
              Testten önce bu bölümde bekleyen Zelvoq değişiklikleri otomatik
              olarak kaydedilir.
            </p>
            <button
              type="button"
              onClick={testZelvoq}
              disabled={busy || !zelvoq.sid}
              className="admin-btn-secondary col-span-full py-3 flex items-center justify-center gap-2"
            >
              {busy ? <Loader2 size={14} className="animate-spin" /> : <TestTube2 size={14} />}
              Zelvoq Bağlantısını Test Et
            </button>
            </>
            )}
            </>
            )}

          </div>
        )}

        {tab === 'affiliate' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!s.affiliate_enabled} onChange={(e) => setS({ ...s, affiliate_enabled: e.target.checked })} />
              Affiliate programı açık
            </label>
            <F l="Min. Çekim Tutarı (₺)">
              <input type="number" step="1" value={s.affiliate_min_withdraw ?? 0} onChange={(e) => setS({ ...s, affiliate_min_withdraw: Number(e.target.value) })} className="admin-input" />
            </F>
            <F l="Referans Takip Süresi (gün)">
              <input type="number" step="1" value={s.affiliate_cookie_days ?? 30} onChange={(e) => setS({ ...s, affiliate_cookie_days: Number(e.target.value) })} className="admin-input" />
            </F>
            <F l="Program Koşulları (üyeye gösterilir)" full>
              <textarea value={s.affiliate_terms || ''} onChange={(e) => setS({ ...s, affiliate_terms: e.target.value })} className="admin-input" style={{ minHeight: 80, padding: 12 }} />
            </F>
            <div className="col-span-2 flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
              <Info size={16} className="text-cyan-400 mt-0.5 shrink-0" />
              <div className="text-xs text-slate-300">
                Davet linki formatı: <code className="text-cyan-400">https://siteniz.com/?ref=KOD</code>.
                Komisyon modeli ve oranı burada ortak olarak tanımlanmaz; her ortak için
                <b className="text-cyan-400"> Affiliate Yönetimi</b> sayfasından kişiye özel girilir.
              </div>
            </div>
          </div>
        )}

        {tab === 'cashback' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <input type="checkbox" checked={!!s.cashback_enabled} onChange={(e) => setS({ ...s, cashback_enabled: e.target.checked })} />
              Kayıp bonusu açık
            </label>
            <F l="Hesaplama Modeli">
              <select value={s.cashback_period || 'daily'} onChange={(e) => setS({ ...s, cashback_period: e.target.value })} className="admin-input">
                <option value="instant">Anlık (bugün 00:00 → şimdi)</option>
                <option value="rolling">Kayan Pencere (son N saat)</option>
                <option value="daily">Günlük (dün)</option>
                <option value="weekly">Haftalık (geçen hafta)</option>
              </select>
            </F>
            <F l="Çevrim Katsayısı (1 = çevrimsiz)">
              <input type="number" step="0.5" value={s.cashback_wager_multiplier ?? 1} onChange={(e) => setS({ ...s, cashback_wager_multiplier: Number(e.target.value) })} className="admin-input" />
            </F>
            <F l="Min. Ödeme Tutarı (₺)">
              <input type="number" step="1" value={s.cashback_min_payout ?? 0} onChange={(e) => setS({ ...s, cashback_min_payout: Number(e.target.value) })} className="admin-input" />
            </F>
            <F l="Max. Bonus (₺, 0 = limitsiz)">
              <input type="number" step="1" value={s.cashback_max_bonus ?? 0} onChange={(e) => setS({ ...s, cashback_max_bonus: Number(e.target.value) })} className="admin-input" />
            </F>

            <div className="col-span-2 border-t border-white/5 pt-4 mt-2">
              <div className="flex items-center gap-2 mb-3">
                <div className="text-sm font-semibold">Bonus Kademeleri</div>
                <button
                  onClick={() => setS({ ...s, cashback_tiers: [...(s.cashback_tiers || []), { min_loss: 0, percent: 0 }] })}
                  className="admin-btn-secondary ml-auto text-xs flex items-center gap-1.5"
                >
                  <Plus size={12} /> Kademe Ekle
                </button>
              </div>
              <div className="space-y-2">
                {(s.cashback_tiers || []).map((t, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="flex-1">
                      <label className="text-[11px] text-slate-500 block mb-1">Min. net kayıp (₺)</label>
                      <input type="number" step="1" value={t.min_loss ?? 0} className="admin-input"
                        onChange={(e) => {
                          const tiers = [...s.cashback_tiers];
                          tiers[i] = { ...tiers[i], min_loss: Number(e.target.value) };
                          setS({ ...s, cashback_tiers: tiers });
                        }} />
                    </div>
                    <div className="flex-1">
                      <label className="text-[11px] text-slate-500 block mb-1">İade oranı (%)</label>
                      <input type="number" step="0.5" value={t.percent ?? 0} className="admin-input"
                        onChange={(e) => {
                          const tiers = [...s.cashback_tiers];
                          tiers[i] = { ...tiers[i], percent: Number(e.target.value) };
                          setS({ ...s, cashback_tiers: tiers });
                        }} />
                    </div>
                    <button onClick={() => setS({ ...s, cashback_tiers: s.cashback_tiers.filter((_, x) => x !== i) })}
                            className="icon-btn ban mt-5" title="Kademeyi sil">
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
                {(s.cashback_tiers || []).length === 0 && (
                  <div className="text-xs text-slate-500 py-3">Kademe yok — bonus hesaplanmaz.</div>
                )}
              </div>
            </div>

            <div className="col-span-2 flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
              <Info size={16} className="text-cyan-400 mt-0.5 shrink-0" />
              <div className="text-xs text-slate-300">
                Net kayıp = toplam bahis − toplam kazanç (spor kuponları + casino turları).
                Kaybı karşılayan <b>en yüksek eşikli</b> kademe uygulanır. Örnek: 1000₺ eşik / %10 →
                1000₺ kayıpta 100₺ iade. {s.cashback_period === 'instant'
                  ? 'Anlık modelde gün içindeki yeni net kayıp hak doğduğu anda talep edilir; aynı kayıp tekrar bonuslanmaz.'
                  : s.cashback_period === 'rolling'
                    ? 'Kayan pencerede ölçü takvim günü değil, şu andan geriye belirlenen saattir; aynı kayıp tekrar bonuslanmaz.'
                    : 'Talep, tamamlanmış dönem için ve dönem başına bir kez yapılır.'}
                {' '}Dikey ayrımı açıksa aşağıdaki oranlar bu tablonun yerine geçer.
              </div>
            </div>

            <CashbackRulesSettings
              canWrite={can('finance.write')}
              period={s.cashback_period || 'daily'}
            />
          </div>
        )}

        {tab === 'gameapi' && (
          <div className="space-y-4">
            <label
              className="flex items-center gap-2 text-sm rounded-xl p-3"
              style={{ background: 'var(--v2-surface-2, rgba(15,23,42,0.45))', border: '1px solid var(--v2-line, rgba(255,255,255,0.08))' }}
            >
              <input type="checkbox" checked={!!s.game_api_enabled} onChange={(e) => setS({ ...s, game_api_enabled: e.target.checked })} />
              <span>
                Oyun sağlayıcı entegrasyonu açık
                <span className="text-xs text-slate-500 block mt-0.5">
                  Kapatıldığında hiçbir servis üzerinden oyun açılmaz.
                </span>
              </span>
            </label>

            {/* Servis seçici — her kart kendi ayar kutusunu açar */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-2">
                <div className="text-sm font-semibold">Oyun Servisleri</div>
                <div className="text-[11px] text-slate-500">
                  Servisi açıp kapatmak ve bilgilerini girmek için karta tıklayın
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {gameServices.map((svc) => {
                  const active = svc.id === activeService.id;
                  const enabled = serviceEnabled(svc);
                  const ready = serviceReady(svc);
                  const ServiceIcon = svc.icon;
                  return (
                    <div
                      key={svc.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => setGameService(svc.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setGameService(svc.id);
                        }
                      }}
                      className="rounded-xl p-3 cursor-pointer transition"
                      style={{
                        background: active ? `${svc.accent}14` : 'rgba(15,23,42,0.45)',
                        border: `1px solid ${active ? svc.accent : 'rgba(255,255,255,0.08)'}`,
                        boxShadow: active ? `0 0 0 3px ${svc.accent}1f` : 'none',
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                          style={{ background: `${svc.accent}22`, color: svc.accent }}
                        >
                          <ServiceIcon size={17} />
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold truncate">{svc.name}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5 leading-tight">{svc.tagline}</div>
                        </div>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={enabled}
                          aria-label={`${svc.name} servisini ${enabled ? 'kapat' : 'aç'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setGameService(svc.id);
                            setS({ ...s, [svc.enabledKey]: !enabled });
                          }}
                          className="shrink-0 mt-0.5"
                        >
                          <span
                            className="block w-10 h-[22px] rounded-full relative transition"
                            style={{ background: enabled ? svc.accent : 'rgba(148,163,184,0.25)' }}
                          >
                            <span
                              className="absolute top-[3px] w-4 h-4 rounded-full bg-white transition-all"
                              style={{ left: enabled ? 22 : 3 }}
                            />
                          </span>
                        </button>
                      </div>
                      <div className="flex flex-wrap items-center gap-1.5 mt-3">
                        <span className={`badge-pill ${enabled ? 'badge-green' : 'badge-red'}`}>
                          {enabled ? 'AÇIK' : 'KAPALI'}
                        </span>
                        <span className={`badge-pill ${ready ? 'badge-blue' : 'badge-yellow'}`}>
                          {ready ? 'Bilgiler tam' : 'Bilgi eksik'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Seçili servisin bilgi kutusu */}
            <div
              className="rounded-xl p-4"
              style={{
                background: 'var(--v2-surface-2, rgba(15,23,42,0.45))',
                border: `1px solid ${activeService.accent}40`,
              }}
            >
              <div className="flex items-center gap-2 flex-wrap">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                  style={{ background: `${activeService.accent}22`, color: activeService.accent }}
                >
                  <activeService.icon size={16} />
                </div>
                <div className="text-sm font-semibold">{activeService.name} API bilgileri</div>
                <span className={`badge-pill ${serviceEnabled(activeService) ? 'badge-green' : 'badge-red'}`}>
                  {serviceEnabled(activeService) ? 'SERVİS AÇIK' : 'SERVİS KAPALI'}
                </span>
              </div>
              <div className="text-xs text-slate-500 mt-2 mb-4">{activeService.note}</div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {activeService.fields.map((field) => {
                  const saved = !!s[`${field.key}_configured`];
                  const missing = (
                    field.required
                    && !field.autoGenerate
                    && !serviceFieldFilled(field)
                  );
                  return (
                    <div key={field.key} className={field.full ? 'md:col-span-2' : ''}>
                      <label className="text-xs text-slate-400 block mb-1">
                        {field.label}
                        {saved && <span className="text-emerald-400 ml-1">· kayıtlı</span>}
                        {!saved && field.autoGenerate && (
                          <span className="text-sky-400 ml-1">· otomatik oluşturulur</span>
                        )}
                        {missing && <span className="text-amber-400 ml-1">· zorunlu</span>}
                      </label>
                      <input
                        type={field.secret ? 'password' : 'text'}
                        value={s[field.key] || ''}
                        onChange={(e) => setS({ ...s, [field.key]: e.target.value })}
                        onFocus={() => {
                          if (field.secret && s[field.key] === MASKED_SECRET) {
                            setS({ ...s, [field.key]: '' });
                          }
                        }}
                        className="admin-input"
                        autoComplete={field.secret ? 'new-password' : 'off'}
                        placeholder={
                          field.secret
                            ? (
                              saved
                                ? 'Değiştirmek için yeni değer girin'
                                : (
                                  field.autoGenerate
                                    ? 'Boş bırakın; Kaydet sırasında oluşturulur'
                                    : field.label
                                )
                            )
                            : (field.placeholder || '')
                        }
                      />
                      {!saved && field.autoGenerate && (
                        <div className="text-[11px] text-slate-500 mt-1">
                          Bu alanı boş bırakabilirsiniz; güvenli token Kaydet sırasında otomatik oluşturulur.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {activeService.scopes?.length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/5">
                  <div className="text-xs text-slate-400 mb-2">Bu servisin siteye çıkacağı bölümler</div>
                  <div className="flex flex-wrap gap-4">
                    {activeService.scopes.map((scope) => (
                      <label key={scope.key} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={s[scope.key] !== false}
                          onChange={(e) => setS({ ...s, [scope.key]: e.target.checked })}
                        />
                        {scope.label}
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {activeService.callbackPath && (
                <div
                  className="mt-4 rounded-lg p-3 text-xs text-slate-300 flex items-center gap-2 flex-wrap"
                  style={{
                    background: `${activeService.accent}14`,
                    border: `1px solid ${activeService.accent}40`,
                  }}
                >
                  <span>{activeService.name} paneline girilecek callback adresi:</span>
                  <code style={{ color: activeService.accent }}>{callbackUrl(activeService.callbackPath)}</code>
                  <button
                    type="button"
                    onClick={() => copyCallback(activeService.callbackPath)}
                    className="admin-btn-secondary text-[11px] flex items-center gap-1.5 ml-auto"
                  >
                    {copiedCallback === activeService.callbackPath
                      ? <><Check size={12} /> Kopyalandı</>
                      : <><Copy size={12} /> Kopyala</>}
                  </button>
                </div>
              )}
            </div>

            {/* Gateway ayarlarının tek yeri Oyun Kontrol → Sistem sayfasıdır.
                Aynı alanlar burada da dururken iki ekran birbirinden habersiz
                kaydediyor ve son kaydeden diğerini eziyordu. */}
            <div
              className="rounded-xl p-4"
              style={{ background: 'var(--v2-surface-2, rgba(15,23,42,0.45))', border: '1px solid rgba(56,189,248,0.25)' }}
            >
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-sm font-semibold">Aynı Domain Oyun Gateway</div>
                  <div className="text-xs text-slate-500 mt-1">
                    Oyunları <code className="text-cyan-400">/gamelaunch/...</code> yolundan,
                    sandbox izolasyonu ve özel DNS üzerinden açar. Ayarları ve bağlantı
                    testi <b>Oyun Kontrol → Sistem</b> sayfasındadır.
                  </div>
                </div>
                <span className={`badge-pill ${s.game_gateway_enabled ? 'badge-green' : 'badge-red'}`}>
                  {s.game_gateway_enabled ? 'GATEWAY AÇIK' : 'GATEWAY KAPALI'}
                </span>
              </div>
            </div>


            {/* Tüm servisler için ortak oyun ayarları */}
            <div
              className="rounded-xl p-4"
              style={{ background: 'var(--v2-surface-2, rgba(15,23,42,0.45))', border: '1px solid var(--v2-line, rgba(255,255,255,0.08))' }}
            >
              <div className="text-sm font-semibold mb-3">Ortak Oyun Ayarları</div>
              <div className="grid grid-cols-2 gap-3">
                <F l="Para Birimi">
                  <input value={s.game_api_currency || 'TRY'} onChange={(e) => setS({ ...s, game_api_currency: e.target.value.toUpperCase() })} className="admin-input" placeholder="TRY" />
                </F>
                <F l="Oyun Dili">
                  <select value={s.game_api_language || 'tr'} onChange={(e) => setS({ ...s, game_api_language: e.target.value })} className="admin-input">
                    {GAME_LANGUAGES.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
                  </select>
                  <p className="text-xs opacity-60 mt-1">{GAME_LANGUAGE_HINT}</p>
                </F>
                <F l="Gerçek Para RTP (Gold Palace: 0 veya izin verilen RTP)" full>
                  <input type="number" min="0" max="99" value={s.game_api_rtp ?? 90} onChange={(e) => setS({ ...s, game_api_rtp: Number(e.target.value) })} className="admin-input" />
                </F>
              </div>
            </div>

            {/* Demo ayarları */}
            <div
              className="rounded-xl p-4"
              style={{ background: 'var(--v2-surface-2, rgba(15,23,42,0.45))', border: '1px solid var(--v2-line, rgba(255,255,255,0.08))' }}
            >
              <div className="text-sm font-semibold mb-3">Demo (&quot;Eğlenmek İçin Oyna&quot;)</div>
              <div className="grid grid-cols-2 gap-3">
                <label className="col-span-2 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!s.demo_enabled} onChange={(e) => setS({ ...s, demo_enabled: e.target.checked })} />
                  Demo mod açık
                </label>
                <label className="col-span-2 flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={!!s.demo_requires_login} onChange={(e) => setS({ ...s, demo_requires_login: e.target.checked })} />
                  Demo için giriş zorunlu olsun (kapalıysa üye olmayanlar da oynayabilir)
                </label>
                <F l="Demo Başlangıç Bakiyesi (₺)">
                  <input type="number" min="0" step="100" value={s.demo_start_balance ?? 10000} onChange={(e) => setS({ ...s, demo_start_balance: Number(e.target.value) })} className="admin-input" />
                </F>
                <F l="Demo RTP (kazanma oranı, 1-99)">
                  <input type="number" min="1" max="99" value={s.demo_rtp ?? 97} onChange={(e) => setS({ ...s, demo_rtp: Number(e.target.value) })} className="admin-input" />
                </F>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
              <Info size={16} className="text-cyan-400 mt-0.5 shrink-0" />
              <div className="text-xs text-slate-300 space-y-1">
                <div>
                  Servis aç/kapat ve bağlantı bilgileri bu ekrandan yönetilir; değişiklikler
                  <b> Kaydet</b> ile uygulanır. Bağlantı testi ve oyun senkronizasyonu <b>Oyunlar</b> sayfasındadır.
                </div>
                <div>
                  Demo oturumları Drakon <code className="text-cyan-400">mode=fun</code> ile açılır; demo bakiyesi
                  sitenin gerçek bakiyesini, kayıp bonusunu ve affiliate komisyonunu etkilemez.
                </div>
              </div>
            </div>
            <button onClick={save} disabled={busy || !canSave} className="admin-btn-primary w-full py-3 flex items-center justify-center gap-2">
              {busy && <Loader2 size={14} className="animate-spin" />}<Save size={14} /> Kaydet
            </button>
          </div>
        )}

        {tab === 'livechat' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!s.livechat_enabled}
                onChange={(e) => setS({ ...s, livechat_enabled: e.target.checked })}
              />
              Canlı destek widget&apos;ı açık
            </label>
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!s.livechat_require_login}
                onChange={(e) => setS({ ...s, livechat_require_login: e.target.checked })}
              />
              Yalnızca giriş yapan üyelere göster
            </label>
            <F l="Sağlayıcı" full>
              <select
                value={liveChatProvider.provider}
                onChange={(e) => setLiveChatProvider({
                  ...liveChatProvider, provider: e.target.value,
                })}
                className="admin-input"
                aria-label="Canlı destek sağlayıcısı"
              >
                <option value="livechat">LiveChat (lisans numarası)</option>
                <option value="text">Text.com (organizasyon kimliği)</option>
              </select>
            </F>
            {liveChatProvider.provider === 'text' ? (
              <F l="Text.com Organization ID" full>
                <input
                  value={liveChatProvider.organization_id}
                  onChange={(e) => setLiveChatProvider({
                    ...liveChatProvider,
                    organization_id: e.target.value.trim().slice(0, 64),
                  })}
                  className="admin-input"
                  autoComplete="off"
                  spellCheck={false}
                  placeholder="Örn. 1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d"
                />
              </F>
            ) : (
              <F l="LiveChat Lisans Numarası" full>
                <input
                  value={s.livechat_license_id || ''}
                  onChange={(e) => setS({
                    ...s,
                    livechat_license_id: e.target.value.replace(/\D/g, '').slice(0, 20),
                  })}
                  className="admin-input"
                  inputMode="numeric"
                  autoComplete="off"
                  placeholder="Örn. 12345678"
                />
              </F>
            )}
            <F l="Grup ID (opsiyonel)" full>
              <input
                value={s.livechat_group_id || ''}
                onChange={(e) => setS({
                  ...s,
                  livechat_group_id: e.target.value.trim().slice(0, 64),
                })}
                className="admin-input"
                autoComplete="off"
                placeholder="Boşsa varsayılan grup kullanılır"
              />
            </F>
            <div
              className="col-span-2 flex items-start gap-2 p-3 rounded-lg"
              style={{
                background: 'rgba(56,189,248,0.08)',
                border: '1px solid rgba(56,189,248,0.25)',
              }}
            >
              <Info size={16} className="text-cyan-400 mt-0.5 shrink-0" />
              <div className="text-xs text-slate-300 space-y-2">
                {liveChatProvider.provider === 'text' ? (
                  <p>
                    Organization ID&apos;yi Text uygulamasında
                    {' '}
                    <b>Settings → Websites</b>
                    {' '}
                    bölümündeki kurulum kodundan alın
                    (<code>window.__lc.organizationId</code> satırı). Kimlik gizli anahtar
                    değildir. Kurulum kodunu Özel Kod alanına ayrıca yapıştırmayın:
                    widget iki kez yüklenir.
                  </p>
                ) : (
                  <p>
                    Lisans numarasını LiveChat panelinde
                    {' '}
                    <b>Settings → Channels → Website</b>
                    {' '}
                    bölümündeki kurulum kodundan alın. Bu numara gizli anahtar değildir.
                  </p>
                )}
                <p>
                  Üye giriş yaptıysa kullanıcı adı ve e-posta temsilci ekranına otomatik aktarılır.
                  Bakiye veya finansal bilgi gönderilmez. İki sağlayıcı aynı widget&apos;ı
                  kullanır; aynı anda yalnızca biri açık olabilir.
                </p>
                <a
                  href={liveChatProvider.provider === 'text'
                    ? 'https://www.text.com/app/settings/websites'
                    : 'https://my.livechatinc.com/settings/channels/website'}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-cyan-400 hover:text-cyan-300"
                >
                  {liveChatProvider.provider === 'text'
                    ? 'Text.com kurulum sayfasını aç'
                    : 'LiveChat kurulum sayfasını aç'}
                  <ExternalLink size={12} />
                </a>
              </div>
            </div>

            <div className="col-span-2 mt-2 border-t border-white/5 pt-4">
              <div className="text-sm font-semibold">Canlı destek görünümü</div>
            </div>

            <div
              className="col-span-2 rounded-lg p-4"
              style={{ background: 'var(--v2-surface-2, rgba(15,23,42,0.45))', border: '1px solid var(--v2-line, rgba(255,255,255,0.08))' }}
            >
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                {resolveApiAssetUrl(s.livechat_icon_url) ? (
                  <img
                    src={resolveApiAssetUrl(s.livechat_icon_url)}
                    alt="Canlı destek butonu önizlemesi"
                    className="h-20 w-20 shrink-0 rounded-full object-cover"
                    style={{ boxShadow: `0 0 22px ${primaryColor}55` }}
                  />
                ) : (
                  <LiveChatDefaultIcon
                    className="h-20 w-20 shrink-0 rounded-full"
                    accent={primaryColor}
                    style={{ boxShadow: `0 0 22px ${primaryColor}55` }}
                  />
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold">Canlı destek buton ikonu</div>
                  <div className="mt-1 text-xs text-slate-400">
                    PNG, JPG, GIF, WEBP veya SVG · En fazla {UPLOAD_MAX_LABEL}. Kare ve şeffaf PNG ya da SVG önerilir; hareketli ikon için GIF yükleyin.
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    <label className={`admin-btn-secondary flex cursor-pointer items-center gap-2 ${liveChatIconBusy ? 'pointer-events-none opacity-50' : ''}`}>
                      {liveChatIconBusy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                      {uploadLabel('livechat_icon_url', s.livechat_icon_url ? 'Değiştir' : 'İkon Yükle')}
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml,.svg"
                        className="hidden"
                        onChange={uploadLiveChatIcon}
                        disabled={liveChatIconBusy}
                      />
                    </label>
                    {s.livechat_icon_url && (
                      <button
                        type="button"
                        onClick={removeLiveChatIcon}
                        disabled={liveChatIconBusy}
                        className="admin-btn-secondary flex items-center gap-2 text-red-400"
                      >
                        <Trash2 size={14} /> Varsayılana Dön
                      </button>
                    )}
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <label className="mb-1 block text-xs text-slate-400">veya İkon Görsel URL’si</label>
                <input
                  value={s.livechat_icon_url || ''}
                  onChange={(event) => setS({ ...s, livechat_icon_url: event.target.value })}
                  className="admin-input"
                  placeholder="https://..."
                  disabled={liveChatIconBusy}
                />
              </div>
            </div>

          </div>
        )}

        {tab === 'telegram' && (
          <div className="grid grid-cols-2 gap-3">
            <label className="col-span-2 flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={!!s.telegram_bot_enabled}
                onChange={(e) => setS({ ...s, telegram_bot_enabled: e.target.checked })}
              />
              Telegram bildirimleri açık
            </label>
            <F l={`Bildirim Bot Tokenı${s.telegram_bot_token_configured ? ' (kayıtlı)' : ''}`} full>
              <input
                type="password"
                value={s.telegram_bot_token || ''}
                onChange={(e) => setS({ ...s, telegram_bot_token: e.target.value.trim() })}
                className="admin-input"
                autoComplete="off"
                placeholder={s.telegram_bot_token_configured ? 'Değiştirmek için yeni token girin' : '123456789:AA...'}
              />
            </F>
            <F l="Varsayılan Sohbet Kimliği (Chat ID)">
              <input
                value={s.telegram_chat_id || ''}
                onChange={(e) => setS({ ...s, telegram_chat_id: e.target.value.trim().slice(0, 64) })}
                className="admin-input"
                autoComplete="off"
                placeholder="-1001234567890 veya @kanaladi"
              />
            </F>
            <F l="Konu ID (opsiyonel)">
              <input
                value={s.telegram_thread_id || ''}
                onChange={(e) => setS({ ...s, telegram_thread_id: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                className="admin-input"
                inputMode="numeric"
                autoComplete="off"
                placeholder="Konu (topic) açık gruplarda"
              />
            </F>

            <div className="col-span-2 border-t border-white/5 pt-4 mt-2">
              <div className="text-sm font-semibold mb-1">
                Topluluk Üyeliği (Çapa, Promosyon Kodları ve Deneme Bonusu)
              </div>
              <p className="text-xs text-slate-400 mb-2">
                Çapa etkinliği, promosyon kodları ve deneme bonusunun isteğe
                bağlı grup üyeliği şartı için kullanılır. Topluluk grubu,
                yukarıdaki bildirim grubundan farklıdır: oyuncuların katıldığı
                gruptur. Aşağıdaki ayrı üyelik botu orada
                {' '}<strong>yönetici</strong> olmalıdır; bildirim botunun tokenı
                değiştirilmez.
              </p>
              <ol className="text-xs text-slate-400 list-decimal pl-4 space-y-1 mb-3">
                <li>Üyelik botunu topluluk grubuna ekleyin ve yönetici yapın.</li>
                <li>
                  Üyelik bot tokenını girin, <strong>Secret Üret</strong> →
                  {' '}<strong>Kaydet</strong> →
                  {' '}<strong>Webhook’u Telegram’a kaydet</strong>.
                </li>
                <li>
                  <strong>Grupları Bul</strong> ile grubu listeden seçin —
                  kimliği elle bulmanız gerekmez.
                </li>
              </ol>
            </div>

            <label className="col-span-2 flex items-start gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-3 text-sm">
              <input
                type="checkbox"
                checked={!!s.promo_require_telegram}
                onChange={(e) => setS({
                  ...s, promo_require_telegram: e.target.checked,
                })}
                className="mt-0.5"
              />
              <span>
                <strong className="block text-white">
                  Promosyon kodlarında Telegram üyeliği zorunlu
                </strong>
                <span className="mt-1 block text-xs text-slate-400">
                  Açıkken oyuncu, topluluk üyeliği doğrulanmadan promosyon kodu
                  alanını göremez ve doğrudan API isteğiyle kod kullanamaz.
                  Deneme bonusunun şartı kendi ekleme/düzenleme formundadır.
                </span>
              </span>
            </label>

            <F
              l={`Topluluk Üyelik Bot Tokenı${s.anchor_telegram_bot_token_configured
                ? ' (kayıtlı)' : ''}`}
              full
            >
              <input
                type="password"
                value={s.anchor_telegram_bot_token || ''}
                onChange={(e) => setS({
                  ...s, anchor_telegram_bot_token: e.target.value.trim(),
                })}
                className="admin-input"
                autoComplete="off"
                placeholder={s.anchor_telegram_bot_token_configured
                  ? 'Değiştirmek için yeni üyelik bot tokenı girin'
                  : '123456789:AA...'}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Yalnızca hesap bağlama, grup üyeliği, çapa, promosyon kodu ve
                deneme bonusu doğrulaması için kullanılır. Üstteki bildirim
                botunu etkilemez.
              </p>
            </F>

            <F l="Topluluk Davet Bağlantısı" full>
              <input
                value={s.telegram_url || ''}
                onChange={(e) => setS({ ...s, telegram_url: e.target.value.trim() })}
                className="admin-input"
                autoComplete="off"
                placeholder="https://t.me/toplulugunuz"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Üye olmayan oyuncu bu adrese yönlendirilir. Sosyal &amp;
                İletişim bölümündeki Telegram URL alanıyla aynıdır.
              </p>
            </F>

            <F l="Topluluk Grubu Kimliği">
              <div className="flex gap-2">
                <input
                  value={s.telegram_community_chat_id || ''}
                  onChange={(e) => setS({
                    ...s,
                    telegram_community_chat_id: e.target.value.trim().slice(0, 64),
                  })}
                  className="admin-input"
                  autoComplete="off"
                  placeholder="-1001234567890"
                />
                <button
                  type="button"
                  onClick={loadTelegramChats}
                  disabled={telegramChatsBusy || canSave
                    || !s.anchor_telegram_bot_token_configured}
                  className="admin-btn text-xs whitespace-nowrap"
                  title={canSave ? 'Önce Telegram ayarlarını kaydedin' : ''}
                >
                  Grupları Bul
                </button>
              </div>
            </F>

            <F l={`Webhook Secret${s.telegram_webhook_secret_configured ? ' (kayıtlı)' : ''}`}>
              <div className="flex gap-2">
                <input
                  type={showWebhookSecret ? 'text' : 'password'}
                  value={s.telegram_webhook_secret || ''}
                  onChange={(e) => setS({
                    ...s, telegram_webhook_secret: e.target.value.trim(),
                  })}
                  className="admin-input font-mono"
                  autoComplete="off"
                  placeholder={s.telegram_webhook_secret_configured
                    ? 'Kayıtlı — değiştirmek için yeni değer girin'
                    : 'Üret düğmesine basın'}
                />
                <button
                  type="button"
                  onClick={() => setShowWebhookSecret((value) => !value)}
                  className="admin-btn text-xs whitespace-nowrap"
                  disabled={!s.telegram_webhook_secret}
                >
                  {showWebhookSecret ? 'Gizle' : 'Göster'}
                </button>
                <button
                  type="button"
                  onClick={copyWebhookSecret}
                  className="admin-btn text-xs whitespace-nowrap"
                  disabled={!s.telegram_webhook_secret}
                >
                  Kopyala
                </button>
                <button
                  type="button"
                  onClick={generateWebhookSecret}
                  className="admin-btn text-xs whitespace-nowrap"
                >
                  Üret
                </button>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">
                Bu değeri bir yere not etmenize gerek yok: webhook’u panelden
                bildirdiğinizde Telegram’a otomatik iletilir. Kaydedildikten
                sonra güvenlik gereği geri gösterilmez — unutulursa yenisini
                üretip webhook’u tekrar bildirmeniz yeterlidir.
              </p>
            </F>

            {telegramChats && (
              <div className="col-span-2 rounded-lg border border-white/10 p-3">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className="text-xs font-semibold">
                    {telegramChats.bot_username
                      ? `Üyelik botu @${telegramChats.bot_username}`
                      : 'Üyelik botunun bulunduğu gruplar'}
                  </span>
                  <button
                    type="button"
                    onClick={scanTelegramChats}
                    disabled={telegramChatsBusy || telegramChats.webhook_active}
                    className="admin-btn text-[11px] ml-auto"
                    title={telegramChats.webhook_active
                      ? 'Webhook kuruluyken tarama yapılamaz; bot gruba eklendiğinde grup kendiliğinden listeye düşer'
                      : ''}
                  >
                    {telegramChatsBusy ? 'Taranıyor…' : 'Tara'}
                  </button>
                </div>

                {(telegramChats.items || []).length === 0 ? (
                  <p className="text-[11px] text-slate-500">
                    Henüz grup görülmedi. Botu gruba ekleyin —
                    {telegramChats.webhook_active
                      ? ' grup kendiliğinden burada listelenir.'
                      : ' sonra Tara düğmesine basın.'}
                  </p>
                ) : (
                  <div className="space-y-1">
                    {telegramChats.items.map((chat) => (
                      <div key={chat.chat_id}
                        className="flex items-center gap-2 text-xs py-1 border-b border-white/5 last:border-0">
                        <div className="min-w-0">
                          <div className="font-semibold truncate">
                            {chat.title || chat.chat_id}
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {chat.chat_id} • {chat.type}
                            {chat.bot_status && ` • bot: ${chat.bot_status}`}
                            {chat.bot_status && chat.bot_status !== 'administrator' && (
                              <span className="text-amber-300">
                                {' '}(yönetici yapın)
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="ml-auto flex items-center gap-1 shrink-0">
                          <button
                            type="button"
                            onClick={() => checkTelegramChat(chat.chat_id)}
                            className="admin-btn text-[11px]"
                          >
                            Durum
                          </button>
                          <button
                            type="button"
                            onClick={() => setS({
                              ...s, telegram_community_chat_id: chat.chat_id })}
                            className="admin-btn text-[11px]"
                            disabled={
                              s.telegram_community_chat_id === chat.chat_id}
                          >
                            {s.telegram_community_chat_id === chat.chat_id
                              ? 'Seçili' : 'Seç'}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="col-span-2 flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={registerTelegramWebhook}
                disabled={telegramWebhookBusy
                  || canSave
                  || !s.anchor_telegram_bot_token_configured
                  || !s.telegram_webhook_secret_configured}
                className="admin-btn text-xs"
                title={canSave ? 'Önce Telegram ayarlarını kaydedin' : ''}
              >
                {telegramWebhookBusy ? 'Kaydediliyor…' : 'Webhook’u Telegram’a kaydet'}
              </button>
              <button
                type="button"
                onClick={loadWebhookInfo}
                disabled={canSave || !s.anchor_telegram_bot_token_configured}
                className="admin-btn text-xs"
                title={canSave ? 'Önce Telegram ayarlarını kaydedin' : ''}
              >
                Webhook Durumu
              </button>
              <span className="text-xs text-slate-500">
                Secret’ı kaydettikten sonra bir kez çalıştırın; bot
                <code className="mx-1">/start</code> mesajlarını buraya iletir.
              </span>
            </div>

            {telegramWebhookInfo && (
              <div className="col-span-2 rounded-lg border border-white/10 p-3 text-xs space-y-1">
                <div className="font-semibold mb-1">Telegram’daki webhook</div>
                {telegramWebhookInfo.bot_username && (
                  <div className="text-slate-400">
                    Üyelik botu:{' '}
                    <span className="text-slate-200">
                      @{telegramWebhookInfo.bot_username}
                    </span>
                  </div>
                )}
                {telegramWebhookInfo.info?.url ? (
                  <>
                    <div className="text-slate-400 break-all">
                      Adres:{' '}
                      <span className="text-slate-200">
                        {telegramWebhookInfo.info.url}
                      </span>
                    </div>
                    <div className="text-slate-400">
                      Bekleyen güncelleme:{' '}
                      {telegramWebhookInfo.info.pending_update_count ?? 0}
                    </div>
                    {telegramWebhookInfo.info.last_error_message && (
                      <div className="text-rose-300">
                        Son hata: {telegramWebhookInfo.info.last_error_message}
                      </div>
                    )}
                    {!telegramWebhookInfo.configured && (
                      <div className="text-amber-300">
                        Panelde secret kayıtlı değil; hesap bağlama ucu kapalı.
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-amber-300">
                    Telegram’da kayıtlı webhook yok — “Webhook’u Telegram’a
                    kaydet” düğmesine basın.
                  </div>
                )}
              </div>
            )}

            <div className="col-span-2 border-t border-white/5 pt-4 mt-2">
              <div className="text-sm font-semibold mb-1">Bildirim Gönderilecek Olaylar</div>
              <p className="text-xs text-slate-400 mb-3">
                Her tür ayrı bir gruba gönderilebilir: yatırımlar bir gruba,
                yeni üyeler başka bir gruba. Grup kimliği boş kalan tür
                yukarıdaki varsayılan sohbete gider; yalnızca konu ID’si
                girilirse varsayılan grubun o konusuna gider. Bildirim botu
                her grupta üye olmalıdır.
              </p>
              <datalist id="telegram-known-chats">
                {(telegramChats?.items || []).map((chat) => (
                  <option key={chat.chat_id} value={chat.chat_id}>
                    {chat.title || chat.chat_id}
                  </option>
                ))}
              </datalist>
              <div className="space-y-2">
                {telegramEvents.map((event) => {
                  const route = telegramRoutes[event.key] || {};
                  const saved = telegramRoutesInitial?.[event.key] || {};
                  const routeDirty = telegramRoutesDirtyKeys().includes(event.key);
                  return (
                    <div
                      key={event.key}
                      className="grid grid-cols-1 gap-2 rounded-lg border border-white/5 p-2 md:grid-cols-[minmax(0,1fr)_minmax(0,14rem)_6.5rem_auto] md:items-center"
                    >
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={!!s[event.key]}
                          onChange={(e) => setS({ ...s, [event.key]: e.target.checked })}
                        />
                        {event.label}
                      </label>
                      <input
                        value={route.chat_id || ''}
                        onChange={(e) => setTelegramRoute(
                          event.key, 'chat_id', e.target.value.trim().slice(0, 64))}
                        className="admin-input"
                        list="telegram-known-chats"
                        autoComplete="off"
                        aria-label={`${event.label} — grup kimliği`}
                        placeholder="Varsayılan grup"
                      />
                      <input
                        value={route.thread_id || ''}
                        onChange={(e) => setTelegramRoute(
                          event.key, 'thread_id', e.target.value.replace(/\D/g, '').slice(0, 10))}
                        className="admin-input"
                        inputMode="numeric"
                        autoComplete="off"
                        aria-label={`${event.label} — konu ID`}
                        placeholder="Konu ID"
                      />
                      <button
                        type="button"
                        onClick={() => sendTelegramTest(event.key)}
                        disabled={busy || routeDirty || !s.telegram_bot_token_configured
                          || !(saved.chat_id || saved.thread_id)}
                        className="admin-btn-secondary text-xs"
                        title={routeDirty
                          ? 'Önce kaydedin'
                          : 'Bu türün grubuna test mesajı gönderir'}
                      >
                        Dene
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="col-span-2 flex items-start gap-2 p-3 rounded-lg" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
              <Info size={16} className="text-cyan-400 mt-0.5 shrink-0" />
              <div className="text-xs text-slate-300 space-y-1">
                <div>
                  Bot token&apos;ı <b>@BotFather</b>&apos;dan alın, botu bildirim grubuna ekleyip yönetici yapın.
                  Grup kimliğini öğrenmek için gruba bir mesaj atıp
                  {' '}
                  <code className="text-cyan-400">https://api.telegram.org/bot&lt;TOKEN&gt;/getUpdates</code>
                  {' '}
                  adresindeki <code className="text-cyan-400">chat.id</code> değerini kullanın (süper gruplar <code className="text-cyan-400">-100</code> ile başlar).
                </div>
                <div>
                  Bildirimler arka planda gönderilir; Telegram&apos;a ulaşılamazsa üye işlemleri etkilenmez,
                  hata aşağıdaki geçmişe yazılır.
                </div>
              </div>
            </div>

            <button type="button" onClick={() => sendTelegramTest()} disabled={busy || !s.telegram_bot_token_configured} className="admin-btn-secondary col-span-2 py-3 flex items-center justify-center gap-2">
              <TestTube2 size={14} /> Test Bildirimi Gönder
            </button>

            <div className="col-span-2 border-t border-white/5 pt-4 mt-2">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-sm font-semibold">Gönderim Geçmişi</div>
                <button onClick={() => loadTelegramLogs()} className="admin-btn-secondary ml-auto text-xs">Yenile</button>
              </div>
              {telegramLogs.length === 0 ? <div className="text-xs text-slate-500">Kayıt yok</div> : (
                <div className="space-y-2">
                  {telegramLogs.map((l) => (
                    <div key={l.id} className="flex items-center gap-3 text-sm p-3 rounded-lg" style={{ background: 'var(--v2-surface-2, rgba(15,23,42,0.5))' }}>
                      <Bell size={14} className="text-cyan-400 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{l.title || l.event}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {new Date(l.created_at).toLocaleString('tr-TR')}
                          {l.error ? ` • ${l.error}` : ''}
                        </div>
                      </div>
                      <span className={`badge-pill ${l.status === 'sent' ? 'badge-green' : l.status === 'skipped' ? 'badge-yellow' : 'badge-red'}`}>
                        {l.status === 'sent' ? 'gönderildi' : l.status === 'skipped' ? 'atlandı' : 'hata'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {telegramLogTotal > TELEGRAM_LOG_PAGE_SIZE && (
                <div className="flex items-center gap-3 mt-3">
                  <button
                    type="button"
                    disabled={telegramLogPage === 0}
                    onClick={() => setTelegramLogPage((current) => Math.max(0, current - 1))}
                    className="admin-btn-secondary text-xs inline-flex items-center gap-2 disabled:opacity-40"
                  >
                    <ChevronLeft size={14} /> Önceki
                  </button>
                  <span className="text-xs text-slate-400" aria-live="polite">
                    Sayfa {telegramLogPage + 1} / {telegramLogPages}
                  </span>
                  <button
                    type="button"
                    disabled={telegramLogPage + 1 >= telegramLogPages}
                    onClick={() => setTelegramLogPage((current) => current + 1)}
                    className="admin-btn-secondary text-xs ml-auto inline-flex items-center gap-2 disabled:opacity-40"
                  >
                    Sonraki <ChevronRight size={14} />
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'social' && (
          <div className="grid grid-cols-2 gap-3">
            <F l="Instagram URL" full><input value={s.instagram_url} onChange={(e) => setS({ ...s, instagram_url: e.target.value })} className="admin-input" placeholder="https://instagram.com/..." /></F>
            <F l="Telegram URL" full><input value={s.telegram_url} onChange={(e) => setS({ ...s, telegram_url: e.target.value })} className="admin-input" placeholder="https://t.me/..." /></F>
            <F l="WhatsApp URL" full><input value={s.whatsapp_url} onChange={(e) => setS({ ...s, whatsapp_url: e.target.value })} className="admin-input" placeholder="https://wa.me/..." /></F>
          </div>
        )}

        {tab === 'otp' && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div
              className="col-span-full flex items-start gap-3 rounded-xl p-4"
              style={{
                background: 'rgba(56,189,248,0.06)',
                border: '1px solid rgba(56,189,248,0.18)',
              }}
            >
              <ShieldCheck size={18} className="mt-0.5 shrink-0 text-cyan-300" />
              <div>
                <div className="text-sm font-semibold text-slate-100">
                  SMS doğrulama bağlantısı
                </div>
                <p className="mt-1 text-xs leading-5 text-slate-400">
                  İlk kayıt doğrulamasını ve telefon numarası bulunan
                  kullanıcıların iki adımlı doğrulama kodlarını yönetir.
                  Anahtar kaydedildikten sonra tekrar gösterilmez.
                </p>
              </div>
            </div>

            <F
              l={`HeraclesOtp Api${s.heracles_otp_api_key_configured ? ' (kayıtlı)' : ''}`}
              full
            >
              <input
                type="password"
                autoComplete="new-password"
                value={s.heracles_otp_api_key || ''}
                onChange={(e) => {
                  setS({ ...s, heracles_otp_api_key: e.target.value });
                  setHeraclesOtpStatus(null);
                }}
                className="admin-input"
                placeholder={
                  s.heracles_otp_api_key_configured
                    ? 'Değiştirmek için yeni entegrasyon anahtarı girin'
                    : 'Entegrasyon anahtarı'
                }
              />
            </F>

            <label
              className="col-span-full flex items-start gap-3 rounded-xl p-4 text-sm"
              style={{
                background: 'var(--v2-hover, rgba(255,255,255,0.025))',
                border: '1px solid var(--v2-line, rgba(255,255,255,0.08))',
              }}
            >
              <input
                type="checkbox"
                className="mt-0.5"
                checked={Boolean(s.registration_otp_enabled)}
                onChange={(e) => setS({
                  ...s,
                  registration_otp_enabled: e.target.checked,
                })}
              />
              <span>
                <span className="block font-semibold text-slate-100">
                  İlk kayıtta telefon doğrulaması
                </span>
                <span className="mt-1 block text-xs leading-5 text-slate-400">
                  Açıkken hesap, telefona gönderilen 6 haneli kod girilmeden
                  oturum açamaz ve doğrulama tamamlanınca üyenin 2FA’sı
                  otomatik açılır — bunun maliyeti her girişte bir SMS’tir.
                  Kapattığınızda bu otomatik haklar susar, geri açtığınızda
                  kendiliğinden döner. Üyenin hesap güvenliği ekranından
                  kendi açtığı 2FA bu ayardan etkilenmez.
                </span>
              </span>
            </label>

            <div className="col-span-full">
              <button
                type="button"
                onClick={testHeraclesOtp}
                disabled={heraclesOtpBusy || !canTestHeracles}
                className="admin-btn-secondary flex items-center justify-center gap-2 py-3 disabled:cursor-not-allowed disabled:opacity-50"
                title={
                  canTestHeracles
                    ? 'Kayıtlı anahtarla bakiye sorgusu yap'
                    : 'Önce entegrasyon anahtarını kaydedin'
                }
              >
                {heraclesOtpBusy
                  ? <Loader2 size={14} className="animate-spin" />
                  : <TestTube2 size={14} />}
                Bağlantıyı Kontrol Et
              </button>
            </div>

            {heraclesOtpStatus && (
              <div
                className="col-span-full rounded-xl p-4 text-sm"
                role="status"
                style={{
                  background: heraclesOtpStatus.ok
                    ? 'rgba(34,197,94,0.08)'
                    : 'rgba(248,113,113,0.08)',
                  border: heraclesOtpStatus.ok
                    ? '1px solid rgba(34,197,94,0.24)'
                    : '1px solid rgba(248,113,113,0.24)',
                }}
              >
                <div className={`flex items-center gap-2 font-semibold ${
                  heraclesOtpStatus.ok ? 'text-emerald-300' : 'text-red-300'
                }`}
                >
                  {heraclesOtpStatus.ok && <Check size={16} />}
                  {heraclesOtpStatus.ok
                    ? 'HeraclesOtp bağlantısı çalışıyor'
                    : 'HeraclesOtp bağlantısı kurulamadı'}
                </div>
                <div className="mt-1 text-xs text-slate-400">
                  {heraclesOtpStatus.ok
                    ? 'Entegrasyon anahtarı geçerli, sağlayıcıya ulaşıldı.'
                    : heraclesOtpStatus.message}
                </div>
              </div>
            )}

            <SempicoSettings
              canWrite={can('content.write')}
              siteName={s.site_name}
              heraclesConfigured={Boolean(s.heracles_otp_api_key_configured)}
            />
          </div>
        )}

        {tab === 'smtp' && (
          <div>
            <div className="flex items-start gap-2 p-3 rounded-lg mb-4" style={{ background: 'rgba(56,189,248,0.08)', border: '1px solid rgba(56,189,248,0.25)' }}>
              <Info size={16} className="text-cyan-400 mt-0.5" />
              <div className="text-xs text-slate-300">Gmail için: <code className="text-cyan-400">smtp.gmail.com</code>, port <code className="text-cyan-400">587</code>, TLS açık. Uygulama şifresi gerekir. Sendgrid/AWS SES vb. desteklenir.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <F l="SMTP Host"><input value={s.smtp_host} onChange={(e) => setS({ ...s, smtp_host: e.target.value })} className="admin-input" placeholder="smtp.gmail.com" /></F>
              <F l="Port"><input type="number" value={s.smtp_port} onChange={(e) => setS({ ...s, smtp_port: Number(e.target.value) })} className="admin-input" /></F>
              <F l="Kullanıcı (Gmail vb.)" full><input value={s.smtp_user} onChange={(e) => setS({ ...s, smtp_user: e.target.value })} className="admin-input" /></F>
              <F l={`Şifre / App Password${s.smtp_password_configured ? ' (kayıtlı)' : ''}`} full><input type="password" value={s.smtp_password || ''} onChange={(e) => setS({ ...s, smtp_password: e.target.value })} className="admin-input" placeholder={s.smtp_password_configured ? 'Değiştirmek için yeni şifre girin' : ''} /></F>
              <F l="Gönderen Adı"><input value={s.smtp_from_name} onChange={(e) => setS({ ...s, smtp_from_name: e.target.value })} className="admin-input" /></F>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={s.smtp_use_tls} onChange={(e) => setS({ ...s, smtp_use_tls: e.target.checked })} /> TLS Kullan (587)</label>
              <div className="col-span-2 border-t border-white/5 pt-4 mt-2">
                <div className="text-sm font-semibold mb-2">Test Gönderimi</div>
                <div className="flex gap-2">
                  <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test@example.com" className="admin-input flex-1" />
                  <button onClick={testSmtp} disabled={busy} className="admin-btn-primary flex items-center gap-1"><TestTube2 size={14} /> Test Gönder</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {tab === 'mail' && (
          <div>
            <div className="grid grid-cols-2 gap-3">
              <F l="Konu" full><input value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} className="admin-input" /></F>
              <F l="Hedef Grup"><select value={mail.target} onChange={(e) => setMail({ ...mail, target: e.target.value })} className="admin-input">
                <option value="all">Tüm Kullanıcılar</option>
                <option value="active">Aktif Kullanıcılar</option>
                <option value="verified">Doğrulanmış</option>
                <option value="banned">Yasaklılar</option>
                <option value="custom">Özel Liste</option>
              </select></F>
              <F l="" ><div className="h-11"/></F>
              {mail.target === 'custom' && (
                <F l="E-Posta Listesi (virgül veya satırla ayırın)" full><textarea value={mail.custom_emails} onChange={(e) => setMail({ ...mail, custom_emails: e.target.value })} className="admin-input" style={{ minHeight: 80, padding: 12 }} placeholder="a@x.com, b@y.com" /></F>
              )}
              <F l="HTML İçerik" full><textarea value={mail.body_html} onChange={(e) => setMail({ ...mail, body_html: e.target.value })} className="admin-input" style={{ minHeight: 200, padding: 12, fontFamily: 'monospace', fontSize: 12 }} placeholder="<h1>Merhaba!</h1><p>...</p>" /></F>
              <button onClick={sendBulk} disabled={busy} className="admin-btn-primary col-span-2 py-3 flex items-center justify-center gap-2">{busy && <Loader2 size={14} className="animate-spin" />}<Send size={14} /> Gönder</button>
            </div>

            <div className="mt-6 border-t border-white/5 pt-4">
              <div className="text-sm font-semibold mb-2">Gönderim Geçmişi</div>
              {logs.length === 0 ? <div className="text-xs text-slate-500">Kayıt yok</div> : (
                <div className="space-y-2">
                  {logs.map((l) => (
                    <div key={l.id} className="flex items-center gap-3 text-sm p-3 rounded-lg" style={{ background: 'var(--v2-surface-2, rgba(15,23,42,0.5))' }}>
                      <Mail size={14} className="text-cyan-400" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold truncate">{l.subject}</div>
                        <div className="text-xs text-slate-500">{l.target} • {new Date(l.created_at).toLocaleString('tr-TR')}</div>
                      </div>
                      <div className="text-xs"><span className="text-green-400">{l.sent}</span> / <span className="text-red-400">{l.failed}</span> / <span className="text-slate-400">{l.total}</span></div>
                      <span className={`badge-pill ${l.status === 'completed' ? 'badge-green' : 'badge-yellow'}`}>{l.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {tab === 'backup' && (
          <BackupPanelV2
            s={s}
            setS={setS}
            status={backupStatus}
            busy={backupBusy}
            running={backupRunning}
            downloadBusy={downloadBusy}
            snapshotFormat={snapshotFormat}
            setSnapshotFormat={setSnapshotFormat}
            jobWatch={jobWatch}
            restoreFile={restoreFile}
            setRestoreFile={setRestoreFile}
            onRunBackup={runBackup}
            onSnapshot={runSnapshot}
            onDownload={downloadBackup}
            onReload={() => loadBackupStatus()}
            onRestoreSourceStarted={() => {
              setJobWatch({
                kind: 'restore',
                afterId: backupStatus?.logs?.[0]?.id || '',
                startedAt: Date.now(),
              });
              loadBackupStatus();
            }}
            onRestoreFile={() => setConfirmRestore(true)}
          />
        )}

          </div>

          {/* Kaydet her sekmede ayrı bir yerde ve ayrı bir yazıyla duruyordu;
              şerit bölmenin dibinde yapışıktır ve neyin değiştiğini söyler. */}
          <div className={`settings-savebar${canSave ? ' is-dirty' : ''}`}>
            <div className="settings-savebar-note">
              {canSave ? (
                <>
                  <span className="settings-nav-dot" />
                  <span title={tabChangedKeys(tab).join(', ')}>
                    <b className="text-slate-100">{activeTab?.name}</b>
                    {` · ${tabChangedKeys(tab).length} alan değişti`}
                  </span>
                </>
              ) : (
                <span className="settings-savebar-idle">Kaydedilmemiş değişiklik yok</span>
              )}
            </div>
            {/* Temiz durumda düğme çizilmez: iki soluk, tıklanamayan düğme
                şeridi bozuk gösteriyordu. */}
            {canSave && (
              <div className="settings-savebar-actions">
                <button
                  type="button"
                  onClick={revertTab}
                  disabled={busy}
                  className="admin-btn-secondary flex items-center gap-2"
                >
                  <RotateCcw size={15} /> Vazgeç
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={busy}
                  className="admin-btn-primary flex items-center gap-2"
                >
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />}
                  Kaydet
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={confirmSecret}
        title="SMTP parolasını değiştir"
        description="Kayıtlı SMTP parolası yeni değerle değiştirilecek. Mevcut parola geri gösterilemez ve işlem audit loguna yazılır."
        confirmLabel="Parolayı değiştir"
        tone="danger"
        busy={busy}
        onCancel={() => {
          if (busy) return;
          setPendingPayload(null);
          setConfirmSecret(false);
        }}
        onConfirm={saveSecret}
      />
      <ConfirmDialog
        open={Boolean(themeUnlockPrompt)}
        title={`${themeUnlockPrompt?.name || ''} şeması parola ister`}
        description="Bu tasarım şemasına geçmek için geçiş parolasını girin. Parola doğrulanmadan şema seçilemez; şemadan çıkmak parola istemez."
        confirmLabel="Kilidi aç"
        tone="danger"
        busy={themeUnlockBusy}
        extra={(
          <input
            type="password"
            className="admin-input"
            autoFocus
            autoComplete="off"
            placeholder="Şema geçiş parolası"
            value={themeUnlockPassword}
            onChange={(e) => setThemeUnlockPassword(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !themeUnlockBusy) confirmThemeUnlock();
            }}
            aria-label="Şema geçiş parolası"
          />
        )}
        onCancel={() => {
          if (themeUnlockBusy) return;
          setThemeUnlockPrompt(null);
          setThemeUnlockPassword('');
        }}
        onConfirm={confirmThemeUnlock}
      />
      <ConfirmDialog
        open={confirmRestore}
        title="Yedeği geri yükle"
        description={`"${restoreFile?.name || ''}" dosyasındaki veriler mevcut verilerin yerine yazılacak. İşlemden önce otomatik bir güvenlik kopyası alınır; geri dönmek isterseniz onu yükleyebilirsiniz. İşlem audit loguna yazılır.`}
        confirmLabel="Geri yükle"
        tone="danger"
        confirmPhrase={RESTORE_CONFIRM_PHRASE}
        busy={backupBusy}
        onCancel={() => {
          if (backupBusy) return;
          setConfirmRestore(false);
        }}
        onConfirm={startRestore}
      />
    </div>
  );
};

// Alt menü adresleri elle de yazılabilir; liste yalnızca sık kullanılanları
// önerir (datalist).
const BOTTOM_NAV_URL_PRESETS = [
  { url: '/', label: 'Anasayfa' },
  { url: '/spor', label: 'Spor' },
  { url: '/slotlar', label: 'Slotlar' },
  { url: '/canli-casino', label: 'Canlı Casino' },
  { url: '/crash', label: 'Hızlı Oyunlar' },
  { url: '/promosyonlar', label: 'Bonuslar' },
  { url: '/cark', label: 'Şans Çarkı' },
  { url: '/slotlar?flag=hot', label: 'En Popüler Oyunlar' },
  { url: '/slotlar?flag=new', label: 'Yeni Oyunlar' },
  { url: '/hesap/bahis-kuponlari', label: 'Kuponlarım' },
  { url: '/hesap/para-yatir', label: 'Para Yatırma' },
];

/* Mobil alt menünün yuvaları.

   Ortadaki yuvarlak düğme listede yoktur: girişsiz ziyaretçide "Giriş Yap",
   üyede "Para Yatır" olarak sabittir ve panelden kaldırılamaz. Kalan dört
   yuva buradan yönetilir. */
const BottomNavEditor = ({ items, onChange }) => {
  // Bölüm uzun; varsayılan olarak kapalı durur, yalnızca özet satırı görünür.
  const [open, setOpen] = useState(false);
  const rows = Array.isArray(items) && items.length ? items : DEFAULT_BOTTOM_NAV;
  const hasMenuSlot = rows.some((row) => row.type === 'menu');
  const preview = splitBottomNav(normalizeBottomNav(rows));

  const update = (index, patch) => onChange(
    rows.map((row, i) => (i === index ? { ...row, ...patch } : row)),
  );
  const remove = (index) => onChange(rows.filter((_, i) => i !== index));
  const move = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= rows.length) return;
    const next = [...rows];
    next[index] = rows[target];
    next[target] = rows[index];
    onChange(next);
  };

  const previewItem = (item, index) => {
    const Icon = bottomNavIcon(item.icon);
    return (
      <div
        key={`${item.type}-${item.url}-${index}`}
        className="flex min-w-0 flex-1 flex-col items-center gap-1 text-[10px] text-slate-400"
      >
        <Icon size={17} />
        <span className="max-w-full truncate">{item.label}</span>
      </div>
    );
  };

  return (
    <div className="col-span-full mt-2 border-t border-white/5 pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button
          type="button"
          onClick={() => setOpen((value) => !value)}
          aria-expanded={open}
          className="flex min-w-0 flex-1 items-start gap-2 text-left"
        >
          <ChevronDown
            size={16}
            className={`mt-0.5 shrink-0 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`}
          />
          <div className="min-w-0">
            <div className="text-sm font-semibold">Mobil alt menü</div>
            {open ? (
              <div className="mt-1 text-xs text-slate-500">
                Telefonda ekranın altında duran menü. Ortadaki yuvarlak düğme
                sabittir: ziyaretçide <b>Giriş Yap</b>, üyede <b>Para Yatır</b>.
                Kalan {MAX_BOTTOM_NAV_ITEMS} yuvanın etiketi, ikonu ve adresi
                buradan değişir; ilk yarısı düğmenin soluna, kalanı sağına düşer.
              </div>
            ) : (
              <div className="mt-1 text-xs text-slate-500">
                {rows.length} yuva tanımlı — düzenlemek için tıklayın.
                {!hasMenuSlot && (
                  <span className="text-amber-300/90"> Yan menüyü açan yuva yok.</span>
                )}
              </div>
            )}
          </div>
        </button>
        {open && (
          <button
            type="button"
            onClick={() => onChange([
              ...rows,
              { label: 'Yeni', icon: 'star', type: 'link', url: '/' },
            ])}
            disabled={rows.length >= MAX_BOTTOM_NAV_ITEMS}
            className="admin-btn-secondary flex shrink-0 items-center gap-1.5 text-xs disabled:opacity-40"
            title={rows.length >= MAX_BOTTOM_NAV_ITEMS
              ? `En fazla ${MAX_BOTTOM_NAV_ITEMS} yuva eklenebilir`
              : 'Yeni yuva ekle'}
          >
            <Plus size={13} /> Yuva Ekle
          </button>
        )}
      </div>

      {open && (
        <>
        {/* Barın telefondaki hâli: yuva sırası ve orta düğmenin yeri. */}
        <div className="mt-3 rounded-xl border border-white/[0.07] bg-slate-950/50 p-3">
          <div className="mx-auto flex max-w-sm items-end gap-1">
            {preview.left.map(previewItem)}
            <div className="flex w-16 shrink-0 flex-col items-center gap-1">
              <span
                className="flex h-10 w-10 items-center justify-center rounded-full"
                style={{ background: 'rgba(188,155,106,0.9)' }}
              >
                <Wallet size={18} className="text-slate-900" />
              </span>
              <span className="text-[10px] text-slate-400">Giriş / Yatır</span>
            </div>
            {preview.right.map(previewItem)}
          </div>
        </div>

        {!hasMenuSlot && (
          <div className="mt-3 rounded-lg border border-amber-400/25 bg-amber-400/10 p-3 text-xs text-amber-200">
            Yuvaların hiçbiri <b>Menü (yan menüyü açar)</b> tipinde değil.
            Telefonda yan menüyü açan başka düğme olmadığı için kategoriler
            listesi erişilemez kalır.
          </div>
        )}

        <datalist id="bottom-nav-urls">
          {BOTTOM_NAV_URL_PRESETS.map((preset) => (
            <option key={preset.url} value={preset.url}>{preset.label}</option>
          ))}
        </datalist>

        <div className="mt-3 flex flex-col gap-2">
          {rows.map((row, index) => (
            <div
              key={index}
              className="grid gap-2 rounded-xl border border-white/[0.07] bg-slate-950/35 p-3 sm:grid-cols-[1fr_150px_190px_1.4fr_auto] sm:items-end"
            >
              <F l={`${index + 1}. yuva etiketi`}>
                <input
                  value={row.label || ''}
                  maxLength={24}
                  onChange={(event) => update(index, { label: event.target.value })}
                  className="admin-input"
                  placeholder="Anasayfa"
                />
              </F>
              <F l="İkon">
                <select
                  value={row.icon || 'home'}
                  onChange={(event) => update(index, { icon: event.target.value })}
                  className="admin-input"
                >
                  {BOTTOM_NAV_ICON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </F>
              <F l="Davranış">
                <select
                  value={row.type === 'menu' ? 'menu' : 'link'}
                  onChange={(event) => update(index, { type: event.target.value })}
                  className="admin-input"
                >
                  <option value="link">Sayfaya gider</option>
                  <option value="menu">Yan menüyü açar</option>
                </select>
              </F>
              <F l="Adres">
                <input
                  value={row.url || ''}
                  list="bottom-nav-urls"
                  maxLength={255}
                  disabled={row.type === 'menu'}
                  onChange={(event) => update(index, { url: event.target.value })}
                  className="admin-input disabled:opacity-40"
                  placeholder={row.type === 'menu' ? 'Gerekmiyor' : '/slotlar'}
                />
              </F>
              <div className="flex items-center gap-1 sm:pb-1">
                <button
                  type="button"
                  onClick={() => move(index, -1)}
                  disabled={index === 0}
                  className="admin-btn-secondary px-2 py-2 disabled:opacity-30"
                  aria-label="Yukarı taşı"
                >
                  <ChevronUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => move(index, 1)}
                  disabled={index === rows.length - 1}
                  className="admin-btn-secondary px-2 py-2 disabled:opacity-30"
                  aria-label="Aşağı taşı"
                >
                  <ChevronDown size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  disabled={rows.length <= 1}
                  className="admin-btn-secondary px-2 py-2 text-red-400 disabled:opacity-30"
                  aria-label="Yuvayı sil"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
        </>
      )}
    </div>
  );
};

// Marka sekmesindeki kartların ortak başlığı: ikon kutusu + ad + tek satır
// ipucu. Logo ve favicon kartları aynı hizada dursun diye ayrıldı.
const BrandingCardHeader = ({ icon: Icon, title, hint }) => (
  <div className="flex items-start gap-3">
    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-800">
      <Icon size={18} className="text-slate-400" />
    </div>
    <div className="min-w-0">
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-0.5 text-xs text-slate-400">{hint}</div>
    </div>
  </div>
);

// Sayfadaki her alanın etiketi buradan çizilir; biçimi tek yerde durur ki
// on dört bölüm aynı ritmi tutsun.
const F = ({ l, children, full }) => (
  <div className={full ? 'col-span-full' : ''}>
    <label className="settings-field-label">{l}</label>
    {children}
  </div>
);

export default SettingsPage;
